import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import ReactFlow, {
  Background,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
  useStoreApi,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import AiChat from './AiChat';
import { v4 as uuidv4 } from "uuid";
import { BsGrid3X3GapFill, BsFront, BsBack } from "react-icons/bs";
import { HiMinus } from "react-icons/hi";
import { FaSquareFull, FaFont, FaCircle } from "react-icons/fa6";
import { FiPlusSquare } from "react-icons/fi";
import { RiFullscreenLine, RiShapesFill } from "react-icons/ri";
import { LuSticker, LuFrame } from "react-icons/lu";
import { BiUndo, BiRedo } from "react-icons/bi";
import { SketchPicker } from "react-color";
import { getSocket } from "../../../services/socketManager";
import { useUser } from "../../../contexts/UserContext";
import { useMsal } from "@azure/msal-react";
import { useYjsSync } from "../../../hooks/useYjsSync";
import StyleCard from "./nodes/StyleCard";
import Rectangle from "./nodes/Rectangle";
import Circle from "./nodes/Circle";
import TextBox from "./nodes/TextBox";
import Image from "./nodes/Image";
import Sticker from "./nodes/Sticker";
import Line from "./nodes/Line";
import Frame from "./nodes/Frame";
import { TfiLineDouble } from "react-icons/tfi";
import { RxDragHandleDots2 } from "react-icons/rx";

// Socket.IO 싱글톤 인스턴스 가져오기
const socket = getSocket();

const initialNodes = [];
const initialEdges = [];

function ReactFlowInner({ tabId = 'default', cadData, fileId = 'default', onTabsRestore = null, isPersonal = false, isPersonalLoaded = false, boardAutoUpdate = false, boardMapType = null, boardBrdCd = null, boardSesn = null }) {
  // 권한 체크 및 사용자 정보
  const { hasPermission, loading: userLoading, user } = useUser();
  const { accounts } = useMsal();
  const canEdit = !userLoading && hasPermission('matrixBoard-teamProject-node', 'edit');

  // MSAL에서 사용자 이름 가져오기 (화면 상단과 동일한 형식)
  const getUserNameFromMsal = () => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      const fullName = account.name || account.username || '';

      // "amber(이경선) F/KR/DD/PRCS/AIE/BI" 형식에서 이름만 추출
      // 패턴: "이름(한글명)" 형식을 찾음
      const match = fullName.match(/^([^\s]+(?:\([^)]+\))?)/);
      if (match) {
        return match[1]; // "amber(이경선)" 반환
      }

      return fullName || null;
    }
    return null;
  };

  // 여러 캔버스 관리
  const [canvases, setCanvases] = useState([{ id: 'canvas-1', name: '캔버스 1', nodes: initialNodes, edges: initialEdges }]);
  const [activeCanvasId, setActiveCanvasId] = useState('canvas-1');

  // 현재 활성 캔버스 찾기
  const activeCanvas = canvases.find(c => c.id === activeCanvasId) || canvases[0];

  const [nodes, setNodes, onNodesChange] = useNodesState(activeCanvas?.nodes || initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(activeCanvas?.edges || initialEdges);
  const { screenToFlowPosition, getViewport, flowToScreenPosition, fitView, setViewport, getNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const rfStore = useStoreApi();
  const setViewportRef = useRef(setViewport);
  useEffect(() => {
    setViewportRef.current = setViewport;
  }, [setViewport]);

  // 고정된 확대 비율
  const fixedZoom = 0.5;
  const cadAutoRefreshedRef = useRef(new Set()); // 캐드맵 자동갱신 완료된 canvasId 추적
  const broadcastTimeoutRef = useRef(null); // debounce용
  const thumbnailTimeoutRef = useRef(null); // 썸네일 생성 debounce용
  const isDraggingRef = useRef(false); // 드래그 중인지 추적
  const draggingNodeIdsRef = useRef(new Set()); // 드래그 중인 노드 ID들 추적
  const draggedNodeIdsAtStartRef = useRef(new Set()); // 드래그 시작 시점의 노드 ID들 추적 (여러 노드 동시 이동용)
  const dragStartPositionsRef = useRef(new Map()); // 드래그 시작 시점의 노드 위치 저장 (실제 이동 확인용)
  const isDragHandledByDragStopRef = useRef(false); // onNodeDragStop에서 처리했는지 여부
  const DRAG_SYNC_THROTTLE_MS = 50; // 리사이즈 동기화 간격 (드래그는 throttle 없음)
  const copiedNodesRef = useRef([]); // 복사된 노드 저장
  const copiedEdgesRef = useRef([]); // 복사된 Edge 저장
  const isExternalUpdateRef = useRef(false); // 외부 업데이트 중인지 추적 (무한 루프 방지)
  const isInitialLoadRef = useRef(true); // 초기 로드 중인지 추적 (초기 select 이벤트 무시용)
  const pendingDimChangesRef = useRef([]); // 초기 로드 중 dimension 변경 누적용
  const dimBatchTimerRef = useRef(null); // dimension 배치 적용 타이머
  const saveToHistoryRef = useRef(null); // saveToHistory 함수 참조
  const isPasteOperationRef = useRef(false); // 붙여넣기 작업 중인지 추적 (중복 broadcast 방지)
  const isResizingRef = useRef(false); // 리사이즈 중인지 추적
  const resizeTimeoutRef = useRef(null); // 리사이즈 debounce용
  const resizingNodeIdsRef = useRef(new Set()); // 리사이즈 중인 노드 ID들
  const lastResizeSyncTimeRef = useRef(0); // 리사이즈 중 동기화 throttle 타임스탬프
  const recentlyCreatedNodeIdsRef = useRef(new Set()); // 최근 생성된 노드 ID들 (리사이즈 무시용)
  const selectionOrderRef = useRef([]); // 노드 선택 순서 추적 (가장 최근 선택한 노드가 마지막)
  const selectionStartPosRef = useRef(null); // 선택 시작 Flow 좌표 (진단용)

  // Undo/Redo 히스토리 관리
  const historyRef = useRef([]); // 히스토리 스택 (초기에는 비어있음, 로드 후 초기화)
  const historyIndexRef = useRef(-1); // 현재 히스토리 인덱스 (초기 -1)
  const [historyIndex, setHistoryIndex] = useState(-1); // UI 업데이트용 state
  const [historyLength, setHistoryLength] = useState(0); // UI 업데이트용 state
  const isUndoRedoRef = useRef(false); // undo/redo 작업 중인지 추적
  const isHistoryInitializedRef = useRef(false); // 히스토리 초기화 여부
  const initialStateRef = useRef(null); // 초기 상태 저장 (Undo 시 돌아갈 기준점, 히스토리에 포함 안 됨)
  const MAX_HISTORY_SIZE = 50; // 최대 히스토리 개수


  // 템플릿 추가 다이얼로그 상태
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateCols, setTemplateCols] = useState('');
  const [templateRows, setTemplateRows] = useState('');

  // 카드/템플릿 추가 모드 상태
  const [addCardMode, setAddCardMode] = useState(false);
  const [addTemplateMode, setAddTemplateMode] = useState(false);
  const [templateGridSize, setTemplateGridSize] = useState({ cols: 0, rows: 0 });

  // 스타일카드 메뉴 상태
  const [showStyleCardMenu, setShowStyleCardMenu] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [selectedMapType, setSelectedMapType] = useState(
    boardMapType === 'S' ? '판매맵' : boardMapType === 'G' ? 'CAD맵' : '레인지맵'
  );
  const styleCardMenuRef = useRef(null);
  const templateMenuRef = useRef(null);

  // 박스 선택 모드 상태
  const [boxSelectionMode, setBoxSelectionMode] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null); // { x1, y1, x2, y2 } - 선택 박스 영역
  const [isBoxSelecting, setIsBoxSelecting] = useState(false); // 현재 박스 선택 중인지

  // 도구 모드 상태 (rectangle, heart, textbox, pen)
  const [toolMode, setToolMode] = useState(null);
  const [showToolMenu, setShowToolMenu] = useState(false);
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const toolMenuRef = useRef(null);
  const shapeMenuRef = useRef(null);
  const stickerMenuRef = useRef(null);
  const [pendingToolMode, setPendingToolMode] = useState(null); // 색상 선택 대기 중인 도구

  // 스티커 관련 상태
  const [showStickerModal, setShowStickerModal] = useState(false);
  const [showStickerUploadModal, setShowStickerUploadModal] = useState(false); // 스티커 업로드 모달
  const [stickers, setStickers] = useState([]); // 업로드된 스티커 목록
  const [uploadingSticker, setUploadingSticker] = useState(false);
  const [isDraggingOverSticker, setIsDraggingOverSticker] = useState(false); // 드래그 오버 상태
  const stickerFileInputRef = useRef(null);
  const stickerUploadModalRef = useRef(null);

  // 접속 인원 수 및 사용자 목록
  const [connectedUsers, setConnectedUsers] = useState(1);
  const [userList, setUserList] = useState([]);

  // toolMode 변경 추적
  // useEffect(() => {
  //   console.log('🖊️ toolMode 변경:', toolMode);
  // }, [toolMode]);


  // 사각형 드래그 상태
  const [rectangleStart, setRectangleStart] = useState(null);
  const [rectangleEnd, setRectangleEnd] = useState(null);

  // 프레임 드래그 상태
  const [frameStart, setFrameStart] = useState(null);
  const [frameEnd, setFrameEnd] = useState(null);

  const [circleStart, setCircleStart] = useState(null);
  const [circleEnd, setCircleEnd] = useState(null);

  // 텍스트박스 드래그 상태
  const [textBoxStart, setTextBoxStart] = useState(null);
  const [textBoxEnd, setTextBoxEnd] = useState(null);

  // 선 드래그 상태
  const [lineStart, setLineStart] = useState(null);
  const [lineEnd, setLineEnd] = useState(null);

  // toolbar 드래그 상태
  const [toolbarPosition, setToolbarPosition] = useState(() => {
    // 초기값을 우측 상단으로 설정 (조금 내린 위치)
    const initialRight = typeof window !== 'undefined' ? 12 : 12;
    // top 기준으로 위치 설정 (조금 내린 위치)
    const initialTop = typeof window !== 'undefined' ? 70 : 70;
    return { top: initialTop, right: initialRight };
  });
  const [isDraggingToolbar, setIsDraggingToolbar] = useState(false);
  const toolbarDragStartRef = useRef({ x: 0, y: 0, initialTop: 0, initialRight: 0 });

  // 뒤로 보내기/앞으로 보내기 작업 중 플래그
  const isLayerOperationRef = useRef(false);

  // 색상 선택기 드래그 상태
  const [colorPickerPosition, setColorPickerPosition] = useState(() => {
    // toolbar의 기본 위치를 기준으로 좌측에 배치
    // toolbar: right: 12px, top: 70px
    // toolbar 너비: 약 50px, 간격: 8px
    // 색상박스 너비: 250px
    const initialRight = typeof window !== 'undefined' ? 90 : 90;
    // toolbar와 동일한 top 위치
    const initialTop = typeof window !== 'undefined' ? 70 : 70;
    return { top: initialTop, right: initialRight };
  });
  const [isDraggingColorPicker, setIsDraggingColorPicker] = useState(false);
  const colorPickerDragStartRef = useRef({ x: 0, y: 0, initialTop: 0, initialRight: 0 });

  // 선 설정 모달 및 설정 상태
  const [showLineSettingsModal, setShowLineSettingsModal] = useState(false);
  const [lineSettings, setLineSettings] = useState({
    color: '#000000',
    strokeWidth: 5,
    strokeDasharray: 'none',
    arrowEnd: false,
    arrowStart: false,
  });

  // 색상 팔레트 상태
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [selectedColor, setSelectedColor] = useState({
    fill: "#3b82f6",
    stroke: "#1e40af",
    fillOpacity: 1,
    strokeOpacity: 0 // 테두리는 항상 투명
  });

  // 노드 색상 편집 상태
  const [showNodeColorPicker, setShowNodeColorPicker] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null); // 선택된 Edge ID
  const [nodeColorType, setNodeColorType] = useState('fill'); // 'fill' or 'stroke'
  const [nodeStrokeWidth, setNodeStrokeWidth] = useState(1); // 테두리 굵기
  const [nodeLineStyle, setNodeLineStyle] = useState(0); // 선 스타일 (0: 실선, 30: 점선)

  // 격자 설정
  const gridSize = 10; // 격자 크기 (픽셀)

  // 최신 nodes/edges 상태를 ref로 추적 (브로드캐스트용)
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // 활성 캔버스 변경 시 nodes/edges 업데이트
  useEffect(() => {
    const canvas = canvases.find(c => c.id === activeCanvasId);
    if (canvas) {
      setNodes(canvas.nodes || []);
      setEdges(canvas.edges || []);
    }
  }, [activeCanvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // nodes/edges 변경 시 현재 캔버스 업데이트 (최신 상태 저장)
  useEffect(() => {
    setCanvases(prev =>
      prev.map(canvas =>
        canvas.id === activeCanvasId
          ? { ...canvas, nodes, edges }
          : canvas
      )
    );
  }, [nodes, edges, activeCanvasId]);

  // fileId, tabId와 activeCanvasId를 ref로 추적 (이벤트 리스너에서 최신 값 참조)
  const fileIdRef = useRef(fileId);
  const tabIdRef = useRef(tabId);
  const activeCanvasIdRef = useRef(activeCanvasId);
  const socketIdRef = useRef(null); // 자신의 socketId 추적
  const loadedCanvasesRef = useRef(new Set()); // 이미 로드된 캔버스 ID 추적
  const isPersonalRef = useRef(isPersonal); // 개인 보드 여부 ref (useCallback에서 최신 값 참조)

  useEffect(() => {
    fileIdRef.current = fileId;
    tabIdRef.current = tabId;
    activeCanvasIdRef.current = activeCanvasId;
    isPersonalRef.current = isPersonal;
  }, [fileId, tabId, activeCanvasId, isPersonal]);

  // 소켓 연결 및 이벤트 등록 (한 번만 등록)
  useEffect(() => {
    // 연결 상태 로깅
    const handleConnect = () => {
      socketIdRef.current = socket.id; // socketId 저장
      console.log('✅ Socket.IO 연결 성공:', socket.id);
    };

    const handleDisconnect = (reason) => {
      socketIdRef.current = null;
      console.warn('⚠️ Socket.IO 연결 해제:', reason);
    };

    const handleConnectError = (error) => {
      const socketUrl = socket.io.uri || window.location.origin;
      console.error('❌ Socket.IO 연결 오류:', error);
      console.error('연결 시도 URL:', `${socketUrl}/socket.io/`);
      console.error('서버가 실행 중인지 확인하세요.');
      if (error.message === 'timeout') {
        console.error('타임아웃: 서버가 응답하지 않습니다. 서버가 실행 중인지 확인하세요.');
      }
      if (error.message && error.message.includes('CORS')) {
        console.error('CORS 오류: 서버의 ALLOWED_ORIGINS 환경 변수에 현재 도메인이 포함되어 있는지 확인하세요.');
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    // 이미 연결되어 있으면 socketId 저장
    if (socket.connected) {
      socketIdRef.current = socket.id;
    }

    // 소켓 재연결 시 캔버스 상태 다시 요청
    const handleReconnected = (event) => {
      const newSocketId = event?.detail?.socketId || socket.id;
      console.log('🔄 소켓 재연결 감지, 캔버스 상태 재요청:', newSocketId);

      // socketId 업데이트
      socketIdRef.current = newSocketId;

      // socket이 연결될 때까지 대기 (이벤트가 먼저 발생할 수 있음)
      const requestCanvasState = () => {
        if (socket.connected && fileIdRef.current && tabIdRef.current && activeCanvasIdRef.current) {
          // 현재 캔버스의 캐시 제거 (최신 상태를 받기 위해)
          const currentCanvasId = `${fileIdRef.current}-${tabIdRef.current}-${activeCanvasIdRef.current}`;
          loadedCanvasesRef.current.delete(currentCanvasId);
          console.log('🔄 재연결 후 캔버스 캐시 제거:', currentCanvasId);

          // 서버에서 최신 상태 다시 요청
          const userName = getUserNameFromMsal();
          socket.emit("canvas-init", { canvasId: currentCanvasId, userName });
          console.log('📤 재연결 후 canvas-init 요청:', currentCanvasId, userName);
        } else {
          // 아직 연결되지 않았으면 잠시 후 재시도
          setTimeout(requestCanvasState, 100);
        }
      };

      requestCanvasState();
    };

    // 커스텀 재연결 이벤트 리스너 등록
    window.addEventListener('socket:reconnected', handleReconnected);

    // 연결 시 서버에서 초기 상태 받기
    // ⚡ Yjs가 데이터 동기화를 처리하므로 canvas-init은 유저 추적/room join 용도로만 사용
    const handleCanvasInit = (state) => {
      const currentCanvasId = `${fileIdRef.current}-${tabIdRef.current}-${activeCanvasIdRef.current}`;
      if (state && state.canvasId && state.canvasId === currentCanvasId) {
        // ⚡ [Yjs 전환] 노드/엣지 데이터 처리는 Yjs가 담당
        // Socket.IO canvas-init은 room join과 user-count 용도로만 유지
        console.log('ℹ️ [Yjs] canvas-init 수신 (유저 추적 전용):', state.canvasId);
        
        // 로드 완료 표시 (캐시 관리용)
        loadedCanvasesRef.current.add(state.canvasId);

        // ⚡ [Yjs 전환] 히스토리/뷰포트/노드 초기화는 Yjs useEffect에서 처리
        // (히스토리 → Yjs UndoManager, 뷰포트 → yjsReady useEffect)
      } else {
        console.log('ℹ️ [Yjs] canvas-init 무시 (다른 캔버스):', {
          receivedCanvasId: state?.canvasId,
          currentCanvasId
        });
      }
    };


    // ⚡ [Yjs 전환] canvas-update 핸들러 제거됨
    // Yjs CRDT observer가 실시간 노드/엣지 동기화를 자동으로 처리합니다.
    // Socket.IO canvas-update 이벤트는 더 이상 사용되지 않습니다.

    console.log('🔌 Socket 연결 상태:', socket.connected, 'Socket ID:', socket.id);

    // 접속 인원 수 업데이트 (Socket.IO 유지 - Yjs Awareness와 병행)
    const handleUserCount = (data) => {
      if (data && typeof data.count === 'number') {
        // Yjs Awareness가 더 정확하므로 Yjs 값이 있으면 Yjs 우선
        // (여기서는 fallback용으로만 유지)
        if (!yjsConnectedUsers || yjsConnectedUsers <= 1) {
          setConnectedUsers(data.count);
          setUserList(data.users || []);
        }
      }
    };

    socket.on("canvas-init", handleCanvasInit);
    // ⚡ canvas-update 리스너 제거됨 (Yjs가 대체)
    socket.on("user-count", handleUserCount);

    return () => {
      socket.off("canvas-init", handleCanvasInit);
      socket.off("user-count", handleUserCount);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      window.removeEventListener('socket:reconnected', handleReconnected);

      // 타이머 정리
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (thumbnailTimeoutRef.current) {
        clearTimeout(thumbnailTimeoutRef.current);
      }
    };
  }, []); // 한 번만 실행

  // canvases를 ref로 추적 (의존성 문제 방지)
  const canvasesRef = useRef(canvases);
  useEffect(() => {
    canvasesRef.current = canvases;
  }, [canvases]);

  // 탭 변경 추적용 ref
  const prevTabIdForCacheRef = useRef(tabId);

  // ⚡ [Yjs] 활성 캔버스 변경 시 처리
  // Yjs는 canvasId(yjsCanvasId) 변경 시 useYjsSync 내에서 자동으로 새 문서에 연결됩니다.
  // Socket.IO canvas-init은 유저 추적/room join 용도로만 emit합니다.
  useEffect(() => {
    const canvasIdWithFileAndTab = `${fileId}-${tabId}-${activeCanvasId}`;
    const t0 = performance.now();

    // 노드를 빈 배열로 초기화 (Yjs 동기화 전까지)
    isInitialLoadRef.current = true;
    setNodes([]);
    setEdges([]);

    // 탭 변경 감지
    const prevTabId = prevTabIdForCacheRef.current;
    const isTabChanged = prevTabId !== tabId;
    if (isTabChanged) {
      // 탭 변경 시 캐시 제거
      if (prevTabId !== null && prevTabId !== undefined) {
        const prevTabCanvasPattern = `${fileId}-${prevTabId}-`;
        const prevCanvasesToRemove = Array.from(loadedCanvasesRef.current).filter(id => id.startsWith(prevTabCanvasPattern));
        prevCanvasesToRemove.forEach(id => loadedCanvasesRef.current.delete(id));
      }
      const tabCanvasPattern = `${fileId}-${tabId}-`;
      const canvasesToRemove = Array.from(loadedCanvasesRef.current).filter(id => id.startsWith(tabCanvasPattern));
      canvasesToRemove.forEach(id => loadedCanvasesRef.current.delete(id));

      setCanvases(prev => prev.map(canvas =>
        canvas.id === activeCanvasId
          ? { ...canvas, nodes: [], edges: [] }
          : canvas
      ));

      prevTabIdForCacheRef.current = tabId;
    }

    // Socket.IO canvas-init emit (유저 추적/room join 용도)
    // Yjs가 데이터 동기화를 처리하므로 데이터 로드 목적이 아님
    const requestCanvasInit = () => {
      const userName = getUserNameFromMsal();
      if (socket.connected) {
        socket.emit("canvas-init", { canvasId: canvasIdWithFileAndTab, userName });
      } else {
        socket.once("connect", () => {
          socket.emit("canvas-init", { canvasId: canvasIdWithFileAndTab, userName });
        });
      }
    };

    // 탭 변경이 복제일 가능성이 높은 경우 약간의 지연
    const isLikelyDuplicate = isTabChanged && prevTabId !== null && prevTabId !== undefined && tabId > prevTabId;
    if (isLikelyDuplicate) {
      setTimeout(requestCanvasInit, 500);
    } else {
      requestCanvasInit();
    }

  }, [activeCanvasId, tabId, fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 탭 변경 시 노드들을 중앙에 맞추기 (고정된 확대 비율 사용)
  const prevTabIdRef = useRef(tabId);
  useEffect(() => {
    if (prevTabIdRef.current !== tabId && nodes.length > 0) {
      // 탭이 변경되고 노드가 있을 때만 중앙으로 이동
      setTimeout(() => {
        // 노드들의 중심점 계산
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        nodes.forEach(node => {
          const x = node.position.x;
          const y = node.position.y;
          const width = node.width || 200;
          const height = node.height || 150;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x + width);
          maxY = Math.max(maxY, y + height);
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // 화면 중앙으로 이동 (고정된 zoom 레벨 사용)
        const pane = document.querySelector('.react-flow__pane');
        if (pane) {
          const paneWidth = pane.clientWidth;
          const paneHeight = pane.clientHeight;

          // 화면 중앙에 노드 중심이 오도록 x, y 계산
          const x = paneWidth / 2 - centerX * fixedZoom;
          const y = paneHeight / 2 - centerY * fixedZoom;

          setViewportRef.current({ x, y, zoom: fixedZoom }, { duration: 600 });
        }
      }, 200); // 노드가 완전히 렌더링될 때까지 대기
    }
    prevTabIdRef.current = tabId;
  }, [tabId, nodes.length]);

  // 모드 상태 디버깅
  useEffect(() => {
    console.log('모드 상태 변경:', { addCardMode, addTemplateMode, templateGridSize });
  }, [addCardMode, addTemplateMode, templateGridSize]);

  // 커서 모양 변경
  useEffect(() => {
    const updateCursor = () => {
      const reactFlowPane = document.querySelector('.react-flow__pane');
      if (reactFlowPane) {
        if (addCardMode || addTemplateMode || toolMode || boxSelectionMode) {
          reactFlowPane.style.cursor = toolMode === 'pen' ? 'crosshair' : 'crosshair';
        } else {
          reactFlowPane.style.cursor = 'default';
        }
      }
    };

    // 즉시 실행
    updateCursor();

    // ReactFlow가 렌더링될 때까지 대기
    const timer = setTimeout(updateCursor, 100);

    // MutationObserver로 DOM 변경 감지
    const observer = new MutationObserver(updateCursor);
    const reactFlowContainer = document.querySelector('.react-flow');
    if (reactFlowContainer) {
      observer.observe(reactFlowContainer, { childList: true, subtree: true });
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      const pane = document.querySelector('.react-flow__pane');
      if (pane) {
        pane.style.cursor = 'default';
      }
    };
  }, [addCardMode, addTemplateMode, toolMode, boxSelectionMode]);

  // 썸네일 생성 요청 함수 (서버에 비동기로 요청)
  // 썸네일 자동 생성 로직 주석처리
  const generateThumbnail = useCallback(() => {
    // 썸네일 자동 생성 기능 임시 비활성화
    return;
  }, []);

  // =============================================
  // Yjs CRDT 동기화 훅 초기화
  // - Socket.IO canvas-update를 대체하여 CRDT 기반 실시간 공동작업 제공
  // - Yjs UndoManager로 유저별 독립 Undo/Redo
  // - Awareness 프로토콜로 유저 프레즌스
  // =============================================
  const yjsCanvasId = useMemo(() => {
    if (!fileId || fileId === 'default' || !tabId || tabId === 'default') return null;
    return `${fileId}-${tabId}-${activeCanvasId}`;
  }, [fileId, tabId, activeCanvasId]);

  // 유저명 기반 결정론적 색상 (같은 유저는 항상 같은 색 → 새로고침/탭 전환해도 색상 일관성 유지)
  const getUserColorByName = (name) => {
    if (!name) return '#A89F91';
    const palette = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD',
      '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA',
      '#F1948A', '#73C6B6', '#FF8C42', '#6C5CE7', '#00B894',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = (hash << 5) - hash + name.charCodeAt(i);
      hash |= 0;
    }
    return palette[Math.abs(hash) % palette.length];
  };

  const yjsUser = useMemo(() => {
    const name = getUserNameFromMsal() || 'Anonymous';
    return { name, color: getUserColorByName(name) };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    isReady: yjsReady,
    isSynced: yjsSynced,
    connectedUsers: yjsConnectedUsers,
    userList: yjsUserList,
    canUndo: yjsCanUndo,
    canRedo: yjsCanRedo,
    syncNodes,
    syncEdges,
    undo: yjsUndo,
    redo: yjsRedo,
    beginUndoCapture,
    commitUndoCapture,
    updateSelectedNodes: yjsUpdateSelectedNodes,
    updateCursor: yjsUpdateCursor,
    updateViewport: yjsUpdateViewport,
    awarenessStates,
    ydocRef,
  } = useYjsSync({
    canvasId: yjsCanvasId,
    setNodes,
    setEdges,
    nodesRef,
    edgesRef,
    user: yjsUser,
    draggingNodeIdsRef,
    resizingNodeIdsRef,
    generateThumbnail,
    enabled: !!yjsCanvasId,
  });

  // Yjs 초기 동기화 완료 시 뷰포트 중앙 정렬
  useEffect(() => {
    if (yjsReady && nodesRef.current && nodesRef.current.length > 0) {
      setTimeout(() => {
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      }, 500);
      requestAnimationFrame(() => {
        const t0 = performance.now();
        const currentNodes = nodesRef.current;
        if (!currentNodes || currentNodes.length === 0) return;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        currentNodes.forEach(node => {
          const x = node.position.x;
          const y = node.position.y;
          const width = node.width || 200;
          const height = node.height || 150;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x + width);
          maxY = Math.max(maxY, y + height);
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        const pane = document.querySelector('.react-flow__pane');
        if (pane) {
          const paneWidth = pane.clientWidth;
          const paneHeight = pane.clientHeight;
          const x = paneWidth / 2 - centerX * fixedZoom;
          const y = paneHeight / 2 - centerY * fixedZoom;
          setViewportRef.current({ x, y, zoom: fixedZoom }, { duration: 600 });
        }
      });

      // Yjs가 데이터를 로드했으므로 캔버스 캐시에도 추가
      if (yjsCanvasId) {
        loadedCanvasesRef.current.add(yjsCanvasId);
      }
    }
  }, [yjsReady, yjsCanvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Yjs connectedUsers를 기존 state와 동기화
  useEffect(() => {
    if (yjsConnectedUsers > 0) {
      setConnectedUsers(yjsConnectedUsers);
      setUserList(yjsUserList);
    }
  }, [yjsConnectedUsers, yjsUserList]);

  // broadcast 함수 - Yjs CRDT를 통해 노드/엣지 변경사항 동기화
  // ⚡ 기존 Socket.IO 기반에서 Yjs 기반으로 전환됨
  // - CRDT가 충돌 해결을 자동 처리
  // - UndoManager가 히스토리를 자동 관리
  // - saveHistory=false → UndoManager에 기록하지 않음 (선택 변경 등 UI 상태)
  // - 함수 시그니처는 호환성을 위해 유지
  //
  // 🔧 호환성 처리: 구 API에서 2번째 인수로 edges 배열을 전달하는 호출 패턴 감지
  //   구 API: broadcast(nodes, edges, saveHistory, updatedNodeIds)
  //   신 API: broadcast(nodes, immediate, updatedNodeIds, saveHistory, deletedNodeIds)
  const broadcast = useCallback((newNodes, immediateOrEdges = false, updatedNodeIds = null, saveHistory = true, deletedNodeIds = null) => {
    // 외부 업데이트 중이면 동기화하지 않음 (무한 루프 방지)
    if (isExternalUpdateRef.current) {
      console.log('🚫 [Yjs] 외부 업데이트 중이므로 syncNodes 건너뜀');
      return;
    }

    // 🔧 구 API 감지: 2번째 인수가 배열이면 edges로 간주
    // 예: broadcast(nodesRef.current, updatedEdges, false, [])
    //     broadcast(updatedNodes, edgesRef.current, true, [selectedNodeId])
    //     broadcast(nodesRef.current, newEdges, true, [])
    if (Array.isArray(immediateOrEdges)) {
      const edgesArray = immediateOrEdges;
      // 구 API의 3번째 인수는 saveHistory(boolean) 또는 updatedNodeIds
      // 구 API의 4번째 인수는 updatedNodeIds 배열
      const oldArg3 = updatedNodeIds; // 구 API에서는 saveHistory 또는 노드 변경 여부
      const oldArg4 = saveHistory;     // 구 API에서는 updatedNodeIds

      // 엣지 동기화
      syncEdges(edgesArray);

      // 구 API에서 노드도 함께 전달된 경우 노드도 동기화
      // 4번째 인수가 배열이면 updatedNodeIds
      const nodeIds = Array.isArray(oldArg4) && oldArg4.length > 0 ? oldArg4 : null;
      if (nodeIds && nodeIds.length > 0) {
        nodesRef.current = newNodes;
        syncNodes(newNodes, {
          updatedNodeIds: nodeIds,
          triggerThumbnail: true,
        });
      }

      console.log('📤 [Yjs] 엣지+노드 동기화 (구 API):', {
        edgeCount: edgesArray?.length || 0,
        updatedNodeIds: nodeIds,
      });
      return;
    }

    // 신 API: broadcast(nodes, immediate, updatedNodeIds, saveHistory, deletedNodeIds)
    // nodesRef 업데이트 (최신 상태 유지)
    nodesRef.current = newNodes;

    // updatedNodeIds 유효성 검사 (true나 다른 값이 올 수 있음)
    const validUpdatedNodeIds = updatedNodeIds === null
      ? null
      : (Array.isArray(updatedNodeIds) ? updatedNodeIds : null);

    // Yjs CRDT에 동기화 (자동으로 모든 클라이언트에 전파)
    // saveHistory=false → UndoManager에 기록하지 않음 (선택 변경 등)
    syncNodes(newNodes, {
      updatedNodeIds: validUpdatedNodeIds,
      deletedNodeIds: deletedNodeIds,
      triggerThumbnail: validUpdatedNodeIds && validUpdatedNodeIds.length > 0,
      trackHistory: saveHistory !== false,
    });

    // console.log('📤 [Yjs] 노드 동기화:', {
    //   nodeCount: newNodes?.length || 0,
    //   updatedNodeIds: validUpdatedNodeIds,
    //   deletedNodeIds: deletedNodeIds,
    //   trackHistory: saveHistory !== false,
    // });
  }, [syncNodes, syncEdges]);

  // 히스토리에 현재 상태 저장
  const saveToHistory = useCallback((newNodes, newEdges, updatedNodeIds = null) => {
    // 🔍 디버깅: 함수 호출 시 파라미터 확인
    console.log('🔍 saveToHistory 호출됨:', {
      newNodesCount: newNodes?.length,
      newEdgesCount: newEdges?.length,
      updatedNodeIds: updatedNodeIds,
      updatedNodeIdsType: typeof updatedNodeIds,
      isArray: Array.isArray(updatedNodeIds),
      length: Array.isArray(updatedNodeIds) ? updatedNodeIds.length : 'N/A'
    });

    // 노드에서 선택 관련 속성 제거 헬퍼 함수
    const removeSelectionFromNode = (node) => {
      const { selected, data, ...nodeWithoutSelected } = node;
      const { selectedBy, ...dataWithoutSelectedBy } = data || {};
      return { ...nodeWithoutSelected, data: dataWithoutSelectedBy };
    };

    // 히스토리가 초기화되지 않았으면 저장하지 않음 (페이지 로드 전)
    if (!isHistoryInitializedRef.current) {
      console.log('🔍 saveToHistory 조기 반환: 히스토리 미초기화');
      return;
    }

    // undo/redo 작업 중이거나 외부 업데이트 중이면 저장하지 않음
    if (isUndoRedoRef.current || isExternalUpdateRef.current) {
      console.log('📝 히스토리 저장 건너뜀:', {
        isUndoRedo: isUndoRedoRef.current,
        isExternalUpdate: isExternalUpdateRef.current
      });
      return;
    }

    // updatedNodeIds가 빈 배열인 경우 저장하지 않음 (updatedNodeCount: 0)
    if (Array.isArray(updatedNodeIds) && updatedNodeIds.length === 0) {
      console.log('📝 히스토리 저장 건너뜀: updatedNodeCount가 0 ✅ 여기서 반환됨');
      return;
    }

    // Undo 후 새 작업 시: 현재 인덱스 이후의 히스토리 제거 (미래 상태 버리기)
    // 현재 인덱스가 마지막이 아니면 (즉, Undo 후 상태면) 이후 히스토리 제거
    if (historyIndexRef.current < historyRef.current.length - 1) {
      // 현재 인덱스까지의 히스토리만 유지 (현재 인덱스 포함)
      const removedCount = historyRef.current.length - (historyIndexRef.current + 1);
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
      console.log('🔄 Undo 후 새 작업: 미래 상태 제거', {
        removedCount,
        currentIndex: historyIndexRef.current,
        newLength: historyRef.current.length
      });
    }

    // 히스토리 저장 시 현재 상태를 그대로 저장 (상대 작업 포함)
    // 변경되지 않은 노드도 현재 상태를 저장하여 상대 작업을 보존
    let nodesToSave = newNodes;
    if (updatedNodeIds === null) {
      // null이면 전체 노드 업데이트 (노드 삭제 등)이므로 전체 저장
      nodesToSave = newNodes;
    }
    // updatedNodeIds가 있으면 변경된 노드의 이전 상태만 별도 저장 (previousNodeStates)
    // nodesToSave는 현재 상태(newNodes)를 그대로 사용하여 상대 작업 보존
    // updatedNodeIds가 빈 배열이면 변경 없음이므로 저장하지 않음 (아래 비교에서 처리)

    // 이전 히스토리 상태와 비교 (selected 속성 및 displayOrder 제외)
    // 현재 인덱스의 히스토리와 비교 (Undo 후에는 현재 인덱스가 마지막이 됨)
    // 히스토리가 비어있으면 initialStateRef 사용 (첫 번째 작업)
    const lastHistory = historyRef.current.length > 0 
      ? historyRef.current[historyRef.current.length - 1]
      : (initialStateRef.current ? { nodes: initialStateRef.current.nodes, edges: initialStateRef.current.edges, previousNodeStates: {} } : null);

    // selected 속성과 displayOrder, selectedBy를 제외한 노드 비교 함수
    const compareNodesWithoutSelection = (node1, node2) => {
      const { selected: _, data: data1, ...node1WithoutSelected } = node1;
      const { selected: __, data: data2, ...node2WithoutSelected } = node2;

      // data에서 displayOrder와 selectedBy 제외하고 비교
      const data1WithoutSelection = data1 ? { ...data1, displayOrder: undefined, selectedBy: undefined } : {};
      const data2WithoutSelection = data2 ? { ...data2, displayOrder: undefined, selectedBy: undefined } : {};

      const node1ForCompare = { ...node1WithoutSelected, data: data1WithoutSelection };
      const node2ForCompare = { ...node2WithoutSelected, data: data2WithoutSelection };

      return JSON.stringify(node1ForCompare) === JSON.stringify(node2ForCompare);
    };

    if (lastHistory) {
      // updatedNodeIds가 제공된 경우, 해당 노드들만 마지막 히스토리와 비교
      const savedUpdatedNodeIds = updatedNodeIds === null
        ? null
        : (Array.isArray(updatedNodeIds) ? updatedNodeIds : []);

      if (savedUpdatedNodeIds && savedUpdatedNodeIds.length > 0) {
        // updatedNodeIds가 제공된 경우, 해당 노드들만 비교
        const lastHistoryNodesMap = new Map(lastHistory.nodes.map(n => [n.id, n]));
        let hasActualChange = false;

        savedUpdatedNodeIds.forEach(nodeId => {
          const prevNode = lastHistoryNodesMap.get(nodeId);
          const currentNode = newNodes.find(n => n.id === nodeId);

          if (!prevNode) {
            // 새 노드가 추가된 경우
            if (currentNode) {
              hasActualChange = true;
            }
          } else if (!currentNode) {
            // 노드가 삭제된 경우
            hasActualChange = true;
          } else {
            // displayOrder 변경 확인 (뒤로 보내기/앞으로 보내기 작업)
            const prevDisplayOrder = prevNode.data?.displayOrder ?? 0;
            const currentDisplayOrder = currentNode.data?.displayOrder ?? 0;
            if (prevDisplayOrder !== currentDisplayOrder) {
              hasActualChange = true;
            } else if (!compareNodesWithoutSelection(prevNode, currentNode)) {
              // 노드가 실제로 변경된 경우
              hasActualChange = true;
            }
          }
        });

        // 실제 변경이 없으면 저장하지 않음
        if (!hasActualChange) {
          console.log('📝 히스토리 저장 건너뜀: updatedNodeIds에 해당하는 노드들이 실제로 변경되지 않음', {
            updatedNodeIds: savedUpdatedNodeIds
          });
          return;
        }
      } else {
        // updatedNodeIds가 없거나 null인 경우 전체 비교
        // 노드/엣지 개수가 다르면 실제 변경이므로 저장
        if (newNodes.length !== lastHistory.nodes.length || newEdges.length !== lastHistory.edges.length) {
          // 실제 변경이므로 저장 진행
        } else {
          // 노드 개수가 같으면 ID로 매칭하여 selected 제외하고 비교
          const oldNodesMap = new Map(lastHistory.nodes.map(n => [n.id, n]));
          const allNodesSame = newNodes.every((newNode) => {
            const oldNode = oldNodesMap.get(newNode.id);
            if (!oldNode) {
              return false; // 새 노드가 있으면 변경됨
            }
            return compareNodesWithoutSelection(newNode, oldNode);
          });

          // 엣지도 ID로 매칭하여 selected 제외하고 비교
          const oldEdgesMap = new Map(lastHistory.edges.map(e => [e.id, e]));
          const allEdgesSame = newEdges.every((newEdge) => {
            const oldEdge = oldEdgesMap.get(newEdge.id);
            if (!oldEdge) {
              return false; // 새 엣지가 있으면 변경됨
            }
            const { selected: _, ...edge1WithoutSelected } = newEdge;
            const { selected: __, ...edge2WithoutSelected } = oldEdge;
            return JSON.stringify(edge1WithoutSelected) === JSON.stringify(edge2WithoutSelected);
          });

          // selected만 변경된 경우 저장하지 않음
          if (allNodesSame && allEdgesSame) {
            console.log('📝 히스토리 저장 건너뜀: 선택(selected)만 변경됨');
            return;
          }
        }
      }
    }

    // 깊은 복사로 저장 (참조 문제 방지)
    // selected 속성과 selectedBy 제거 (선택 상태는 로컬에서만 유지)
    // nodesToSave 사용 (updatedNodeIds 처리 후의 노드 배열)
    const nodesCopy = JSON.parse(JSON.stringify(nodesToSave.map(node => {
      const { selected, data, ...nodeWithoutSelected } = node;
      const { selectedBy, ...dataWithoutSelectedBy } = data || {};
      return { ...nodeWithoutSelected, data: dataWithoutSelectedBy };
    })));
    const edgesCopy = JSON.parse(JSON.stringify(newEdges.map(edge => {
      const { selected, ...edgeWithoutSelected } = edge;
      return edgeWithoutSelected;
    })));

    // 변경된 노드 ID 목록 저장 (null이면 전체 업데이트, 빈 배열이면 변경 없음)
    // updatedNodeIds가 배열인지 확인 (true나 다른 값이 올 수 있음)
    const savedUpdatedNodeIds = updatedNodeIds === null
      ? null
      : (Array.isArray(updatedNodeIds) ? updatedNodeIds : []);

    // 빈 배열이면 변경 없음이므로 히스토리 저장 건너뛰기
    if (Array.isArray(savedUpdatedNodeIds) && savedUpdatedNodeIds.length === 0) {
      console.log('📝 히스토리 저장 건너뜀: updatedNodeIds가 빈 배열 (변경 없음)');
      return;
    }

    // 변경된 노드의 이전 상태 저장 (Undo 시 상대 작업을 건드리지 않기 위해)
    // 내가 작업하기 전의 노드 상태를 저장 (이전 히스토리에서 가져오기)
    let previousNodeStates = null;

    if (savedUpdatedNodeIds && savedUpdatedNodeIds.length > 0) {
      // 변경된 노드가 있는 경우
      if (lastHistory) {
        previousNodeStates = {};

        // selected 속성과 displayOrder, selectedBy를 제외한 노드 비교 함수
        const compareNodesWithoutSelection = (node1, node2) => {
          const { selected: _, data: data1, ...node1WithoutSelected } = node1;
          const { selected: __, data: data2, ...node2WithoutSelected } = node2;

          // data에서 displayOrder와 selectedBy 제외하고 비교
          const data1WithoutSelection = data1 ? { ...data1, displayOrder: undefined, selectedBy: undefined } : {};
          const data2WithoutSelection = data2 ? { ...data2, displayOrder: undefined, selectedBy: undefined } : {};

          const node1ForCompare = { ...node1WithoutSelected, data: data1WithoutSelection };
          const node2ForCompare = { ...node2WithoutSelected, data: data2WithoutSelection };

          return JSON.stringify(node1ForCompare) === JSON.stringify(node2ForCompare);
        };

        // 마지막 히스토리의 previousNodeStates와 비교하여 실제로 다른 것만 저장
        const lastPreviousNodeStates = lastHistory.previousNodeStates || {};

        savedUpdatedNodeIds.forEach(nodeId => {
          // 이전 히스토리에서 해당 노드의 상태를 찾기
          const prevNode = lastHistory.nodes.find(n => n.id === nodeId);
          const currentNode = newNodes.find(n => n.id === nodeId);
          const lastPreviousState = lastPreviousNodeStates[nodeId];

          if (prevNode) {
            // 이전 히스토리에 있으면 실제로 변경되었는지 확인
            // displayOrder 변경도 체크 (앞으로 보내기/뒤로 보내기)
            const prevDisplayOrder = prevNode.data?.displayOrder ?? 0;
            const currentDisplayOrder = currentNode?.data?.displayOrder ?? 0;
            const displayOrderChanged = prevDisplayOrder !== currentDisplayOrder;
            const nodeContentChanged = currentNode && !compareNodesWithoutSelection(prevNode, currentNode);
            
            if (currentNode && (nodeContentChanged || displayOrderChanged)) {
              // 실제로 변경되었으면 이전 상태를 저장
              // 단, 마지막 히스토리의 previousNodeStates에 이미 저장된 상태와 다른 경우에만 저장
              if (lastPreviousState === undefined ||
                (lastPreviousState !== null && !compareNodesWithoutSelection(lastPreviousState, prevNode))) {
                // 마지막 히스토리의 previousNodeStates에 없거나 다른 경우에만 저장
                previousNodeStates[nodeId] = JSON.parse(JSON.stringify(removeSelectionFromNode(prevNode)));
                console.log(`  ✅ previousNodeStates[${nodeId}] 저장 (이전 히스토리에서, 실제 변경됨):`, {
                  nodeId,
                  position: prevNode.position,
                  type: prevNode.type,
                  hasData: !!prevNode.data,
                  hadLastPreviousState: lastPreviousState !== undefined,
                  displayOrderChanged,
                  prevDisplayOrder,
                  currentDisplayOrder
                });
              } else {
                // 마지막 히스토리의 previousNodeStates와 같으면 저장하지 않음
                console.log(`  ⏭️ previousNodeStates[${nodeId}] 저장 건너뜀 (마지막 히스토리의 previousNodeStates와 동일):`, {
                  nodeId,
                  position: currentNode.position,
                  type: currentNode.type
                });
              }
            } else if (!currentNode) {
              // 노드가 삭제되었으면 이전 상태를 저장
              // 단, 마지막 히스토리의 previousNodeStates에 이미 저장된 상태와 다른 경우에만 저장
              if (lastPreviousState === undefined ||
                (lastPreviousState !== null && !compareNodesWithoutSelection(lastPreviousState, prevNode))) {
                previousNodeStates[nodeId] = JSON.parse(JSON.stringify(removeSelectionFromNode(prevNode)));
                console.log(`  ✅ previousNodeStates[${nodeId}] 저장 (이전 히스토리에서, 삭제됨):`, {
                  nodeId,
                  position: prevNode.position,
                  type: prevNode.type,
                  hasData: !!prevNode.data
                });
              } else {
                console.log(`  ⏭️ previousNodeStates[${nodeId}] 저장 건너뜀 (마지막 히스토리의 previousNodeStates와 동일, 삭제):`, {
                  nodeId
                });
              }
            } else {
              // 실제로 변경되지 않았으면 저장하지 않음
              console.log(`  ⏭️ previousNodeStates[${nodeId}] 저장 건너뜀 (변경 없음):`, {
                nodeId,
                position: currentNode.position,
                type: currentNode.type,
                displayOrderChanged,
                prevDisplayOrder,
                currentDisplayOrder
              });
            }
          } else {
            // 이전 히스토리에 없으면 새로 추가된 노드
            // 새로 추가된 노드는 previousNodeStates에 null을 저장하여 Undo 시 삭제되도록 함
            // 단, 마지막 히스토리의 previousNodeStates에 이미 null로 저장된 경우 저장하지 않음
            if (currentNode) {
              if (lastPreviousState === undefined) {
                // 마지막 히스토리의 previousNodeStates에 없으면 새로 저장
                previousNodeStates[nodeId] = null;
                console.log(`  ⚠️ previousNodeStates[${nodeId}] 저장 (새 노드, null로 표시 - Undo 시 삭제):`, {
                  nodeId,
                  position: currentNode.position,
                  type: currentNode.type,
                  hasData: !!currentNode.data
                });
              } else {
                // 마지막 히스토리의 previousNodeStates에 이미 null로 저장되어 있으면 저장하지 않음
                console.log(`  ⏭️ previousNodeStates[${nodeId}] 저장 건너뜀 (마지막 히스토리의 previousNodeStates에 이미 null로 저장됨):`, {
                  nodeId,
                  position: currentNode.position,
                  type: currentNode.type
                });
              }
            } else {
              console.log(`  ❌ previousNodeStates[${nodeId}] 저장 실패: 노드를 찾을 수 없음`);
            }
          }
        });

        // previousNodeStates가 비어있으면 실제 변경이 없으므로 히스토리 저장 건너뛰기
        if (Object.keys(previousNodeStates).length === 0) {
          console.log('📝 히스토리 저장 건너뜀: previousNodeStates가 비어있음 (실제 변경 없음)');
          return;
        }

        console.log('📝 previousNodeStates 저장 완료:', {
          savedCount: Object.keys(previousNodeStates).length,
          nodeIds: Object.keys(previousNodeStates),
          details: Object.keys(previousNodeStates).map(id => ({
            nodeId: id,
            position: previousNodeStates[id]?.position,
            type: previousNodeStates[id]?.type,
            isNull: previousNodeStates[id] === null
          }))
        });
      } else {
        // 이전 히스토리가 없으면 (첫 번째 히스토리) initialStateRef에서 이전 상태 가져오기
        previousNodeStates = {};
        console.log('⚠️ 이전 히스토리가 없음 (첫 번째 히스토리), initialStateRef 사용');
        
        // initialStateRef에서 이전 상태 가져오기
        const initialNodes = initialStateRef.current?.nodes || [];
        const initialNodesMap = new Map(initialNodes.map(n => [n.id, n]));
        
        savedUpdatedNodeIds.forEach(nodeId => {
          const initialNode = initialNodesMap.get(nodeId);
          const currentNode = newNodes.find(n => n.id === nodeId);
          
          if (initialNode) {
            // 초기 상태에 있던 노드 - 초기 상태 저장
            previousNodeStates[nodeId] = JSON.parse(JSON.stringify(removeSelectionFromNode(initialNode)));
            console.log(`  ✅ previousNodeStates[${nodeId}] 저장 (첫 번째 히스토리, 초기 상태 사용):`, {
              nodeId,
              position: initialNode.position,
              type: initialNode.type
            });
          } else if (currentNode) {
            // 새로 추가된 노드 - null로 표시 (Undo 시 삭제)
            previousNodeStates[nodeId] = null;
            console.log(`  ⚠️ previousNodeStates[${nodeId}] 저장 (첫 번째 히스토리, 새 노드 - null):`, {
              nodeId,
              position: currentNode.position,
              type: currentNode.type
            });
          }
        });
      }
    } else if (savedUpdatedNodeIds === null) {
      // null이면 전체 노드 업데이트 (노드 삭제 등)
      // 이전 히스토리와 현재 상태를 비교하여 실제로 변경된 노드만 찾아서 저장
      console.log('📝 previousNodeStates 저장 시작 (전체 업데이트, 변경된 노드 찾기):', {
        hasLastHistory: !!lastHistory,
        lastHistoryNodesCount: lastHistory?.nodes?.length || 0,
        currentNodesCount: newNodes.length
      });

      if (lastHistory) {
        previousNodeStates = {};
        const lastHistoryNodesMap = new Map(lastHistory.nodes.map(n => [n.id, n]));
        const currentNodesMap = new Map(newNodes.map(n => [n.id, n]));

        // selected 속성과 displayOrder, selectedBy를 제외한 노드 비교 함수
        const compareNodesWithoutSelection = (node1, node2) => {
          const { selected: _, data: data1, ...node1WithoutSelected } = node1;
          const { selected: __, data: data2, ...node2WithoutSelected } = node2;

          // data에서 displayOrder와 selectedBy 제외하고 비교
          const data1WithoutSelection = data1 ? { ...data1, displayOrder: undefined, selectedBy: undefined } : {};
          const data2WithoutSelection = data2 ? { ...data2, displayOrder: undefined, selectedBy: undefined } : {};

          const node1ForCompare = { ...node1WithoutSelected, data: data1WithoutSelection };
          const node2ForCompare = { ...node2WithoutSelected, data: data2WithoutSelection };

          return JSON.stringify(node1ForCompare) === JSON.stringify(node2ForCompare);
        };

        // 이전 히스토리에 있던 모든 노드의 이전 상태 저장
        lastHistory.nodes.forEach(prevNode => {
          const currentNode = currentNodesMap.get(prevNode.id);
          // 노드가 삭제되었거나 변경되었으면 이전 상태 저장
          if (!currentNode || !compareNodesWithoutSelection(prevNode, currentNode)) {
            previousNodeStates[prevNode.id] = JSON.parse(JSON.stringify(removeSelectionFromNode(prevNode)));
            console.log(`  ✅ previousNodeStates[${prevNode.id}] 저장 (전체 업데이트, 이전 히스토리에서):`, {
              nodeId: prevNode.id,
              position: prevNode.position,
              type: prevNode.type,
              reason: !currentNode ? '삭제됨' : '변경됨'
            });
          }
        });

        // 새로 추가된 노드는 null로 표시 (Undo 시 삭제용)
        newNodes.forEach(currentNode => {
          if (!lastHistoryNodesMap.has(currentNode.id)) {
            previousNodeStates[currentNode.id] = null;
            console.log(`  ⚠️ previousNodeStates[${currentNode.id}] 저장 (전체 업데이트, 새 노드 - null로 표시):`, {
              nodeId: currentNode.id,
              position: currentNode.position,
              type: currentNode.type
            });
          }
        });

        console.log('📝 previousNodeStates 저장 완료 (전체 업데이트):', {
          savedCount: Object.keys(previousNodeStates).length,
          nodeIds: Object.keys(previousNodeStates)
        });
      } else {
        console.log('⚠️ 이전 히스토리가 없어서 previousNodeStates 저장 건너뜀 (전체 업데이트)');
      }
    } else {
      // savedUpdatedNodeIds가 빈 배열이면 이미 위에서 return 했으므로 여기 도달하지 않음
      // 하지만 null인 경우는 전체 업데이트이므로 previousNodeStates 저장 필요
      if (savedUpdatedNodeIds === null) {
        // 전체 업데이트인 경우 previousNodeStates 저장
        if (lastHistory) {
          previousNodeStates = {};
          const lastHistoryNodesMap = new Map(lastHistory.nodes.map(n => [n.id, n]));

          // selected 속성과 displayOrder, selectedBy를 제외한 노드 비교 함수
          const compareNodesWithoutSelection = (node1, node2) => {
            const { selected: _, data: data1, ...node1WithoutSelected } = node1;
            const { selected: __, data: data2, ...node2WithoutSelected } = node2;
            const data1WithoutSelection = data1 ? { ...data1, displayOrder: undefined, selectedBy: undefined } : {};
            const data2WithoutSelection = data2 ? { ...data2, displayOrder: undefined, selectedBy: undefined } : {};
            const node1ForCompare = { ...node1WithoutSelected, data: data1WithoutSelection };
            const node2ForCompare = { ...node2WithoutSelected, data: data2WithoutSelection };
            return JSON.stringify(node1ForCompare) === JSON.stringify(node2ForCompare);
          };

          const currentNodesMap = new Map(newNodes.map(n => [n.id, n]));

          // 이전 히스토리의 노드 중 삭제되거나 변경된 노드 저장
          lastHistory.nodes.forEach(prevNode => {
            const currentNode = currentNodesMap.get(prevNode.id);
            if (!currentNode || !compareNodesWithoutSelection(prevNode, currentNode)) {
              previousNodeStates[prevNode.id] = JSON.parse(JSON.stringify(removeSelectionFromNode(prevNode)));
              console.log(`  ✅ previousNodeStates[${prevNode.id}] 저장 (전체 업데이트):`, {
                nodeId: prevNode.id,
                reason: !currentNode ? '삭제됨' : '변경됨'
              });
            }
          });

          // 새로 추가된 노드는 null로 표시
          newNodes.forEach(currentNode => {
            if (!lastHistoryNodesMap.has(currentNode.id)) {
              previousNodeStates[currentNode.id] = null;
              console.log(`  ⚠️ previousNodeStates[${currentNode.id}] 저장 (전체 업데이트, 새 노드 - null):`, {
                nodeId: currentNode.id
              });
            }
          });

          // previousNodeStates가 비어있으면 실제 변경이 없으므로 히스토리 저장 건너뛰기
          if (previousNodeStates && Object.keys(previousNodeStates).length === 0) {
            console.log('📝 히스토리 저장 건너뜀: previousNodeStates가 비어있음 (전체 업데이트, 실제 변경 없음)');
            return;
          }
        }
      } else {
        console.log('📝 previousNodeStates 저장 건너뜀:', {
          savedUpdatedNodeIds,
          reason: 'savedUpdatedNodeIds가 빈 배열'
        });
      }
    }

    // previousNodeStates가 null이면 실제 변경이 없으므로 히스토리 저장 건너뛰기
    if (!previousNodeStates || (previousNodeStates && Object.keys(previousNodeStates).length === 0)) {
      console.log('📝 히스토리 저장 건너뜀: previousNodeStates가 비어있음 (실제 변경 없음)');
      return;
    }

    // 새로운 상태를 항상 끝에 추가 (변경된 노드 ID 목록 및 이전 상태 포함)
    historyRef.current.push({
      nodes: nodesCopy,
      edges: edgesCopy,
      updatedNodeIds: savedUpdatedNodeIds,
      previousNodeStates: previousNodeStates // 변경된 노드의 이전 상태
    });

    // 최대 히스토리 크기 제한
    if (historyRef.current.length > MAX_HISTORY_SIZE) {
      const removedCount = historyRef.current.length - MAX_HISTORY_SIZE;
      historyRef.current = historyRef.current.slice(removedCount); // 가장 오래된 히스토리 제거
      // slice로 제거했으므로 인덱스도 조정 필요
      historyIndexRef.current = historyRef.current.length - 1;
      console.log('🔄 히스토리 크기 제한:', {
        removedCount,
        newLength: historyRef.current.length,
        newIndex: historyIndexRef.current
      });
    } else {
      // 인덱스를 마지막으로 이동
      historyIndexRef.current = historyRef.current.length - 1;
    }

    // UI 업데이트를 위한 state 갱신
    setHistoryIndex(historyIndexRef.current);
    setHistoryLength(historyRef.current.length);

    console.log('📝 히스토리 저장:', {
      index: historyIndexRef.current,
      total: historyRef.current.length,
      nodesCount: nodesToSave.length,
      edgesCount: newEdges.length,
      updatedNodeIds: savedUpdatedNodeIds,
      updatedNodeCount: savedUpdatedNodeIds?.length || 0,
      savedNodes: savedUpdatedNodeIds ? nodesToSave.filter(n => savedUpdatedNodeIds.includes(n.id)).map(n => ({ id: n.id, type: n.type })) : nodesToSave.map(n => ({ id: n.id, type: n.type })),
      previousNodeStates: previousNodeStates ? {
        count: Object.keys(previousNodeStates).length,
        nodeIds: Object.keys(previousNodeStates),
        details: Object.keys(previousNodeStates).map(id => ({
          nodeId: id,
          position: previousNodeStates[id]?.position,
          type: previousNodeStates[id]?.type
        }))
      } : null
    });

    // 히스토리 리스트 출력 (변경된 노드 ID 및 previousNodeStates 포함)
    console.log('📋 히스토리 리스트:', historyRef.current.map((h, idx) => ({
      index: idx,
      nodesCount: h.nodes.length,
      edgesCount: h.edges.length,
      updatedNodeIds: h.updatedNodeIds,
      updatedNodeCount: h.updatedNodeIds?.length || 0,
      hasPreviousNodeStates: !!h.previousNodeStates,
      previousNodeStatesCount: h.previousNodeStates ? Object.keys(h.previousNodeStates).length : 0,
      previousNodeStatesKeys: h.previousNodeStates ? Object.keys(h.previousNodeStates) : null
    })));
  }, []);

  // ⚡ [Yjs] Undo 실행 - Yjs UndoManager 기반 (유저별 독립 Undo)
  const handleUndo = useCallback(() => {
    if (yjsCanUndo) {
      console.log('↩️ [Yjs] Undo 실행');
      yjsUndo();
      return;
    }
    console.log('↩️ [Yjs] Undo: 더 이상 되돌릴 수 없습니다');
  }, [yjsCanUndo, yjsUndo]);

  // ⚡ [Yjs] Redo 실행 - Yjs UndoManager 기반 (유저별 독립 Redo)
  const handleRedo = useCallback(() => {
    if (yjsCanRedo) {
      console.log('↪️ [Yjs] Redo 실행');
      yjsRedo();
      return;
    }
    console.log('↪️ [Yjs] Redo: 더 이상 다시 실행할 수 없습니다');
  }, [yjsCanRedo, yjsRedo]);

  // === 아래는 기존 Undo/Redo 코드 (Yjs 전환 후 비활성화) ===
  // eslint-disable-next-line no-constant-condition
  if (false) {
  // 기존 Undo 실행 (비활성화됨 - Yjs UndoManager가 대체)
  const handleUndoLegacy = (() => {
    if (historyIndexRef.current >= 0) {
      isUndoRedoRef.current = true;

      // Undo 전의 현재 히스토리 항목에서 previousNodeStates 가져오기
      // previousNodeStates에는 작업 이전 상태가 저장되어 있음
      const currentState = historyRef.current[historyIndexRef.current];
      const previousNodeStates = currentState?.previousNodeStates;

      // 인덱스 감소 (이전 상태로 이동)
      historyIndexRef.current--;

      // historyIndexRef.current === -1이면 초기 상태로 복원
      const prevState = historyIndexRef.current >= 0 
        ? historyRef.current[historyIndexRef.current] 
        : initialStateRef.current; // 초기 상태 사용
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;

      // 이전 히스토리에 있는 노드 ID Set 생성 (새로 추가된 노드 판별용)
      const prevStateNodeIds = new Set(prevState?.nodes?.map(n => n.id) || []);

      console.log('↩️ Undo 실행:', {
        currentIndex: historyIndexRef.current + 1, // Undo 전 인덱스
        targetIndex: historyIndexRef.current, // Undo 후 인덱스
        total: historyRef.current.length,
        hasPreviousNodeStates: !!previousNodeStates,
        previousNodeStatesCount: previousNodeStates ? Object.keys(previousNodeStates).length : 0,
        previousNodeStatesKeys: previousNodeStates ? Object.keys(previousNodeStates) : null,
        prevStateNodeCount: prevState?.nodes?.length || 0
      });

      // previousNodeStates가 있으면 저장된 노드 ID만 찾아서 이전 상태로 복원
      if (previousNodeStates && Object.keys(previousNodeStates).length > 0) {
        const savedNodeIds = Object.keys(previousNodeStates); // 저장된 노드 ID 목록

        console.log('✅ 저장된 노드 ID만 복원:', {
          savedNodeIds,
          currentNodesCount: currentNodes.length,
          prevStateNodeCount: prevStateNodeIds.size
        });

        // setNodes를 함수형 업데이트로 사용하여 저장된 노드만 업데이트
        // 다른 노드는 전혀 건드리지 않음 (참조 유지)
        setNodes((nds) => {
          const nodesToAdd = []; // 삭제된 노드 복원용
          const deletedNodeIds = []; // 새로 추가된 노드 삭제 추적

          // 현재 노드들 중 저장된 노드 ID를 찾아서 이전 상태로 복원
          const updatedNodes = nds
            .filter(node => {
              // 새로 추가된 노드 삭제: previousNodeStates에 null로 표시된 노드
              if (savedNodeIds.includes(node.id)) {
                const prevNode = previousNodeStates[node.id];
                if (prevNode === null) {
                  // 새로 추가된 노드 (null로 표시됨) - 삭제
                  deletedNodeIds.push(node.id);
                  console.log(`  🗑️ 노드 ${node.id} 삭제 (새로 추가된 노드):`, {
                    position: node.position,
                    type: node.type
                  });
                  return false; // 새로 추가된 노드는 필터링하여 삭제
                }
              }
              return true; // 나머지는 유지
            })
            .map(node => {
              // 저장된 노드 ID에 있으면 이전 상태로 복원
              if (savedNodeIds.includes(node.id)) {
                const prevNode = previousNodeStates[node.id];
                if (prevNode === null) {
                  // 새로 추가된 노드 (null로 표시됨) - 삭제하지 않고 필터링 단계에서 처리
                  return node; // 여기서는 유지, 필터링 단계에서 삭제됨
                } else if (prevNode) {
                  console.log(`  ✅ 노드 ${node.id} 복원 (작업 이전 상태로):`, {
                    from: { position: node.position, type: node.type },
                    to: { position: prevNode.position, type: prevNode.type }
                  });
                  // selected, selectedBy, showUserLabel 속성은 현재 상태 유지
                  return { 
                    ...prevNode, 
                    selected: node.selected,
                    data: {
                      ...prevNode.data,
                      selectedBy: node.data?.selectedBy,
                      showUserLabel: node.data?.showUserLabel
                    }
                  };
                }
              }
              // 저장되지 않은 노드는 그대로 유지 (참조 유지로 리렌더링 방지)
              return node;
            });

          // 삭제된 노드 복원 (이전 상태에 있지만 현재 노드에 없는 노드)
          savedNodeIds.forEach(nodeId => {
            const prevNode = previousNodeStates[nodeId];
            // 이전 히스토리에 있었던 노드만 복원 (새로 추가된 노드는 null이므로 제외)
            if (prevNode !== null && prevNode && !nds.find(n => n.id === nodeId) && prevStateNodeIds.has(nodeId)) {
              // 복원 시 selectedBy, showUserLabel 제거 (선택 상태 아님)
              const restoredNode = {
                ...prevNode,
                selected: false,
                data: {
                  ...prevNode.data,
                  selectedBy: undefined,
                  showUserLabel: undefined
                }
              };
              nodesToAdd.push(restoredNode);
              console.log(`  ➕ 노드 ${nodeId} 복원 (삭제된 노드):`, {
                position: prevNode.position,
                type: prevNode.type
              });
            }
          });

          // 삭제된 노드가 있으면 추가
          const finalNodes = nodesToAdd.length > 0
            ? [...updatedNodes, ...nodesToAdd]
            : updatedNodes;

          // ref 업데이트
          nodesRef.current = finalNodes;

          // 복원된 노드 ID 목록
          console.log('↩️ 변경된 노드만 복원 완료:', {
            restoredNodeIds: savedNodeIds,
            restoredCount: savedNodeIds.length,
            deletedNodeIds,
            deletedCount: deletedNodeIds.length,
            currentNodesCount: currentNodes.length,
            finalNodesCount: finalNodes.length
          });

          // 노드 선택 해제 및 색상 박스 닫기
          setShowNodeColorPicker(false);
          setSelectedNodeId(null);
          setSelectedEdgeId(null);

          // 서버에 전송 (히스토리 저장 안 함 - 이미 히스토리에서 가져온 상태이므로)
          // 삭제된 노드가 있으면 deletedNodeIds와 함께 부분 업데이트 전송 (다른 사용자 작업 보존)
          // setNodes 완료 후 약간의 지연을 두어 상태가 완전히 업데이트된 후 전송
          setTimeout(() => {
            // 삭제된 노드 ID를 제외한 업데이트할 노드 ID 목록
            const updatedNodeIdsToSend = savedNodeIds.filter(id => !deletedNodeIds.includes(id));

            // 안전 장치: 삭제할 노드가 3개 이상이거나 전체의 30% 이상이면 삭제하지 않음
            const safeDeletedNodeIds = (deletedNodeIds.length <= 2 && deletedNodeIds.length < finalNodes.length * 0.3)
              ? deletedNodeIds
              : [];

            if (safeDeletedNodeIds.length !== deletedNodeIds.length) {
              console.warn('⚠️ [안전 장치] 삭제할 노드가 너무 많아 삭제 무시:', {
                originalCount: deletedNodeIds.length,
                finalNodesCount: finalNodes.length,
                deletedNodeIds
              });
            }

            if (safeDeletedNodeIds.length > 0) {
              // 삭제된 노드가 있으면 deletedNodeIds와 복원된 노드를 함께 전송
              // 전체 노드 목록 대신 부분 업데이트 + 삭제 ID 전송 (다른 사용자 작업 보존)
              broadcast(finalNodes, true, updatedNodeIdsToSend, false, safeDeletedNodeIds);
              console.log('📤 Undo 변경사항 웹소켓 전송 (부분 업데이트 + 노드 삭제):', {
                nodeCount: finalNodes.length,
                updatedNodeIds: updatedNodeIdsToSend,
                deletedNodeIds: safeDeletedNodeIds,
                deletedCount: safeDeletedNodeIds.length
              });
            } else {
              // 삭제된 노드가 없으면 변경된 노드만 전송
              broadcast(finalNodes, true, updatedNodeIdsToSend.length > 0 ? updatedNodeIdsToSend : savedNodeIds, false);
              console.log('📤 Undo 변경사항 웹소켓 전송:', {
                nodeCount: finalNodes.length,
                restoredNodeIds: updatedNodeIdsToSend.length > 0 ? updatedNodeIdsToSend : savedNodeIds
              });
            }
          }, 0);

          return finalNodes;
        });

        // UI 업데이트를 위한 state 갱신
        setHistoryIndex(historyIndexRef.current);
        setHistoryLength(historyRef.current.length);

        // 약간의 지연 후 플래그 해제 (웹소켓 전송이 완료된 후)
        setTimeout(() => {
          isUndoRedoRef.current = false;
        }, 150); // 웹소켓 전송(setTimeout 0ms) 후 플래그 해제
      } else {
        // previousNodeStates가 없으면 Undo 불가 (하위 호환성)
        console.log('⚠️ previousNodeStates가 없어서 Undo 건너뜀 (상대 작업 보존)');
        // 인덱스 되돌리기
        historyIndexRef.current++;
        isUndoRedoRef.current = false;
      }
    } else {
      console.log('↩️ Undo: 더 이상 되돌릴 수 없습니다 (historyIndex:', historyIndexRef.current, ')');
    }
  }, [setNodes, broadcast, getNodes]);

  // 기존 Redo 실행 (비활성화됨 - Yjs UndoManager가 대체)
  const handleRedoLegacy = (() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      isUndoRedoRef.current = true;

      historyIndexRef.current++;

      const nextState = historyRef.current[historyIndexRef.current];
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;

      console.log('↪️ Redo 실행:', {
        index: historyIndexRef.current,
        total: historyRef.current.length,
        updatedNodeIds: nextState.updatedNodeIds,
        updatedNodeCount: nextState.updatedNodeIds?.length || 0
      });

      // 변경된 노드만 앞으로 가기 (다른 노드는 전혀 건드리지 않음)
      let restoredNodes = currentNodes;
      let restoredEdges = currentEdges;
      let needFullUpdate = false; // 전체 업데이트가 필요한지 여부

      if (nextState.updatedNodeIds && nextState.updatedNodeIds.length > 0) {
        // 변경된 노드만 개별적으로 업데이트
        const nextNodesMap = new Map(nextState.nodes.map(n => [n.id, n]));
        const updatedNodeIdsSet = new Set(nextState.updatedNodeIds);

        console.log('↪️ 변경된 노드만 복원:', {
          updatedNodeIds: nextState.updatedNodeIds,
          restoredCount: nextState.updatedNodeIds.length,
          currentNodesCount: currentNodes.length
        });

        // setNodes를 함수형 업데이트로 사용하여 변경된 노드만 업데이트
        // 변경되지 않은 노드는 참조를 유지하여 리렌더링 방지
        setNodes((nds) => {
          const nodesToAdd = []; // 새로 추가할 노드 (삭제된 노드 복원)

          const updatedNodes = nds.map(node => {
            // 변경된 노드만 업데이트
            if (updatedNodeIdsSet.has(node.id)) {
              const nextNode = nextNodesMap.get(node.id);
              if (nextNode) {
                console.log(`  ✅ 노드 ${node.id} 업데이트:`, {
                  from: { position: node.position, type: node.type },
                  to: { position: nextNode.position, type: nextNode.type }
                });
                // selected, selectedBy, showUserLabel 속성은 현재 상태 유지
                return { 
                  ...nextNode, 
                  selected: node.selected,
                  data: {
                    ...nextNode.data,
                    selectedBy: node.data?.selectedBy,
                    showUserLabel: node.data?.showUserLabel
                  }
                };
              }
            }
            // 변경되지 않은 노드는 참조 유지 (리렌더링 방지)
            return node;
          });

          // 삭제된 노드 추가 (다음 상태에 있지만 현재 노드에 없는 노드)
          nextState.updatedNodeIds.forEach(nodeId => {
            const nextNode = nextNodesMap.get(nodeId);
            if (nextNode && !nds.find(n => n.id === nodeId)) {
              // 복원 시 selectedBy, showUserLabel 제거 (선택 상태 아님)
              const restoredNode = {
                ...nextNode,
                selected: false,
                data: {
                  ...nextNode.data,
                  selectedBy: undefined,
                  showUserLabel: undefined
                }
              };
              nodesToAdd.push(restoredNode);
              console.log(`  ➕ 노드 ${nodeId} 복원 (삭제된 노드):`, {
                position: nextNode.position,
                type: nextNode.type
              });
            }
          });

          // 삭제된 노드가 있으면 추가
          const finalNodes = nodesToAdd.length > 0
            ? [...updatedNodes, ...nodesToAdd]
            : updatedNodes;

          // ref 업데이트
          nodesRef.current = finalNodes;

          // 노드 선택 해제 및 색상 박스 닫기
          setShowNodeColorPicker(false);
          setSelectedNodeId(null);
          setSelectedEdgeId(null);

          // 서버에 전송 (히스토리 저장 안 함 - 이미 히스토리에서 가져온 상태이므로)
          // 변경된 노드만 전송
          // setNodes 완료 후 약간의 지연을 두어 상태가 완전히 업데이트된 후 전송
          setTimeout(() => {
            // Undo/Redo 중이 아니면 전송하지 않음 (다른 작업으로 인한 변경일 수 있음)
            if (!isUndoRedoRef.current) {
              console.log('⚠️ Undo/Redo 플래그가 false이므로 웹소켓 전송 건너뜀');
              return;
            }

            // finalNodes가 유효한지 확인
            if (!finalNodes || !Array.isArray(finalNodes)) {
              console.error('⚠️ finalNodes가 유효하지 않아 웹소켓 전송 건너뜀:', finalNodes);
              return;
            }

            broadcast(finalNodes, true, nextState.updatedNodeIds, false);
            console.log('📤 Redo 변경사항 웹소켓 전송:', {
              nodeCount: finalNodes.length,
              restoredNodeIds: nextState.updatedNodeIds
            });
          }, 0);

          return finalNodes;
        });
      } else if (nextState.updatedNodeIds === null) {
        // null이면 전체 노드 업데이트 (노드 삭제 등)이므로 전체 복원
        needFullUpdate = true;
        // 현재 노드의 selectedBy, showUserLabel 상태 유지
        const currentNodesMap = new Map(currentNodes.map(n => [n.id, n]));
        restoredNodes = nextState.nodes.map(node => {
          const currentNode = currentNodesMap.get(node.id);
          if (currentNode) {
            return {
              ...node,
              selected: currentNode.selected,
              data: {
                ...node.data,
                selectedBy: currentNode.data?.selectedBy,
                showUserLabel: currentNode.data?.showUserLabel
              }
            };
          }
          // 새로 추가된 노드는 selectedBy, showUserLabel 제거
          return {
            ...node,
            selected: false,
            data: {
              ...node.data,
              selectedBy: undefined,
              showUserLabel: undefined
            }
          };
        });
        restoredEdges = nextState.edges;
        console.log('↪️ 전체 노드 복원 (updatedNodeIds가 null)');
      } else {
        // 빈 배열이면 변경 없음이므로 현재 상태 유지
        console.log('↪️ 변경 없음 (updatedNodeIds가 빈 배열)');
      }

      // 전체 업데이트가 필요한 경우에만 setNodes 사용
      if (needFullUpdate) {
        setNodes(restoredNodes);
        nodesRef.current = restoredNodes;
        setEdges(restoredEdges);
        edgesRef.current = restoredEdges;

        // 노드 선택 해제 및 색상 박스 닫기
        setShowNodeColorPicker(false);
        setSelectedNodeId(null);
        setSelectedEdgeId(null);

        // 서버에 전송 (히스토리 저장 안 함 - 이미 히스토리에서 가져온 상태이므로)
        // 전체 업데이트이므로 null 전송
        setTimeout(() => {
          // Undo/Redo 중이 아니면 전송하지 않음 (다른 작업으로 인한 변경일 수 있음)
          if (!isUndoRedoRef.current) {
            console.log('⚠️ Undo/Redo 플래그가 false이므로 웹소켓 전송 건너뜀');
            return;
          }

          // restoredNodes가 유효한지 확인
          if (!restoredNodes || !Array.isArray(restoredNodes)) {
            console.error('⚠️ restoredNodes가 유효하지 않아 웹소켓 전송 건너뜀:', restoredNodes);
            return;
          }

          broadcast(restoredNodes, true, null, false);
          console.log('📤 Redo 변경사항 웹소켓 전송 (전체 업데이트):', {
            nodeCount: restoredNodes.length
          });
        }, 0);
      } else if (restoredEdges !== currentEdges) {
        // Edge만 변경된 경우
        setEdges(restoredEdges);
        edgesRef.current = restoredEdges;
      }

      // UI 업데이트를 위한 state 갱신
      setHistoryIndex(historyIndexRef.current);
      setHistoryLength(historyRef.current.length);

      // 약간의 지연 후 플래그 해제 (웹소켓 전송이 완료된 후)
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 150); // 웹소켓 전송(setTimeout 0ms) 후 플래그 해제
    } else {
      console.log('↪️ Redo: 더 이상 다시 실행할 수 없습니다');
    }
  }, [setNodes, setEdges, broadcast, getNodes]);
  } // end of if(false) - 기존 Undo/Redo 비활성화 블록 끝

  // saveToHistory ref 업데이트
  useEffect(() => {
    saveToHistoryRef.current = saveToHistory;
  }, [saveToHistory]);

  // 노드/엣지 변경 시 히스토리 저장은 broadcast 함수에서 처리
  // (웹소켓 전송할 때 같이 저장)

  // 스타일 코드로 데이터 찾아서 카드 업데이트
  const handleUpdateCardData = useCallback(async (nodeId, partCode) => {
    // 현재 노드의 mapType 확인
    const currentNode = nodesRef.current.find(n => n.id === nodeId);
    const mapType = currentNode?.data?.mapType;

    // 품번이 비어있거나 없으면 데이터 초기화
    if (!partCode || partCode.trim() === '') {
      const emptyData = {
        part_cd: '',
        sesn: '',
        brd_cd: '',
        item: '',
        fabric: '',
        fiber_content: '',
        domain: '',
        set_up_style_no: '',
        life_style: '',
        level: '',
        description: '',
        fit: '',
        price: undefined,
        colors: [],
        designer: '',
        vendor: '',
        image: '',
        // 판매맵 데이터 초기화
        tag_price: undefined,
        unit_cost: undefined,
        ac_ord_qty_kor: undefined,
        ac_sale_qty_cns: undefined,
        cns_delv_dt_1st: '',
        style_sale_rate: undefined,
        w1_cns: undefined,
        w2_cns: undefined,
        w3_cns: undefined,
        salesData: []
      };

      // 노드 데이터 초기화
      setNodes((nds) => {
        const updatedNodes = nds.map(node => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                ...emptyData
              }
            };
          }
          return node;
        });
        broadcast(updatedNodes, true, [nodeId], true);
        return updatedNodes;
      });
      return;
    }

    // 판매맵인 경우 API 호출
    if (mapType === '판매맵') {
      // 로딩 상태 시작
      setNodes((nds) => {
        return nds.map(node => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                part_cd: partCode,
                isLoading: true
              }
            };
          }
          return node;
        });
      });

      try {
        console.log('📊 판매맵 데이터 조회 시작:', partCode);
        const response = await fetch('/api/matrix-board/sales-map-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partCd: partCode.toUpperCase(), brdCd: boardBrdCd, sesn: boardSesn })
        });

        if (!response.ok) {
          throw new Error('판매맵 데이터 조회 실패');
        }

        const result = await response.json();

        if (result.data && result.data.length > 0) {
          // 첫 번째 데이터에서 스타일 레벨 정보 추출
          const firstRow = result.data[0];
          const updatedData = {
            part_cd: firstRow.PART_CD || partCode,
            description: firstRow.PRDT_NM || '',
            image: firstRow.IMG || '',
            tag_price: firstRow.TAG_PRICE,
            unit_cost: firstRow.UNIT_COST,
            ac_ord_qty_kor: result.data.reduce((sum, r) => sum + (r.AC_ORD_QTY_KOR || 0), 0),
            ac_sale_qty_cns: result.data.reduce((sum, r) => sum + (r.AC_SALE_QTY_CNS || 0), 0),
            cns_delv_dt_1st: firstRow.CNS_DELV_DT_1ST,
            style_sale_rate: firstRow.STYLE_SALE_RATE,
            w1_cns: result.data.reduce((sum, r) => sum + (r.W1_CNS || 0), 0),
            w2_cns: result.data.reduce((sum, r) => sum + (r.W2_CNS || 0), 0),
            w3_cns: result.data.reduce((sum, r) => sum + (r.W3_CNS || 0), 0),
            salesData: result.data.map(row => ({
              color_cd: row.COLOR_CD,
              ac_ord_qty_kor: row.AC_ORD_QTY_KOR,
              ac_stor_qty_kor: row.AC_STOR_QTY_KOR,
              ac_sale_qty_cns: row.AC_SALE_QTY_CNS,
              stock_qty: row.STOCK_QTY,
              sale_rate: row.SALE_RATE,
              image: row.IMG || row.img || ''
            })),
            mapType: '판매맵',
            lastFetchedDate: result.endDate,
            isLoading: false
          };

          setNodes((nds) => {
            const updatedNodes = nds.map(node => {
              if (node.id === nodeId) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    ...updatedData
                  }
                };
              }
              return node;
            });
            broadcast(updatedNodes, true, [nodeId], true);
            return updatedNodes;
          });
        } else {
          // 데이터 없으면 모든 데이터 초기화 및 로딩 종료
          setNodes((nds) => {
            const updatedNodes = nds.map(node => {
              if (node.id === nodeId) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    part_cd: '',
                    description: '',
                    image: '',
                    tag_price: undefined,
                    unit_cost: undefined,
                    ac_ord_qty_kor: undefined,
                    ac_sale_qty_cns: undefined,
                    cns_delv_dt_1st: '',
                    style_sale_rate: undefined,
                    w1_cns: undefined,
                    w2_cns: undefined,
                    w3_cns: undefined,
                    salesData: [],
                    isLoading: false
                  }
                };
              }
              return node;
            });
            broadcast(updatedNodes, true, [nodeId], true);
            return updatedNodes;
          });
          setTimeout(() => alert(`${partCode} 스타일의 판매 데이터를 찾을 수 없습니다.`), 0);
        }
      } catch (error) {
        console.error('❌ 판매맵 데이터 조회 오류:', error);
        setTimeout(() => alert('판매맵 데이터 조회에 실패했습니다.'), 0);
        // 에러 발생 시 로딩 종료
        setNodes((nds) => {
          return nds.map(node => {
            if (node.id === nodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  isLoading: false
                }
              };
            }
            return node;
          });
        });
      }
      return;
    }

    // X 브랜드 캐드맵: API로 단일 카드 조회
    if (boardBrdCd === 'X' && (mapType === '캐드맵' || mapType === 'CAD맵')) {
      setNodes((nds) => nds.map(node =>
        node.id === nodeId ? { ...node, data: { ...node.data, part_cd: partCode, isLoading: true } } : node
      ));
      try {
        const response = await fetch('/api/matrix-board/dx-cad-card', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partCd: partCode, brdCd: boardBrdCd })
        });
        if (!response.ok) throw new Error('DX 캐드 카드 조회 실패');
        const result = await response.json();
        if (result.data) {
          const d = result.data;
          const updatedData = {
            part_cd: d.part_cd || '',
            sesn: d.sesn || '',
            brd_cd: d.brd_cd || '',
            item: d.item || '',
            fabric: d.fabric || '',
            fiber_content: '',
            domain: d.domain || '-',
            set_up_style_no: d.set_up_style_no || '',
            life_style: d.life_style || '',
            level: d.level || '-',
            description: d.description || '',
            fit: d.fit || '',
            price: d.tag,
            colors: d.color ? d.color.split(',') : [],
            designer: d.designer || '',
            vendor: d.vendor || '',
            image: d.img || '',
            isLoading: false
          };
          setNodes((nds) => {
            const updatedNodes = nds.map(node =>
              node.id === nodeId ? { ...node, data: { ...node.data, ...updatedData } } : node
            );
            broadcast(updatedNodes, true, [nodeId], true);
            return updatedNodes;
          });
        } else {
          setNodes((nds) => {
            const updatedNodes = nds.map(node =>
              node.id === nodeId ? { ...node, data: { ...node.data, part_cd: '', isLoading: false } } : node
            );
            broadcast(updatedNodes, true, [nodeId], true);
            return updatedNodes;
          });
          setTimeout(() => alert(`${partCode} 품번을 찾을 수 없습니다.`), 0);
        }
      } catch (error) {
        console.error('❌ DX 캐드 카드 조회 오류:', error);
        setNodes((nds) => nds.map(node =>
          node.id === nodeId ? { ...node, data: { ...node.data, isLoading: false } } : node
        ));
        setTimeout(() => alert('캐드 데이터 조회에 실패했습니다.'), 0);
      }
      return;
    }

    // 기존 로직: cadData에서 스타일 코드로 데이터 찾기 (대소문자 구분 없이)
    const foundData = cadData.find(item =>
      item.part_cd && item.part_cd.toLowerCase() === partCode.toLowerCase()
    );

    if (foundData) {
      // 데이터 매핑 (M 브랜드 / DX 브랜드 필드명 차이 처리)
      const colorStr = foundData.color || foundData.color_list || '';
      const updatedData = {
        part_cd: foundData.part_cd || '',
        sesn: foundData.sesn || '',
        brd_cd: foundData.brd_cd || '',
        item: foundData.item || '',
        fabric: foundData.fabric || foundData.fab_info || '',
        fiber_content: foundData.fiber_content || '',
        domain: foundData.domain || '-',
        set_up_style_no: foundData.set_up_style_no || '',
        life_style: foundData.life_style || foundData.lifestyle_nm || '',
        level: foundData.level || '-',
        description: foundData.description || foundData.prdt_nm || '',
        fit: foundData.fit || foundData.fit_info || '',
        price: foundData.tag === 0 ? foundData.pre_tag : (foundData.pre_tag !== undefined ? foundData.pre_tag : (foundData.tag || foundData.tag_price)),
        colors: colorStr ? colorStr.split(',') : [],
        designer: foundData.designer || foundData.dsgnr_nm || '',
        vendor: foundData.vendor || foundData.mfac_compy_nm || '',
        image: foundData.img || ''
      };

      // 노드 데이터 업데이트
      setNodes((nds) => {
        const updatedNodes = nds.map(node => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                ...updatedData
              }
            };
          }
          return node;
        });
        broadcast(updatedNodes, true, [nodeId], true);
        return updatedNodes;
      });
    } else {
      console.warn(`${partCode} 품번을 찾을 수 없습니다.`);
      // 품번 박스 먼저 비우기
      setNodes((nds) => {
        const updatedNodes = nds.map(node => {
          if (node.id === nodeId) {
            return { ...node, data: { ...node.data, part_cd: '', sesn: '', brd_cd: '', item: '', fabric: '', fiber_content: '', domain: '', set_up_style_no: '', life_style: '', level: '', description: '', fit: '', price: undefined, colors: [], designer: '', vendor: '', image: '' } };
          }
          return node;
        });
        broadcast(updatedNodes, true, [nodeId], true);
        return updatedNodes;
      });
      // setTimeout으로 alert를 지연시켜 mouseup 이벤트가 먼저 처리되게 함 (드래그 상태 방지)
      setTimeout(() => alert(`${partCode} 품번을 찾을 수 없습니다.`), 0);
    }
  }, [cadData, setNodes, broadcast, user, boardBrdCd]);


  // auto_update 보드: 시트 진입 + 노드 로드 완료 시 캐드맵/판매맵 노드 모두 갱신
  // canvases를 dependency에 포함해 첫 시트 노드 로드 후에도 동작
  useEffect(() => {
    if (!boardAutoUpdate) return;
    if (cadAutoRefreshedRef.current.has(activeCanvasId)) return;

    const canvas = canvases.find(c => c.id === activeCanvasId);

    const allNodes = canvas.nodes || [];

    // 판매맵 보드는 기존 기준일자 로직으로 처리
    if (boardMapType === '판매맵' || boardMapType === 'S') return;

    // cadData 로드 완료 후 실행
    if (cadData.length === 0) return;

    const targetNodes = allNodes.filter(n => n.data?.part_cd);


    cadAutoRefreshedRef.current.add(activeCanvasId);
    targetNodes.forEach((node, idx) => {
      setTimeout(() => {
        handleUpdateCardData(node.id, node.data.part_cd);
      }, idx * 600);
    });
  }, [activeCanvasId, boardAutoUpdate, canvases, cadData]); // eslint-disable-line react-hooks/exhaustive-deps

  // 레인지맵 필드 직접 업데이트 핸들러
  const handleUpdateRangeMapField = useCallback((nodeId, fieldName, value) => {
    console.log('📝 레인지맵 필드 업데이트:', { nodeId, fieldName, value });
    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              [fieldName]: value
            }
          };
        }
        return node;
      });
      broadcast(updatedNodes, true, [nodeId], true);
      return updatedNodes;
    });
  }, [setNodes, broadcast]);

  // 텍스트박스 업데이트 핸들러 (편집 종료 시 호출)
  const handleUpdateTextBox = useCallback((nodeId, newText) => {
    console.log('📝 텍스트박스 업데이트 (편집 종료):', { nodeId, textLength: newText?.length });
    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, text: newText }
          };
        }
        return node;
      });
      nodesRef.current = updatedNodes;
      // ⚡ 최종 텍스트를 'local' origin으로 전송 (UndoManager가 추적, 이전 텍스트 변경과 자동 병합)
      syncNodes(updatedNodes, {
        updatedNodeIds: [nodeId],
        triggerThumbnail: true,
        trackHistory: true,
      });
      return updatedNodes;
    });
    // ⚡ commitUndoCapture는 handleUpdateEditingState(false)에서 호출
    // (onUpdateText → onUpdateEditingState(false) 순서로 호출됨)
  }, [setNodes, syncNodes]);

  // ⚡ 텍스트박스 실시간 텍스트 동기화 핸들러 (타이핑 중)
  // trackHistory=true: 'local' origin → UndoManager가 추적
  // captureTimeout(500ms)으로 연속 타이핑이 하나의 undo 스텝으로 자동 병합됨
  const handleTextChangeLive = useCallback((nodeId, newText) => {
    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, text: newText }
          };
        }
        return node;
      });
      nodesRef.current = updatedNodes;
      // ⚡ 'local' origin으로 실시간 전송 (UndoManager가 추적, captureTimeout으로 자동 병합)
      syncNodes(updatedNodes, {
        updatedNodeIds: [nodeId],
        triggerThumbnail: false,
        trackHistory: true,
      });
      return updatedNodes;
    });
  }, [setNodes, syncNodes]);

  // 텍스트박스 편집 상태 업데이트 핸들러
  const handleUpdateEditingState = useCallback((nodeId, isEditing) => {
    const currentUser = getUserNameFromMsal(); // 현재 사용자 정보
    console.log('📝 텍스트박스 편집 상태 변경:', { nodeId, isEditing, user: currentUser });

    // ⚡ 편집 시작 시: stopCapturing()으로 이전 작업과 undo 스텝 분리
    if (isEditing) {
      beginUndoCapture([nodeId]);
    }

    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              isEditing,
              editingBy: isEditing ? currentUser : null // 편집 중인 사용자 정보 추가
            }
          };
        }
        return node;
      });
      nodesRef.current = updatedNodes;
      // ⚡ 편집 상태도 'local' origin으로 전송 (UndoManager가 추적)
      // 편집 시작/종료가 텍스트 변경과 같은 undo 스텝으로 병합됨
      syncNodes(updatedNodes, {
        updatedNodeIds: [nodeId],
        triggerThumbnail: false,
        trackHistory: true,
      });
      return updatedNodes;
    });

    // ⚡ 편집 종료 시: stopCapturing()으로 다음 작업과 undo 스텝 분리
    if (!isEditing) {
      commitUndoCapture();
    }
  }, [setNodes, syncNodes, beginUndoCapture, commitUndoCapture]);

  // StyleCard 편집 상태 업데이트 핸들러
  const handleUpdateStyleCardEditingState = useCallback((nodeId, isEditing) => {
    const currentUser = getUserNameFromMsal();
    console.log('📝 StyleCard 편집 상태 변경:', { nodeId, isEditing, user: currentUser });

    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              isEditing,
              editingBy: isEditing ? currentUser : null
            }
          };
        }
        return node;
      });
      nodesRef.current = updatedNodes;
      // 편집 상태를 다른 사용자에게 즉시 전송
      broadcast(updatedNodes, true, [nodeId], true);
      return updatedNodes;
    });
  }, [setNodes, broadcast]);

  // 텍스트박스 텍스트 방향 업데이트 핸들러
  const handleUpdateTextDirection = useCallback((nodeId, textDirection) => {
    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, textDirection }
          };
        }
        return node;
      });
      nodesRef.current = updatedNodes;
      broadcast(updatedNodes, true, [nodeId], true);
      return updatedNodes;
    });
  }, [setNodes, broadcast]);

  // 점이 사각형 내부에 있는지 검사
  const isPointInRect = useCallback((point, rect) => {
    return point.x >= rect.x1 && point.x <= rect.x2 &&
      point.y >= rect.y1 && point.y <= rect.y2;
  }, []);

  // 선분과 사각형의 교차 검사
  const lineSegmentIntersectsRect = useCallback((p1, p2, rect) => {
    // 선분의 끝점이 사각형 내부에 있는지 검사
    if (isPointInRect(p1, rect) || isPointInRect(p2, rect)) {
      return true;
    }

    // 사각형의 네 변과 선분의 교차 검사
    const rectEdges = [
      [{ x: rect.x1, y: rect.y1 }, { x: rect.x2, y: rect.y1 }], // 상단
      [{ x: rect.x2, y: rect.y1 }, { x: rect.x2, y: rect.y2 }], // 우측
      [{ x: rect.x2, y: rect.y2 }, { x: rect.x1, y: rect.y2 }], // 하단
      [{ x: rect.x1, y: rect.y2 }, { x: rect.x1, y: rect.y1 }]  // 좌측
    ];

    // 두 선분의 교차 검사
    const segmentsIntersect = (a1, a2, b1, b2) => {
      const det = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
      if (Math.abs(det) < 1e-10) return false; // 평행

      const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / det;
      const u = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / det;

      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };

    return rectEdges.some(edge => segmentsIntersect(p1, p2, edge[0], edge[1]));
  }, [isPointInRect]);

  // Line 노드의 중심선과 선택 박스의 교차 검사 (정교화된 버전)
  const checkLineIntersection = useCallback((selBox, nodeX, nodeY, nodeWidth, nodeHeight, rotation, centerOffsetX, centerOffsetY) => {
    // ⭐ 선의 실제 중심점 (centerOffset 사용)
    const centerX = nodeX + (centerOffsetX ?? nodeWidth / 2);
    const centerY = nodeY + (centerOffsetY ?? nodeHeight / 2);

    // 회전 각도를 라디안으로 변환
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    // Line의 시작점과 끝점 (회전 적용)
    const halfWidth = nodeWidth / 2;
    const startPoint = {
      x: centerX - halfWidth * cos,
      y: centerY - halfWidth * sin
    };
    const endPoint = {
      x: centerX + halfWidth * cos,
      y: centerY + halfWidth * sin
    };

    console.log('🔍 checkLineIntersection (박스 선택):', {
      nodeX,
      nodeY,
      nodeWidth,
      nodeHeight,
      rotation,
      center: { x: centerX, y: centerY },
      leftResizer: startPoint,
      rightResizer: endPoint,
      selBox
    });

    // 1. 선분과 사각형의 교차 검사
    if (lineSegmentIntersectsRect(startPoint, endPoint, selBox)) {
      return true;
    }

    // 2. Line이 선택 박스를 완전히 관통하는 경우 검사
    // (선택 박스의 대각선과 Line의 교차)
    const diagonal1Start = { x: selBox.x1, y: selBox.y1 };
    const diagonal1End = { x: selBox.x2, y: selBox.y2 };
    const diagonal2Start = { x: selBox.x2, y: selBox.y1 };
    const diagonal2End = { x: selBox.x1, y: selBox.y2 };

    const segmentsIntersect = (a1, a2, b1, b2) => {
      const det = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
      if (Math.abs(det) < 1e-10) return false;

      const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / det;
      const u = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / det;

      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };

    if (segmentsIntersect(startPoint, endPoint, diagonal1Start, diagonal1End) ||
      segmentsIntersect(startPoint, endPoint, diagonal2Start, diagonal2End)) {
      return true;
    }

    // 3. 선택 박스가 Line을 완전히 포함하는 경우
    if (isPointInRect(startPoint, selBox) && isPointInRect(endPoint, selBox)) {
      return true;
    }

    // 4. Line의 두께를 고려한 추가 검사 (선택 박스와의 최소 거리)
    // Line은 매우 얇지만 (height=10px), 선택하기 쉽도록 약간의 여유 추가
    const tolerance = 5; // 5px 여유

    // 선택 박스를 tolerance만큼 확장
    const expandedBox = {
      x1: selBox.x1 - tolerance,
      y1: selBox.y1 - tolerance,
      x2: selBox.x2 + tolerance,
      y2: selBox.y2 + tolerance
    };

    return lineSegmentIntersectsRect(startPoint, endPoint, expandedBox);
  }, [isPointInRect, lineSegmentIntersectsRect]);


  // 커스텀 박스 선택 처리
  useEffect(() => {
    if (!boxSelectionMode || !isBoxSelecting) return;

    const handleMouseMove = (e) => {
      if (selectionBox) {
        const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        setSelectionBox(prev => ({
          ...prev,
          x2: position.x,
          y2: position.y
        }));
      }
    };

    const handleMouseUp = () => {
      if (selectionBox) {
        // 선택 완료 - 박스 안의 노드들 선택
        const normalizedBox = {
          x1: Math.min(selectionBox.x1, selectionBox.x2),
          y1: Math.min(selectionBox.y1, selectionBox.y2),
          x2: Math.max(selectionBox.x1, selectionBox.x2),
          y2: Math.max(selectionBox.y1, selectionBox.y2)
        };

        console.log('🔍 박스 선택 완료:', normalizedBox);

        // 모든 노드 검사
        const allNodes = nodesRef.current;
        const selectedNodeIds = [];

        allNodes.forEach(node => {
          let isInBox = false;

          if (node.type === 'line') {
            // Line 노드: 리사이저 위치 기준 바운딩 박스 검사
            const rotation = node.data?.rotation || 0;
            const nodeX = node.position.x;
            const nodeY = node.position.y;
            const nodeWidth = node.width || node.data?.width || 200;
            const nodeHeight = node.height || 10;

            // ⭐ 선의 실제 중심점 계산 (centerOffset 사용)
            const centerOffsetX = node.data?.centerOffsetX ?? nodeWidth / 2;
            const centerOffsetY = node.data?.centerOffsetY ?? nodeHeight / 2;
            const centerX = nodeX + centerOffsetX;
            const centerY = nodeY + centerOffsetY;

            // 회전 각도를 라디안으로 변환
            const rad = (rotation * Math.PI) / 180;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);

            // ⭐ 왼쪽/오른쪽 리사이저의 실제 위치 계산
            // node.data.width는 선의 실제 길이 (nodeWidth는 바운딩 박스)
            const lineLength = node.data?.width || nodeWidth;
            const halfLength = lineLength / 2;
            const leftResizerX = centerX - halfLength * cos;
            const leftResizerY = centerY - halfLength * sin;
            const rightResizerX = centerX + halfLength * cos;
            const rightResizerY = centerY + halfLength * sin;

            // 리사이저 위치로 바운딩 박스 계산
            // 작은 x 위치
            const minX = Math.min(leftResizerX, rightResizerX);
            // 작은 y 위치
            const minY = Math.min(leftResizerY, rightResizerY);
            // 두 x 거리
            const bboxWidth = Math.abs(rightResizerX - leftResizerX);
            // 두 y 거리
            const bboxHeight = Math.abs(rightResizerY - leftResizerY);

            const maxX = minX + bboxWidth;
            const maxY = minY + bboxHeight;

            console.log('📦 Line 바운딩 박스 (SelectBox):', {
              nodeId: node.id,
              nodeData: {
                position: { x: nodeX, y: nodeY },
                'node.width (바운딩 박스)': nodeWidth,
                'node.height (바운딩 박스)': nodeHeight,
                'data.width (선 길이)': node.data?.width,
                'data.centerOffsetX': node.data?.centerOffsetX,
                'data.centerOffsetY': node.data?.centerOffsetY,
                rotation: rotation
              },
              calculated: {
                center: { x: centerX, y: centerY },
                leftResizer: { x: leftResizerX, y: leftResizerY },
                rightResizer: { x: rightResizerX, y: rightResizerY },
                boundingBox: {
                  x: minX,
                  y: minY,
                  width: bboxWidth,
                  height: bboxHeight
                }
              },
              selectionBox: normalizedBox
            });

            // 바운딩 박스와 선택 박스의 교차 검사
            isInBox = !(maxX < normalizedBox.x1 || minX > normalizedBox.x2 ||
              maxY < normalizedBox.y1 || minY > normalizedBox.y2);
          } else {
            // 일반 노드: 바운딩 박스 검사
            const nodeX1 = node.position.x;
            const nodeY1 = node.position.y;
            const nodeX2 = nodeX1 + (node.width || node.data?.width || 0);
            const nodeY2 = nodeY1 + (node.height || node.data?.height || 0);

            // 교차 검사
            isInBox = !(nodeX2 < normalizedBox.x1 || nodeX1 > normalizedBox.x2 ||
              nodeY2 < normalizedBox.y1 || nodeY1 > normalizedBox.y2);
          }

          if (isInBox) {
            selectedNodeIds.push(node.id);
          }
        });

        console.log('🔍 박스 선택 영역:', {
          x: normalizedBox.x1,
          y: normalizedBox.y1,
          width: normalizedBox.x2 - normalizedBox.x1,
          height: normalizedBox.y2 - normalizedBox.y1,
        });
        console.log('🔍 선택된 노드:', selectedNodeIds.map(id => {
          const n = allNodes.find(node => node.id === id);
          return n ? {
            id: n.id,
            type: n.type,
            position: n.position,
            width: n.width || n.data?.width,
            height: n.height || n.data?.height,
          } : id;
        }));

        // 노드 선택 상태 업데이트
        setNodes((nds) => {
          const updatedNodes = nds.map(node => ({
            ...node,
            selected: selectedNodeIds.includes(node.id)
          }));
          nodesRef.current = updatedNodes;
          return updatedNodes;
        });
      }

      setIsBoxSelecting(false);
      setSelectionBox(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [boxSelectionMode, isBoxSelecting, selectionBox, screenToFlowPosition, setNodes]);

  // 현재 사용자 정보 메모이제이션
  const currentUser = useMemo(() => getUserNameFromMsal(), [accounts]);

  // nodeTypes 콜백을 ref로 관리 → nodeTypes가 매 렌더마다 재생성되는 것을 방지
  // nodeTypes 재생성 시 ReactFlow가 모든 노드를 언마운트/리마운트하면서 dimensions가 초기화되어
  // selectionOnDrag 히트테스트가 잘못됨
  const nodeTypeCbRef = useRef({});
  nodeTypeCbRef.current = {
    handleUpdateCardData,
    handleUpdateRangeMapField,
    handleUpdateTextBox,
    handleTextChangeLive,
    handleUpdateEditingState,
    handleUpdateStyleCardEditingState,
    handleUpdateTextDirection,
    broadcast,
    currentUser,
  };

  const nodeTypes = useMemo(() => ({
    StyleCard: (props) => (
      <StyleCard
        {...props}
        onUpdateData={nodeTypeCbRef.current.handleUpdateCardData}
        onUpdateField={nodeTypeCbRef.current.handleUpdateRangeMapField}
        onUpdateEditingState={(isEditing) => nodeTypeCbRef.current.handleUpdateStyleCardEditingState(props.id, isEditing)}
        onSelectNode={() => {
          setNodes((nds) => {
            const updatedNodes = nds.map(node => ({
              ...node,
              selected: node.id === props.id,
              data: {
                ...node.data,
                selectedBy: node.id === props.id ? getUserNameFromMsal() : null,
                showUserLabel: node.id === props.id && !isPersonalRef.current ? true : false
              }
            }));
            nodesRef.current = updatedNodes;
            return updatedNodes;
          });
          setSelectedNodeId(props.id);
          setSelectedEdgeId(null);
        }}
        currentUser={nodeTypeCbRef.current.currentUser}
        nodeId={props.id}
      />
    ),
    rectangle: (props) => <Rectangle {...props} />,
    circle: (props) => <Circle {...props} />,
    sticker: (props) => <Sticker {...props} />,
    textbox: (props) => (
      <TextBox
        {...props}
        data={{
          ...props.data,
          onUpdateText: (newText) => nodeTypeCbRef.current.handleUpdateTextBox(props.id, newText),
          onTextChangeLive: (newText) => nodeTypeCbRef.current.handleTextChangeLive(props.id, newText),
          onUpdateEditingState: (isEditing) => nodeTypeCbRef.current.handleUpdateEditingState(props.id, isEditing),
          onUpdateTextDirection: (textDirection) => nodeTypeCbRef.current.handleUpdateTextDirection(props.id, textDirection),
          currentUser: nodeTypeCbRef.current.currentUser,
        }}
      />
    ),
    image: (props) => <Image {...props} />,
    line: (props) => <Line {...props} broadcast={nodeTypeCbRef.current.broadcast} nodesRef={nodesRef} edgesRef={edgesRef} />,
    frame: (props) => <Frame {...props} />
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // edgeTypes 정의 (커스텀 Edge 타입)
  const edgeTypes = useMemo(() => ({}), []);

  // 노드 추가 (좌표 기반) - 초기에는 템플릿만
  const addCard = useCallback(
    (position, cardData) => {
      const id = uuidv4(); // UUID 형식으로 변경
      // 초기 템플릿 데이터 (스타일 코드만 있고 나머지는 비어있음)
      const templateData = {
        part_cd: '', // 품번 입력 대기
        sesn: '',
        brd_cd: '',
        item: '',
        description: '',
        fabric: '',
        fiber_content: '',
        domain: '',
        sub_domain: '', // 서브도메인 (레인지맵용)
        set_up_style_no: '',
        life_style: '',
        level: '',
        fit: '',
        price: 0,
        colors: [],
        color_count: '', // 컬러수 (레인지맵용)
        expected_qty: '', // 예상수량 (레인지맵용)
        designer: '',
        vendor: '',
        image: '',
        mapType: selectedMapType, // 선택된 맵 타입 추가
        // 판매맵용 필드
        tag_price: undefined,
        unit_cost: undefined,
        ac_ord_qty_kor: undefined,
        ac_sale_qty_cns: undefined,
        cns_delv_dt_1st: '',
        style_sale_rate: undefined,
        w1_cns: undefined,
        w2_cns: undefined,
        w3_cns: undefined,
        salesData: [],
      };
      const currentUser = getUserNameFromMsal();
      const nextNode = {
        id,
        type: "StyleCard",
        position,
        data: {
          ...(cardData || templateData),
          width: 260,
          height: 260,
          selectedBy: currentUser,
          showUserLabel: !isPersonalRef.current
        },
        width: 260,
        height: 260,
        resizable: true,
        // 크기 제한 설정 (격자 크기의 배수로)
        minWidth: 200, // 20px * 10
        maxWidth: 600, // 20px * 30
        minHeight: 160, // 20px * 8
        maxHeight: 800, // 20px * 40
        selected: true // 생성 시 자동 선택
      };
      // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
      const previouslySelectedNodeIds = nodesRef.current
        .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
        .map(n => n.id);

      setNodes((nds) => {
        // 기존 노드들의 선택 해제
        const deselectedNodes = nds.map(node => ({
          ...node,
          selected: false,
          data: {
            ...node.data,
            selectedBy: null,
            showUserLabel: false
          }
        }));

        // displayOrder 업데이트는 히스토리 저장 전에 완료
        const newNodes = [...deselectedNodes, {
          ...nextNode,
          data: {
            ...nextNode.data,
            displayOrder: deselectedNodes.length // 현재 노드 개수를 displayOrder로 설정
          }
        }].map((node, index) => ({
          ...node,
          data: {
            ...node.data,
            displayOrder: index // 전체 배열 재정렬
          }
        }));

        // ref 업데이트
        nodesRef.current = newNodes;

        // 최근 생성된 노드로 등록 (리사이즈 broadcast 무시용)
        recentlyCreatedNodeIdsRef.current.add(id);
        setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);

        // 카드 추가는 즉시 전송 (실시간 동기화)
        // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
        setTimeout(() => {
          const allChangedNodeIds = [id, ...previouslySelectedNodeIds];
          broadcast(newNodes, true, allChangedNodeIds, true);
        }, 0);

        return newNodes;
      });
    },
    [setNodes, broadcast, selectedMapType]
  );

  // 스타일카드 메뉴 토글
  const handleAddCardClick = useCallback(() => {
    if (addCardMode) {
      // 이미 활성화되어 있으면 비활성화
      console.log('카드 추가 모드 비활성화');
      setAddCardMode(false);
      setShowStyleCardMenu(false);
    } else if (showStyleCardMenu) {
      // 메뉴가 열려있으면 닫기
      setShowStyleCardMenu(false);
    } else {
      // 메뉴 열기
      console.log('스타일카드 메뉴 열기');
      setShowStyleCardMenu(true);
      setShowTemplateMenu(false);
      setShowShapeMenu(false);
      setShowStickerModal(false);
      setBoxSelectionMode(false);
    }
  }, [addCardMode, showStyleCardMenu]);

  // 맵 타입 선택
  const handleMapTypeSelect = useCallback((mapType) => {
    console.log('맵 타입 선택:', mapType);
    setSelectedMapType(mapType);
    setAddCardMode(true);
    setAddTemplateMode(false);
    setShowStyleCardMenu(false);
    setShowTemplateMenu(false);
    
    // 스타일카드 삽입 모드 활성화 시 기존 선택 해제 및 색상 박스 닫기
    setShowNodeColorPicker(false);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setBoxSelectionMode(false);
  }, []);

  // 템플릿 메뉴 토글
  const handleAddTemplate = useCallback(() => {
    // 이미 템플릿 모드가 활성화되어 있으면 비활성화
    if (addTemplateMode) {
      console.log('템플릿 추가 모드 비활성화');
      setAddTemplateMode(false);
      setShowTemplateMenu(false);
      return;
    }

    if (showTemplateMenu) {
      // 메뉴가 열려있으면 닫기
      setShowTemplateMenu(false);
    } else {
      // 메뉴 열기
      console.log('템플릿 메뉴 열기');
      setShowTemplateMenu(true);
      setShowStyleCardMenu(false);
      setShowShapeMenu(false);
      setShowStickerModal(false);
      setBoxSelectionMode(false);
    }
  }, [addTemplateMode, showTemplateMenu]);

  // 템플릿용 맵 타입 선택
  const handleTemplateMapTypeSelect = useCallback((mapType) => {
    console.log('템플릿 맵 타입 선택:', mapType);
    setSelectedMapType(mapType);
    setShowTemplateMenu(false);
    setShowStyleCardMenu(false);
    setAddCardMode(false);
    // 다이얼로그 표시
    setShowTemplateDialog(true);
    
    // 템플릿 삽입 모드 활성화 시 기존 선택 해제 및 색상 박스 닫기
    setShowNodeColorPicker(false);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setBoxSelectionMode(false);
  }, []);

  // 박스 선택 모드 토글
  const handleBoxSelectionMode = useCallback(() => {
    if (boxSelectionMode) {
      console.log('박스 선택 모드 비활성화');
      setBoxSelectionMode(false);
    } else {
      console.log('박스 선택 모드 활성화');
      setBoxSelectionMode(true);
      setAddCardMode(false);
      setAddTemplateMode(false);
      setToolMode(null);
    }
  }, [boxSelectionMode]);


  // 도구 선택 핸들러 (토글 방식)
  const handleToolSelect = useCallback((tool) => {
    console.log('🖊️ handleToolSelect 호출:', tool, '현재 toolMode:', toolMode, 'pendingToolMode:', pendingToolMode, 'showColorPalette:', showColorPalette);

    // rectangle, heart, circle, line, frame 도구는 색상 팔레트 없이 바로 활성화
    if (tool === 'rectangle' || tool === 'heart' || tool === 'circle' || tool === 'line' || tool === 'frame') {
      // 같은 도구를 다시 선택하면 해제
      if (toolMode === tool) {
        console.log('🖊️ 같은 도구 선택 - 해제:', tool);
        setToolMode(null);
        setPendingToolMode(null);
        setShowColorPalette(false);
        setShowToolMenu(false);
        setAddCardMode(false);
        setAddTemplateMode(false);
        setBoxSelectionMode(false);
        return;
      }

      // 바로 도구 모드 활성화
      setToolMode(tool);
      setPendingToolMode(null);
      setShowColorPalette(false);
    } else {
      // 다른 도구들 (textbox, pen 등)
      // 같은 도구를 다시 선택하면 해제
      if (toolMode === tool) {
        console.log('🖊️ 같은 도구 선택 - 해제');
        setToolMode(null);
        setPendingToolMode(null);
        setShowToolMenu(false);
        setAddCardMode(false);
        setAddTemplateMode(false);
        setBoxSelectionMode(false);
        return;
      }

      console.log('🖊️ toolMode 설정:', tool);
      setToolMode(tool);
      setPendingToolMode(null);
    }
    setShowToolMenu(false);
    setAddCardMode(false);
    setAddTemplateMode(false);
    setBoxSelectionMode(false);
  }, [toolMode, pendingToolMode, showColorPalette]);

  // 사각형 도구 선택 핸들러 - 도형 옵션 메뉴 표시
  const handleRectangleToolSelect = useCallback((e) => {
    e.stopPropagation();
    if (showShapeMenu) {
      // 메뉴가 열려있으면 닫고 도구도 비활성화
      setShowShapeMenu(false);
      setToolMode(null);
      setPendingToolMode(null);
      setShowColorPalette(false);
      setBoxSelectionMode(false);
    } else {
      // 메뉴 열기
      setShowShapeMenu(true);
    }
    setAddCardMode(false);
    setAddTemplateMode(false);
    setShowToolMenu(false);
  }, [showShapeMenu]);

  // 도형 옵션 선택 핸들러
  const handleShapeSelect = useCallback((shapeType) => {
    setShowShapeMenu(false);
    if (toolMode === shapeType) {
      // 같은 도구를 다시 선택하면 해제
      setToolMode(null);
      setPendingToolMode(null);
      setShowColorPalette(false);
      setBoxSelectionMode(false);
    } else {
      // 도구 모드 활성화
      setToolMode(shapeType);
      setPendingToolMode(null);
      setShowColorPalette(false);
      setBoxSelectionMode(false);
    }
  }, [toolMode]);


  // 스티커 목록 불러오기
  const loadStickers = useCallback(async () => {
    try {
      const response = await fetch('/api/matrix-board/stickers');
      const result = await response.json();

      if (result.success && result.data) {
        setStickers(result.data);
      } else {
        console.error('스티커 목록 로드 실패:', result.message);
      }
    } catch (error) {
      console.error('스티커 목록 로드 오류:', error);
    }
  }, []);

  // 스티커 업로드 핸들러
  const handleStickerUpload = useCallback(async (file) => {
    if (!file) return;

    setUploadingSticker(true);
    const formData = new FormData();

    // 스티커용 메타데이터
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `sticker_${timestamp}.${fileExtension}`;

    // 기존 S3 업로드 API 형식에 맞춤
    formData.append('originalFile', file);
    formData.append('compressedFile', file); // 압축 없이 원본 사용
    formData.append('part_cd', `sticker_${timestamp}`);
    formData.append('sesn', 'sticker');
    formData.append('brd_cd', 'matrix_board');
    formData.append('stickerFileName', fileName);
    formData.append('stickerFolder', 'interstellar/matrix_board/stickers/');

    console.log('스티커 업로드 시작:', {
      fileName,
      fileSize: file.size,
      fileType: file.type,
      formDataKeys: Array.from(formData.keys())
    });

    try {
      // S3 업로드 API 호출
      const response = await fetch('/api/s3-upload', {
        method: 'POST',
        body: formData,
      });

      console.log('스티커 업로드 응답:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      const result = await response.text();
      console.log('스티커 업로드 결과:', result);

      if (!response.ok) {
        throw new Error(result || '업로드 실패');
      }

      // 업로드된 스티커 URL 생성
      const stickerUrl = `https://static-dashff.fnf.co.kr/interstellar/matrix_board/stickers/${fileName}`;

      // 업로드 후 목록 새로고침
      await loadStickers();
      alert('스티커가 업로드되었습니다.');
      // 업로드 모달 닫기
      setShowStickerUploadModal(false);
    } catch (error) {
      console.error('스티커 업로드 오류:', error);
      const errorMessage = error.message || '스티커 업로드에 실패했습니다.';
      alert(`스티커 업로드 오류: ${errorMessage}`);
    } finally {
      setUploadingSticker(false);
    }
  }, [loadStickers]);

  // 컴포넌트 마운트 시 스티커 목록 미리 불러오기 (성능 최적화)
  useEffect(() => {
    loadStickers();
  }, [loadStickers]);

  // 스티커 모달이 열릴 때 목록 새로고침 (업데이트 확인)
  useEffect(() => {
    if (showStickerModal) {
      loadStickers();
    }
  }, [showStickerModal, loadStickers]);

  // 클립보드 붙여넣기 이벤트 리스너 (스티커 모달 또는 업로드 모달이 열려있을 때만)
  useEffect(() => {
    if (!showStickerModal && !showStickerUploadModal) return;

    const handlePaste = (e) => {
      // input, textarea 등 입력 필드가 포커스되어 있으면 이벤트를 막지 않음
      const target = e.target;
      const isInputField = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isInputField) {
        return; // 입력 필드에서는 기본 동작 허용
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          e.stopPropagation(); // 다른 핸들러가 처리하지 않도록
          console.log('📋 스티커 업로드 모달에서 이미지 붙여넣기 감지');
          const blob = item.getAsFile();
          if (blob) {
            const file = new File([blob], `pasted_${Date.now()}.png`, { type: 'image/png' });
            handleStickerUpload(file);
          }
          break;
        }
      }
    };

    // capture phase에서 실행하여 다른 핸들러보다 먼저 처리
    window.addEventListener('paste', handlePaste, true);
    return () => {
      window.removeEventListener('paste', handlePaste, true);
    };
  }, [showStickerModal, showStickerUploadModal, handleStickerUpload]);

  // 스티커 선택 핸들러 (스티커를 클릭하면 캔버스에 배치)
  const handleStickerSelect = useCallback((sticker) => {
    setShowStickerModal(false);
    setShowShapeMenu(false);

    // 스티커 배치 모드 활성화 시 기존 선택 해제 및 색상 박스 닫기
    setShowNodeColorPicker(false);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);

    // 스티커 배치 모드 활성화
    setToolMode('sticker');

    // 선택된 스티커 정보 저장
    setPendingToolMode({
      stickerData: sticker,
    });
  }, []);

  // 스티커 배치 핸들러 (캔버스 클릭 시)
  const handleStickerPlace = useCallback((position, stickerData) => {
    if (!stickerData) return;

    const currentUser = getUserNameFromMsal();
    const id = uuidv4(); // UUID만 사용 (36자)
    // node_uuid는 36자로 제한되어 있으므로 UUID만 사용
    const stickerSize = 200;
    const newNode = {
      id,
      type: 'sticker',
      position: { x: position.x - stickerSize / 2, y: position.y - stickerSize / 2 },
      data: {
        imageUrl: stickerData.url,
        imageName: stickerData.name,
        width: stickerSize,
        height: stickerSize,
        opacity: 1,
        rotation: 0,
        selectedBy: currentUser,
        showUserLabel: !isPersonalRef.current
      },
      selected: true, // 생성 시 자동 선택
    };

    // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const newNodes = [...deselectedNodes, newNode].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index
        }
      }));

      // ref 업데이트
      nodesRef.current = newNodes;

      // 최근 생성된 노드로 등록 (리사이즈 broadcast 무시용)
      recentlyCreatedNodeIdsRef.current.add(id);
      setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);

      // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
      setTimeout(() => {
        const allChangedNodeIds = [id, ...previouslySelectedNodeIds];
        broadcast(newNodes, true, allChangedNodeIds, true);
      }, 0);

      return newNodes;
    });
    setToolMode(null);
    setPendingToolMode(null);
  }, [setNodes, broadcast]);

  // RGBA 색상 문자열 생성
  const getRgbaColor = useCallback((hex, opacity) => {
    // transparent 처리
    if (hex === 'transparent') {
      return 'transparent';
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }, []);

  // 노드 색상 변경 핸들러
  const handleNodeColorChange = useCallback((color) => {
    const colorValue = color.hex;
    const opacity = color.rgb.a !== undefined ? color.rgb.a : 1;

    setSelectedColor(prev => ({
      ...prev,
      [nodeColorType]: colorValue,
      [`${nodeColorType}Opacity`]: opacity
    }));

    // 노드가 선택된 경우
    if (selectedNodeId) {
      setNodes((nds) => {
        const updatedNodes = nds.map(node => {
          if (node.id === selectedNodeId) {
            const fillRgba = getRgbaColor(
              nodeColorType === 'fill' ? colorValue : (node.data?.fill || '#000000'),
              nodeColorType === 'fill' ? opacity : (node.data?.fillOpacity !== undefined ? node.data.fillOpacity : 1)
            );
            const strokeRgba = getRgbaColor(
              nodeColorType === 'stroke' ? colorValue : (node.data?.stroke || '#000000'),
              nodeColorType === 'stroke' ? opacity : (node.data?.strokeOpacity !== undefined ? node.data.strokeOpacity : 1)
            );

            return {
              ...node,
              data: {
                ...node.data,
                fill: nodeColorType === 'fill' ? colorValue : (node.data?.fill || '#000000'),
                stroke: nodeColorType === 'stroke' ? colorValue : (node.data?.stroke || '#000000'),
                fillOpacity: nodeColorType === 'fill' ? opacity : (node.data?.fillOpacity !== undefined ? node.data.fillOpacity : 1),
                strokeOpacity: nodeColorType === 'stroke' ? opacity : (node.data?.strokeOpacity !== undefined ? node.data.strokeOpacity : 1),
                fillRgba,
                strokeRgba
              }
            };
          }
          return node;
        });
        nodesRef.current = updatedNodes;
        broadcast(updatedNodes, true, [selectedNodeId], true);
        return updatedNodes;
      });
    }

    // Edge가 선택된 경우 (Edge는 stroke만)
    if (selectedEdgeId && nodeColorType === 'stroke') {
      setEdges((eds) => {
        const updatedEdges = eds.map(edge => {
          if (edge.id === selectedEdgeId) {
            return {
              ...edge,
              style: {
                ...edge.style,
                stroke: colorValue
              },
              data: {
                ...edge.data,
                stroke: colorValue
              }
            };
          }
          return edge;
        });
        edgesRef.current = updatedEdges;
        broadcast(nodesRef.current, updatedEdges, false, []);
        return updatedEdges;
      });
    }
  }, [selectedNodeId, selectedEdgeId, nodeColorType, getRgbaColor, setNodes, setEdges, broadcast]);

  // 노드 테두리 굵기 변경 핸들러
  const handleNodeStrokeWidthChange = useCallback((width) => {
    setNodeStrokeWidth(width);

    // 노드가 선택된 경우
    if (selectedNodeId) {
      setNodes((nds) => {
        const updatedNodes = nds.map(node => {
          if (node.id === selectedNodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                strokeWidth: width
              }
            };
          }
          return node;
        });
        nodesRef.current = updatedNodes;
        broadcast(updatedNodes, true, [selectedNodeId], true);
        return updatedNodes;
      });
    }

    // Edge가 선택된 경우
    if (selectedEdgeId) {
      setEdges((eds) => {
        const updatedEdges = eds.map(edge => {
          if (edge.id === selectedEdgeId) {
            return {
              ...edge,
              style: {
                ...edge.style,
                strokeWidth: width
              },
              data: {
                ...edge.data,
                strokeWidth: width
              }
            };
          }
          return edge;
        });
        edgesRef.current = updatedEdges;
        broadcast(nodesRef.current, updatedEdges, false, []);
        return updatedEdges;
      });
    }
  }, [selectedNodeId, selectedEdgeId, setNodes, setEdges, broadcast]);

  // 선 스타일 변경 핸들러
  const handleLineStyleChange = useCallback((styleValue) => {
    if (!selectedNodeId) return;

    setNodeLineStyle(styleValue);

    // 숫자 값을 strokeDasharray 문자열로 변환
    // 0: 실선, 1-15: 큰 대시 (20->8), 16-30: 점 (6->2)
    let strokeDasharray;
    if (styleValue === 0) {
      strokeDasharray = 'none'; // 실선
    } else if (styleValue <= 15) {
      // 큰 대시: 20에서 8까지 감소
      const dashLength = 20 - Math.floor((styleValue / 15) * 12);
      strokeDasharray = `${dashLength},${dashLength}`;
    } else {
      // 점: 6에서 2까지 감소
      const dotLength = 6 - Math.floor(((styleValue - 16) / 14) * 4);
      strokeDasharray = `${dotLength},${dotLength}`;
    }

    // 노드에 즉시 적용
    setNodes((nds) => {
      const updatedNodes = nds.map(node => {
        if (node.id === selectedNodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              strokeDasharray: strokeDasharray
            }
          };
        }
        return node;
      });
      nodesRef.current = updatedNodes;
      broadcast(updatedNodes, edgesRef.current, true, [selectedNodeId]);
      return updatedNodes;
    });
  }, [selectedNodeId, setNodes, broadcast]);

  // 화살표 변경 핸들러 (Line 노드와 Edge 모두 지원)
  const handleArrowChange = useCallback((arrowType, value) => {
    // Line 노드인 경우
    if (selectedNodeId) {
      const selectedNode = nodesRef.current.find(node => node.id === selectedNodeId);
      if (selectedNode?.type === 'line') {
        setNodes((nds) => {
          const updatedNodes = nds.map(node => {
            if (node.id === selectedNodeId) {
              // 화살표는 한쪽에만 있도록: 한쪽을 활성화하면 다른 쪽은 비활성화
              const newData = {
                ...node.data,
                [arrowType]: value
              };

              // 한쪽 화살표를 활성화하면 다른 쪽은 자동으로 비활성화
              if (value === true) {
                if (arrowType === 'arrowStart') {
                  newData.arrowEnd = false;
                } else if (arrowType === 'arrowEnd') {
                  newData.arrowStart = false;
                }
              }

              // 화살표가 모두 비활성화되었는지 확인
              const arrowStart = newData.arrowStart || false;
              const arrowEnd = newData.arrowEnd || false;

              // 화살표가 모두 비활성화되었으면 color picker 닫기
              if (!arrowStart && !arrowEnd) {
                setTimeout(() => {
                  setShowNodeColorPicker(false);
                  setSelectedNodeId(null);
                }, 0);
              }

              return {
                ...node,
                data: newData
              };
            }
            return node;
          });
          nodesRef.current = updatedNodes;
          broadcast(updatedNodes, true, [selectedNodeId], true);
          return updatedNodes;
        });
        return;
      }
    }

    // Edge인 경우
    if (selectedEdgeId) {
      const selectedEdge = edgesRef.current.find(edge => edge.id === selectedEdgeId);
      setEdges((eds) => {
        const updatedEdges = eds.map(edge => {
          if (edge.id === selectedEdgeId) {
            // 화살표는 한쪽에만 있도록: 한쪽을 활성화하면 다른 쪽은 비활성화
            const newData = {
              ...edge.data,
              [arrowType]: value
            };

            // 한쪽 화살표를 활성화하면 다른 쪽은 자동으로 비활성화
            if (value === true) {
              if (arrowType === 'arrowStart') {
                newData.arrowEnd = false;
              } else if (arrowType === 'arrowEnd') {
                newData.arrowStart = false;
              }
            }

            // 화살표가 모두 비활성화되었는지 확인
            const arrowStart = newData.arrowStart || false;
            const arrowEnd = newData.arrowEnd || false;

            // 화살표가 모두 비활성화되었으면 color picker 닫기
            if (!arrowStart && !arrowEnd) {
              setTimeout(() => {
                setShowNodeColorPicker(false);
                setSelectedEdgeId(null);
              }, 0);
            }

            return {
              ...edge,
              data: newData
            };
          }
          return edge;
        });
        edgesRef.current = updatedEdges;
        broadcast(nodesRef.current, updatedEdges, false, []);
        return updatedEdges;
      });
    }
  }, [selectedNodeId, selectedEdgeId, setNodes, setEdges, broadcast]);

  // HSV를 RGB로 변환
  const hsvToRgb = useCallback((h, s, v) => {
    const c = v * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = v - c;
    let r, g, b;

    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else[r, g, b] = [c, 0, x];

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    };
  }, []);

  // RGB를 HEX로 변환
  const rgbToHex = useCallback((r, g, b) => {
    return `#${[r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
  }, []);

  // HEX를 HSV로 변환
  const hexToHsv = useCallback((hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
      if (max === r) {
        h = ((g - b) / delta) % 6;
      } else if (max === g) {
        h = (b - r) / delta + 2;
      } else {
        h = (r - g) / delta + 4;
      }
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const s = max === 0 ? 0 : delta / max;
    const v = max;

    return { h, s, v };
  }, []);

  // 스펙트럼 색상 선택 상태
  const [spectrumHsv, setSpectrumHsv] = useState({ h: 240, s: 1, v: 1 });
  const spectrumRef = useRef(null);
  const brightnessRef = useRef(null);
  const spectrumCanvasRef = useRef(null);
  const brightnessCanvasRef = useRef(null);

  // HEX 색상을 HSV로 변환하여 스펙트럼 상태 초기화
  useEffect(() => {
    const hsv = hexToHsv(selectedColor.fill);
    setSpectrumHsv(hsv);
  }, [selectedColor.fill, hexToHsv]);

  // 스펙트럼 Canvas 그리기
  useEffect(() => {
    const canvas = spectrumCanvasRef.current;
    if (!canvas || !showColorPalette) return;

    // 컨테이너 크기 가져오기
    const container = spectrumRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);

    // Canvas 크기 설정 (고해상도 디스플레이 지원)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // 가로축: 색상 (Hue) 0-360
    // 세로축: 채도 (Saturation) 1-0 (위에서 아래로)
    for (let y = 0; y < height; y++) {
      const s = 1 - (y / height);
      for (let x = 0; x < width; x++) {
        const h = (x / width) * 360;
        const rgb = hsvToRgb(h, s, 1);
        ctx.fillStyle = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }, [hsvToRgb, showColorPalette]);

  // 밝기 슬라이더 Canvas 그리기
  useEffect(() => {
    const canvas = brightnessCanvasRef.current;
    if (!canvas || !showColorPalette) return;

    // 컨테이너 크기 가져오기
    const container = brightnessRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);

    // Canvas 크기 설정 (고해상도 디스플레이 지원)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // 세로축: 밝기 (Value) 1-0 (위에서 아래로)
    for (let y = 0; y < height; y++) {
      const v = 1 - (y / height);
      const rgb = hsvToRgb(spectrumHsv.h, spectrumHsv.s, v);
      ctx.fillStyle = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
      ctx.fillRect(0, y, width, 1);
    }
  }, [spectrumHsv.h, spectrumHsv.s, hsvToRgb, showColorPalette]);


  // 위치를 격자에 맞추기 (스냅)
  const snapToGrid = useCallback((position) => {
    return {
      x: Math.round(position.x / gridSize) * gridSize,
      y: Math.round(position.y / gridSize) * gridSize
    };
  }, [gridSize]);

  // 크기를 격자에 맞추기 (스냅)
  const snapSizeToGrid = useCallback((size) => {
    return Math.round(size / gridSize) * gridSize;
  }, [gridSize]);

  // 겹치지 않는 위치 찾기
  const findNonOverlappingPosition = useCallback((clickPosition, cardWidth = 260, cardHeight = 260) => {
    const margin = 20; // 카드 간 최소 간격
    const searchRadius = 500; // 검색 반경
    const step = gridSize; // 검색 단계 (격자 크기)

    // 클릭 위치를 격자에 맞춤
    const snappedPosition = snapToGrid(clickPosition);

    // 기존 노드들의 위치 정보 수집
    const existingCards = nodesRef.current.map(node => ({
      x: node.position.x,
      y: node.position.y,
      width: node.width || cardWidth,
      height: node.height || cardHeight
    }));

    // 클릭 위치가 겹치지 않으면 그대로 사용
    const isOverlapping = (x, y) => {
      return existingCards.some(card => {
        const cardRight = card.x + card.width + margin;
        const cardBottom = card.y + card.height + margin;
        const newCardRight = x + cardWidth + margin;
        const newCardBottom = y + cardHeight + margin;

        return !(x > cardRight || newCardRight < card.x || y > cardBottom || newCardBottom < card.y);
      });
    };

    if (!isOverlapping(snappedPosition.x, snappedPosition.y)) {
      return snappedPosition;
    }

    // 겹치면 주변에서 빈 공간 찾기 (스파이럴 검색)
    for (let radius = step; radius <= searchRadius; radius += step) {
      const angles = [0, 90, 180, 270, 45, 135, 225, 315]; // 8방향

      for (const angle of angles) {
        const rad = (angle * Math.PI) / 180;
        const x = snappedPosition.x + Math.cos(rad) * radius;
        const y = snappedPosition.y + Math.sin(rad) * radius;
        const snapped = snapToGrid({ x, y });

        if (!isOverlapping(snapped.x, snapped.y)) {
          return snapped;
        }
      }
    }

    // 모든 위치가 겹치면 클릭 위치에서 아래쪽으로 이동
    const fallback = { x: snappedPosition.x, y: snappedPosition.y + cardHeight + margin };
    return snapToGrid(fallback);
  }, [snapToGrid, gridSize]);

  // 사각형 드래그 시작
  const startRectangleDrag = useCallback((position) => {
    console.log('사각형 드래그 시작:', position);
    setRectangleStart(position);
    setRectangleEnd(position);
  }, []);

  // 사각형 드래그 업데이트
  const updateRectangleDrag = useCallback((position) => {
    if (rectangleStart) {
      setRectangleEnd(position);
    }
  }, [rectangleStart]);

  // 사각형 드래그 종료 및 생성
  const finishRectangleDrag = useCallback(() => {
    if (!rectangleStart || !rectangleEnd) {
      console.log('사각형 드래그 종료 실패: 시작 또는 끝 위치 없음', { rectangleStart, rectangleEnd });
      return;
    }

    const startFlow = rectangleStart;
    const endFlow = rectangleEnd;

    const width = Math.abs(endFlow.x - startFlow.x);
    const height = Math.abs(endFlow.y - startFlow.y);

    // 너무 작은 사각형은 무시
    if (width < 10 || height < 10) {
      console.log('사각형이 너무 작음:', { width, height });
      setRectangleStart(null);
      setRectangleEnd(null);
      return;
    }

    const position = {
      x: Math.min(startFlow.x, endFlow.x),
      y: Math.min(startFlow.y, endFlow.y)
    };

    console.log('사각형 생성:', { position, width, height });

    const id = uuidv4();
    const currentUser = getUserNameFromMsal();
    // 기본 색상: 검정 테두리, 투명 배경
    const defaultFill = 'transparent';
    const defaultStroke = '#000000';
    const defaultFillOpacity = 1; // 투명 배경
    const defaultStrokeOpacity = 1; // 검정 테두리
    const fillRgba = getRgbaColor(defaultFill, defaultFillOpacity);
    const strokeRgba = getRgbaColor(defaultStroke, defaultStrokeOpacity);
    const newNode = {
      id,
      type: "rectangle",
      position,
      width, // 노드의 width 속성 설정
      height, // 노드의 height 속성 설정
      data: {
        width, // data에도 width 저장 (Rectangle 컴포넌트가 우선 사용)
        height, // data에도 height 저장 (Rectangle 컴포넌트가 우선 사용)
        fill: defaultFill,
        fillOpacity: defaultFillOpacity,
        fillRgba: fillRgba,
        stroke: defaultStroke,
        strokeOpacity: defaultStrokeOpacity,
        strokeRgba: strokeRgba,
        strokeWidth: 5, // 테두리 두께
        selectedBy: currentUser,
        showUserLabel: !isPersonalRef.current
      },
      resizable: true,
      selected: true // 생성 시 자동 선택
    };

    // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const newNodes = [...deselectedNodes, newNode].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index // 전체 배열 재정렬
        }
      }));

      // ref 업데이트
      nodesRef.current = newNodes;

      console.log('📦 Rectangle 생성:', {
        nodeId: id,
        nodesCount: newNodes.length,
        edgesCount: edgesRef.current.length,
        edges: edgesRef.current
      });

      // 최근 생성된 노드로 등록 (리사이즈 broadcast 무시용)
      recentlyCreatedNodeIdsRef.current.add(id);
      setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);

      // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
      setTimeout(() => {
        const allChangedNodeIds = [id, ...previouslySelectedNodeIds];
        broadcast(newNodes, true, allChangedNodeIds, true);
      }, 0);

      return newNodes;
    });

    setRectangleStart(null);
    setRectangleEnd(null);
    setToolMode(null);
    
    setSelectedNodeId(id);
    setSelectedEdgeId(null);
    setShowNodeColorPicker(true);
    setNodeColorType('fill');
  }, [rectangleStart, rectangleEnd, setNodes, broadcast, selectedColor, getRgbaColor]);


  // 프레임 드래그 시작
  const startFrameDrag = useCallback((position) => {
    setFrameStart(position);
    setFrameEnd(position);
  }, []);

  // 프레임 드래그 업데이트
  const updateFrameDrag = useCallback((position) => {
    if (frameStart) setFrameEnd(position);
  }, [frameStart]);

  // 프레임 드래그 종료 및 생성
  const finishFrameDrag = useCallback(() => {
    if (!frameStart || !frameEnd) return;

    const width = Math.abs(frameEnd.x - frameStart.x);
    const height = Math.abs(frameEnd.y - frameStart.y);
    if (width < 20 || height < 20) {
      setFrameStart(null);
      setFrameEnd(null);
      return;
    }

    const position = {
      x: Math.min(frameStart.x, frameEnd.x),
      y: Math.min(frameStart.y, frameEnd.y),
    };

    const id = uuidv4();
    const currentUser = getUserNameFromMsal();
    const newNode = {
      id,
      type: 'frame',
      position,
      width,
      height,
      data: { width, height, title: '프레임', selectedBy: currentUser, showUserLabel: !isPersonalRef.current },
      resizable: true,
      selected: true,
      // 프레임은 다른 노드 뒤에 렌더링
      zIndex: -1,
    };

    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    setNodes((nds) => {
      const deselected = nds.map(node => ({
        ...node,
        selected: false,
        data: { ...node.data, selectedBy: null, showUserLabel: false },
      }));
      const newNodes = [newNode, ...deselected].map((node, index) => ({
        ...node,
        data: { ...node.data, displayOrder: index },
      }));
      nodesRef.current = newNodes;
      recentlyCreatedNodeIdsRef.current.add(id);
      setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);
      setTimeout(() => {
        broadcast(newNodes, true, [id, ...previouslySelectedNodeIds], true);
      }, 0);
      return newNodes;
    });

    setFrameStart(null);
    setFrameEnd(null);
    setToolMode(null);
    setSelectedNodeId(id);
    setSelectedEdgeId(null);
  }, [frameStart, frameEnd, setNodes, broadcast, isPersonalRef]);

  // 원형 드래그 시작
  const startCircleDrag = useCallback((position) => {
    console.log('원형 드래그 시작:', position);
    setCircleStart(position);
    setCircleEnd(position);
  }, []);

  // 원형 드래그 업데이트
  const updateCircleDrag = useCallback((position) => {
    if (circleStart) {
      setCircleEnd(position);
    }
  }, [circleStart]);

  // 원형 드래그 종료 및 생성
  const finishCircleDrag = useCallback(() => {
    if (!circleStart || !circleEnd) {
      console.log('원형 드래그 종료 실패: 시작 또는 끝 위치 없음', { circleStart, circleEnd });
      return;
    }

    const startFlow = circleStart;
    const endFlow = circleEnd;

    const width = Math.abs(endFlow.x - startFlow.x);
    const height = Math.abs(endFlow.y - startFlow.y);

    // 너무 작은 원형은 무시
    if (width < 10 || height < 10) {
      console.log('원형이 너무 작음:', { width, height });
      setCircleStart(null);
      setCircleEnd(null);
      return;
    }

    const position = {
      x: Math.min(startFlow.x, endFlow.x),
      y: Math.min(startFlow.y, endFlow.y)
    };

    console.log('원형 생성:', { position, width, height });

    const id = uuidv4();
    const currentUser = getUserNameFromMsal();
    // 기본 색상: 투명 배경, 검정 테두리
    const defaultFill = 'transparent';
    const defaultStroke = '#000000';
    const defaultFillOpacity = 1; // 투명 배경
    const defaultStrokeOpacity = 1; // 검정 테두리
    const fillRgba = getRgbaColor(defaultFill, defaultFillOpacity);
    const strokeRgba = getRgbaColor(defaultStroke, defaultStrokeOpacity);
    const newNode = {
      id,
      type: "circle",
      position,
      width, // 노드의 width 속성 설정
      height, // 노드의 height 속성 설정
      data: {
        width, // data에도 width 저장
        height, // data에도 height 저장
        fill: defaultFill,
        fillOpacity: defaultFillOpacity,
        fillRgba: fillRgba,
        stroke: defaultStroke,
        strokeOpacity: defaultStrokeOpacity,
        strokeRgba: strokeRgba,
        strokeWidth: 5, // 테두리 두께
        selectedBy: currentUser,
        showUserLabel: !isPersonalRef.current
      },
      resizable: true,
      selected: true // 생성 시 자동 선택
    };

    // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const newNodes = [...deselectedNodes, newNode].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index // 전체 배열 재정렬
        }
      }));

      // ref 업데이트
      nodesRef.current = newNodes;

      // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
      setTimeout(() => {
        const allChangedNodeIds = [id, ...previouslySelectedNodeIds];
        broadcast(newNodes, true, allChangedNodeIds, true);
      }, 0);

      // 최근 생성된 노드로 등록 (리사이즈 broadcast 무시용)
      recentlyCreatedNodeIdsRef.current.add(id);
      setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);

      return newNodes;
    });

    setCircleStart(null);
    setCircleEnd(null);
    setToolMode(null);
    
    setSelectedNodeId(id);
    setSelectedEdgeId(null);
    setShowNodeColorPicker(true);
    setNodeColorType('fill');
  }, [circleStart, circleEnd, setNodes, broadcast, selectedColor, getRgbaColor]);



  // 텍스트박스 드래그 시작
  const startTextBoxDrag = useCallback((position) => {
    console.log('텍스트박스 드래그 시작:', position);
    setTextBoxStart(position);
    setTextBoxEnd(position);
  }, []);

  // 텍스트박스 드래그 업데이트
  const updateTextBoxDrag = useCallback((position) => {
    if (textBoxStart) {
      setTextBoxEnd(position);
    }
  }, [textBoxStart]);

  // 텍스트박스 드래그 종료 및 생성
  const finishTextBoxDrag = useCallback(() => {
    if (!textBoxStart || !textBoxEnd) {
      console.log('텍스트박스 드래그 종료 실패: 시작 또는 끝 위치 없음', { textBoxStart, textBoxEnd });
      return;
    }

    const startFlow = textBoxStart;
    const endFlow = textBoxEnd;

    const width = Math.abs(endFlow.x - startFlow.x);
    const height = Math.abs(endFlow.y - startFlow.y);

    // 너무 작은 텍스트박스는 무시
    if (width < 50 || height < 30) {
      console.log('텍스트박스가 너무 작음:', { width, height });
      setTextBoxStart(null);
      setTextBoxEnd(null);
      return;
    }

    const position = {
      x: Math.min(startFlow.x, endFlow.x),
      y: Math.min(startFlow.y, endFlow.y)
    };

    console.log('텍스트박스 생성:', { position, width, height });

    const id = uuidv4();
    const currentUser = getUserNameFromMsal();
    const newNode = {
      id,
      type: "textbox",
      position,
      width, // 노드의 width 속성 설정
      height, // 노드의 height 속성 설정
      data: {
        width, // data에도 width 저장
        height, // data에도 height 저장
        text: '<p class="ql-align-center" style="font-size: 16px;">텍스트 입력</p>',
        fontSize: 16,
        textDirection: 'horizontal', // 기본값: 가로 텍스트
        fill: "transparent",
        stroke: "#000000",
        strokeWidth: 0, // 테두리 두께
        isEditing: true, // 자동으로 편집 모드 활성화
        selectedBy: currentUser,
        showUserLabel: !isPersonalRef.current
      },
      selected: true, // 자동으로 선택
      resizable: true
    };

    // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    // 노드 추가 (selected 변경은 별도로 처리)
    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const newNodes = [...deselectedNodes, newNode].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index // 전체 배열 재정렬
        }
      }));

      // ref 업데이트
      nodesRef.current = newNodes;

      // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
      setTimeout(() => {
        const allChangedNodeIds = [id, ...previouslySelectedNodeIds];
        broadcast(newNodes, true, allChangedNodeIds, true);
      }, 0);

      // 최근 생성된 노드로 등록 (리사이즈 broadcast 무시용)
      recentlyCreatedNodeIdsRef.current.add(id);
      setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);

      return newNodes;
    });

    // 텍스트박스 선택 및 색상 선택 박스 표시
    setSelectedNodeId(id);
    setShowNodeColorPicker(true);
    setNodeColorType('fill');
    setSelectedColor({
      fill: "transparent",
      stroke: "#000000",
      fillOpacity: 1,
      strokeOpacity: 1
    });
    setNodeStrokeWidth(0);

    setTextBoxStart(null);
    setTextBoxEnd(null);
    setToolMode(null);
  }, [textBoxStart, textBoxEnd, setNodes, broadcast]);

  // 텍스트박스 추가 (기존 함수 - 호환성 유지)
  const addTextBoxAtPosition = useCallback((position) => {
    const id = uuidv4();
    const currentUser = getUserNameFromMsal();
    const safePosition = findNonOverlappingPosition(position, 200, 100);
    const newNode = {
      id,
      type: "textbox",
      position: safePosition,
      data: {
        width: 200,
        height: 100,
        text: '<p class="ql-align-center" style="font-size: 16px;">텍스트 입력</p>',
        fontSize: 16,
        fill: "transparent",
        stroke: "#000000",
        strokeWidth: 0,
        isEditing: true, // 자동으로 편집 모드 활성화
        selectedBy: currentUser,
        showUserLabel: !isPersonalRef.current
      },
      selected: true, // 자동으로 선택
      resizable: true
    };

    // 노드 추가 (selected 변경은 별도로 처리)
    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const newNodes = [...deselectedNodes, newNode].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index // 전체 배열 재정렬
        }
      }));

      // ref 업데이트
      nodesRef.current = newNodes;

      // 노드 추가만 전송 (selected 변경은 제외)
      setTimeout(() => {
        broadcast(newNodes, true, [id], true);
      }, 0);

      return newNodes;
    });

    // 선택 해제는 별도로 처리 (히스토리 저장 안 함)
    setTimeout(() => {
      setNodes((nds) => {
        const updatedNodes = nds.map(n => n.id === id ? { ...n, selected: true } : { ...n, selected: false });
        nodesRef.current = updatedNodes;
        // selected 변경은 히스토리 저장 안 함
        broadcast(updatedNodes, true, [], false);
        return updatedNodes;
      });
    }, 10);

    // 텍스트박스 선택 및 색상 선택 박스 표시
    setSelectedNodeId(id);
    setShowNodeColorPicker(true);
    setNodeColorType('fill');
    setSelectedColor({
      fill: "transparent",
      stroke: "#000000",
      fillOpacity: 1,
      strokeOpacity: 1
    });
    setNodeStrokeWidth(0);

    setToolMode(null);
  }, [setNodes, broadcast, findNonOverlappingPosition]);

  // 선 드래그 시작
  const startLineDrag = useCallback((position) => {
    console.log('선 드래그 시작:', position);
    setLineStart(position);
    setLineEnd(position);
  }, []);

  // 선 드래그 업데이트
  const updateLineDrag = useCallback((position) => {
    if (lineStart) {
      setLineEnd(position);
    }
  }, [lineStart]);

  // 선 드래그 종료 및 생성 (Line 컴포넌트 방식)
  const finishLineDrag = useCallback(() => {
    if (!lineStart || !lineEnd) {
      console.log('선 드래그 종료 실패: 시작 또는 끝 위치 없음', { lineStart, lineEnd });
      return;
    }

    const startFlow = lineStart;
    const endFlow = lineEnd;

    // 시작점과 끝점 사이의 실제 거리 계산
    const dx = endFlow.x - startFlow.x;
    const dy = endFlow.y - startFlow.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    // 너무 짧은 선은 무시
    if (length < 10) {
      console.log('선이 너무 짧음:', { length });
      setLineStart(null);
      setLineEnd(null);
      return;
    }

    // 선의 회전 각도 계산 (라디안 -> 도)
    const rotation = Math.atan2(dy, dx) * (180 / Math.PI);

    // 선의 중심점 계산
    const centerX = (startFlow.x + endFlow.x) / 2;
    const centerY = (startFlow.y + endFlow.y) / 2;

    // 두 리사이저 점의 AABB(Axis-Aligned Bounding Box) 계산
    const minX = Math.min(startFlow.x, endFlow.x);
    const maxX = Math.max(startFlow.x, endFlow.x);
    const minY = Math.min(startFlow.y, endFlow.y);
    const maxY = Math.max(startFlow.y, endFlow.y);

    // 바운딩 박스의 width, height (SelectBox와 동일한 AABB)
    const boundingWidth = maxX - minX;
    const boundingHeight = maxY - minY;

    // 최소 크기 보장 (너무 작으면 선택이 어려움)
    const finalBoundingWidth = Math.max(boundingWidth, 10);
    const finalBoundingHeight = Math.max(boundingHeight, 10);

    // Line 노드의 위치 (바운딩 박스의 왼쪽 위)
    const position = {
      x: minX,
      y: minY
    };

    // 바운딩 박스 내에서의 선의 중심점 (상대 좌표)
    const relativeCenterX = centerX - minX;
    const relativeCenterY = centerY - minY;

    // Line 컴포넌트 노드 생성
    const id = uuidv4();
    const currentUser = getUserNameFromMsal();
    const strokeRgba = getRgbaColor(lineSettings.color, 1);

    const newLineNode = {
      id,
      type: "line", // Line 컴포넌트 타입
      position,
      width: finalBoundingWidth, // 바운딩 박스 width
      height: finalBoundingHeight, // 바운딩 박스 height
      data: {
        lineLength: length, // ⭐ 선의 실제 길이 (Line.jsx와 일치)
        width: length, // 하위 호환성을 위해 유지
        boundingWidth: finalBoundingWidth, // 바운딩 박스 width (Line.jsx에서 사용)
        boundingHeight: finalBoundingHeight, // 바운딩 박스 height (Line.jsx에서 사용)
        stroke: lineSettings.color,
        strokeWidth: lineSettings.strokeWidth,
        strokeOpacity: 1,
        strokeRgba: strokeRgba,
        strokeDasharray: lineSettings.strokeDasharray,
        arrowEnd: lineSettings.arrowEnd,
        arrowStart: lineSettings.arrowStart,
        rotation: rotation,
        // 바운딩 박스 내에서의 선 중심점 오프셋
        centerOffsetX: relativeCenterX,
        centerOffsetY: relativeCenterY,
        selectedBy: currentUser,
        showUserLabel: !isPersonalRef.current
      },
      selectable: true,
      draggable: true,
      resizable: true,
      selected: true // 생성 시 자동 선택
    };

    // 선의 방향 판별 (가로/세로/대각선)
    const isHorizontal = Math.abs(dy) < Math.abs(dx) * 0.2; // 가로선
    const isVertical = Math.abs(dx) < Math.abs(dy) * 0.2; // 세로선
    const lineType = isHorizontal ? '가로선' : isVertical ? '세로선' : '대각선';

    console.log(`🎨 선 생성 (${lineType}):`, {
      start: startFlow,
      end: endFlow,
      delta: { dx, dy },
      length: `${length.toFixed(2)}px`,
      rotation: `${rotation.toFixed(2)}°`,
      center: { x: centerX, y: centerY },
      position,
      boundingBox: {
        width: finalBoundingWidth,
        height: finalBoundingHeight,
        boundingWidth,
        boundingHeight,
        minX,
        minY,
        maxX,
        maxY
      },
      centerOffset: {
        x: relativeCenterX,
        y: relativeCenterY
      },
      lineSettings,
      nodeToCreate: {
        id,
        position,
        'node.width (바운딩)': finalBoundingWidth,
        'node.height (바운딩)': finalBoundingHeight,
        'data.lineLength': length,
        'data.width': length,
        'data.rotation': rotation,
        'data.centerOffsetX': relativeCenterX,
        'data.centerOffsetY': relativeCenterY
      }
    });

    // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    // Line 노드 추가
    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const newNodes = [...deselectedNodes, newLineNode].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index
        }
      }));

      // nodesRef도 즉시 업데이트
      nodesRef.current = newNodes;

      // 최근 생성된 노드로 등록 (리사이즈 broadcast 무시용)
      recentlyCreatedNodeIdsRef.current.add(id);
      setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(id), 500);

      // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
      setTimeout(() => {
        const allChangedNodeIds = [id, ...previouslySelectedNodeIds];
        broadcast(newNodes, true, allChangedNodeIds, true);
      }, 0);

      return newNodes;
    });

    // ReactFlow가 노드 크기를 인식할 수 있도록 강제로 업데이트
    setTimeout(() => {
      updateNodeInternals(id);
    }, 0);

    setLineStart(null);
    setLineEnd(null);
    setToolMode(null);

    // 선 그리기 완료 후 lineSettings 초기화 (화살표 설정 제거)
    setLineSettings({
      color: '#000000',
      strokeWidth: 5, // 기본 선 굵기 5px 유지
      strokeDasharray: 'none',
      arrowEnd: false,
      arrowStart: false,
    });
    
    setSelectedNodeId(id);
    setSelectedEdgeId(null);
    setShowNodeColorPicker(true);
    setNodeColorType('stroke');
  }, [lineStart, lineEnd, lineSettings, setNodes, setEdges, broadcast]);



  // 템플릿 생성 실행 (다이얼로그에서 확인 후 모드 활성화)
  const handleCreateTemplate = useCallback(() => {
    const cols = parseInt(templateCols);
    const rows = parseInt(templateRows);

    if (isNaN(cols) || isNaN(rows) || cols < 1 || cols > 10 || rows < 1 || rows > 10) {
      alert('1~10 사이의 숫자를 입력해주세요.');
      return;
    }

    // 그리드 크기 저장하고 모드 활성화
    console.log('템플릿 추가 모드 활성화:', { cols, rows });
    setTemplateGridSize({ cols, rows });
    setAddTemplateMode(true);
    setAddCardMode(false);

    // 다이얼로그 닫기
    setShowTemplateDialog(false);
    setTemplateCols('');
    setTemplateRows('');
  }, [templateCols, templateRows]);

  // 마우스 위치에 카드 추가
  const addCardAtPosition = useCallback((position) => {
    console.log('addCardAtPosition 호출:', position);
    if (!position || !position.x || !position.y) {
      console.error('유효하지 않은 위치:', position);
      return;
    }

    const cardSize = 260;
    addCard({ x: position.x - cardSize / 2, y: position.y - cardSize / 2 });
    // 객체 추가 후 모드 비활성화 및 선택 옵션 초기화
    setAddCardMode(false);
    setSelectedMapType(null);
  }, [addCard]);

  // 마우스 위치에 템플릿 추가
  const addTemplateAtPosition = useCallback((position) => {
    console.log('addTemplateAtPosition 호출:', position, templateGridSize);
    if (!position || !position.x || !position.y) {
      console.error('유효하지 않은 위치:', position);
      return;
    }
    if (!templateGridSize.cols || !templateGridSize.rows) {
      console.error('유효하지 않은 그리드 크기:', templateGridSize);
      return;
    }
    const { cols, rows } = templateGridSize;
    const currentUser = getUserNameFromMsal();
    // 카드 크기를 격자에 맞춤 (격자 크기: 20px)
    const cardWidth = 260; // 카드 너비 (20px * 13 = 260px, 격자에 맞춤)
    // 카드 높이를 격자 크기의 배수로 설정
    const cardHeight = 260; // 카드 높이 (20px * 13 = 260px, 격자에 맞춤)
    const startX = position.x;
    const startY = position.y;

    // 이전에 선택된 노드 ID 수집 (선택 해제 broadcast용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    const newNodes = [];

    // 그리드 형태로 카드 생성 (margin 없이)
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = startX + col * cardWidth;
        const y = startY + row * cardHeight;

        const id = uuidv4();
        const isLastCard = (row === rows - 1) && (col === cols - 1);
        const templateData = {
          part_cd: '',
          sesn: '',
          brd_cd: '',
          item: '',
          description: '',
          fit: '',
          fabric: '',
          fiber_content: '',
          domain: '',
          sub_domain: '', // 서브도메인 (레인지맵용)
          set_up_style_no: '',
          life_style: '',
          level: '',
          price: 0,
          colors: [],
          color_count: '', // 컬러수 (레인지맵용)
          expected_qty: '', // 예상수량 (레인지맵용)
          designer: '',
          vendor: '',
          image: '',
          mapType: selectedMapType, // 선택된 맵 타입 추가
          // 판매맵용 필드
          tag_price: undefined,
          unit_cost: undefined,
          ac_ord_qty_kor: undefined,
          ac_sale_qty_cns: undefined,
          cns_delv_dt_1st: '',
          style_sale_rate: undefined,
          w1_cns: undefined,
          w2_cns: undefined,
          w3_cns: undefined,
          salesData: [],
          width: cardWidth,
          height: cardHeight,
          selectedBy: isLastCard ? currentUser : null,
          showUserLabel: isLastCard && !isPersonalRef.current
        };

        newNodes.push({
          id,
          type: "StyleCard",
          position: { x, y },
          data: templateData,
          width: cardWidth,
          height: cardHeight, // 격자에 맞춘 높이 설정
          resizable: true,
          minWidth: 200, // 20px * 10
          maxWidth: 600, // 20px * 30
          minHeight: 160, // 20px * 8
          maxHeight: 800, // 20px * 40
          selected: isLastCard // 마지막 카드만 선택
        });
      }
    }

    // 모든 카드를 한 번에 추가
    setNodes((nds) => {
      // 기존 노드들의 선택 해제
      const deselectedNodes = nds.map(node => ({
        ...node,
        selected: false,
        data: {
          ...node.data,
          selectedBy: null,
          showUserLabel: false
        }
      }));

      const updatedNodes = [...deselectedNodes, ...newNodes].map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index // 전체 배열 재정렬
        }
      }));
      // ref 업데이트
      nodesRef.current = updatedNodes;
      
      // 최근 생성된 노드들로 등록 (리사이즈 broadcast 무시용)
      const newNodeIds = newNodes.map(node => node.id);
      newNodeIds.forEach(nodeId => {
        recentlyCreatedNodeIdsRef.current.add(nodeId);
        setTimeout(() => recentlyCreatedNodeIdsRef.current.delete(nodeId), 500);
      });

      // 새 노드 + 이전 선택된 노드들의 선택 해제 broadcast
      const allChangedNodeIds = [...newNodeIds, ...previouslySelectedNodeIds];
      broadcast(updatedNodes, true, allChangedNodeIds, true);
      return updatedNodes;
    });

    // 객체 추가 후 모드 비활성화 및 선택 옵션 초기화
    setAddTemplateMode(false);
    setSelectedMapType(null);
  }, [templateGridSize, setNodes, broadcast, selectedMapType]);

  // 캔버스 클릭 이벤트 직접 처리
  useEffect(() => {
    const handleCanvasClick = (event) => {
      // ReactFlow의 pane 영역인지 확인
      const target = event.target;
      const isReactFlowPane = target.classList?.contains('react-flow__pane') ||
        target.closest('.react-flow__pane');

      if (!isReactFlowPane) {
        return;
      }

      console.log('캔버스 클릭 이벤트:', { addCardMode, addTemplateMode, toolMode, target });

      // 카드 추가 모드일 때
      if (addCardMode) {
        event.preventDefault();
        event.stopPropagation();
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        console.log('카드 추가 모드 - 클릭 위치:', position);
        addCardAtPosition(position);
        return;
      }

      // 템플릿 추가 모드일 때
      if (addTemplateMode) {
        event.preventDefault();
        event.stopPropagation();
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        console.log('템플릿 추가 모드 - 클릭 위치:', position);
        addTemplateAtPosition(position);
        return;
      }

      // 스티커 배치 모드
      if (toolMode === 'sticker' && pendingToolMode?.stickerData) {
        event.preventDefault();
        event.stopPropagation();
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        handleStickerPlace(position, pendingToolMode.stickerData);
        return;
      }

      // 도구 모드일 때 (사각형, 하트, 원형, 텍스트박스, 펜, 선은 드래그로 처리되므로 여기서는 제외)
      if (toolMode && toolMode !== 'rectangle' && toolMode !== 'pen' && toolMode !== 'heart' && toolMode !== 'circle' && toolMode !== 'textbox' && toolMode !== 'line' && toolMode !== 'sticker' && toolMode !== 'frame') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    };

    // 사각형, 하트, 원형, 텍스트박스, 펜, 선은 드래그로 처리되므로 클릭 이벤트 리스너에 포함하지 않음
    const needsClickHandler = addCardMode || addTemplateMode || (toolMode && toolMode !== 'rectangle' && toolMode !== 'pen' && toolMode !== 'heart' && toolMode !== 'circle' && toolMode !== 'textbox' && toolMode !== 'line' && toolMode !== 'sticker' && toolMode !== 'frame') || (toolMode === 'sticker' && pendingToolMode?.stickerData);

    if (needsClickHandler) {
      document.addEventListener('click', handleCanvasClick, true);
      return () => {
        document.removeEventListener('click', handleCanvasClick, true);
      };
    }
  }, [addCardMode, addTemplateMode, toolMode, screenToFlowPosition, addCardAtPosition, addTemplateAtPosition, addTextBoxAtPosition]);

  // 사각형 드래그를 위한 전역 마우스 이벤트
  useEffect(() => {
    if (toolMode !== 'rectangle') return;

    const handleMouseDown = (event) => {
      // ReactFlow pane 영역인지 확인
      const target = event.target;
      const isReactFlowPane = target.classList?.contains('react-flow__pane') ||
        target.closest('.react-flow__pane');

      if (!isReactFlowPane) return;

      // 이미 드래그 중이면 무시
      if (rectangleStart) return;

      event.preventDefault();
      event.stopPropagation();
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      console.log('사각형 드래그 시작 - 전역 마우스 이벤트:', position);
      startRectangleDrag(position);
    };

    const handleMouseMove = (event) => {
      if (rectangleStart && toolMode === 'rectangle') {
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        updateRectangleDrag(position);
      }
    };

    const handleMouseUp = () => {
      if (rectangleStart && toolMode === 'rectangle') {
        console.log('사각형 드래그 종료');
        finishRectangleDrag();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [toolMode, rectangleStart, screenToFlowPosition, startRectangleDrag, updateRectangleDrag, finishRectangleDrag]);


  // 원형 드래그를 위한 전역 마우스 이벤트
  useEffect(() => {
    if (toolMode !== 'circle') return;

    const handleMouseDown = (event) => {
      // ReactFlow pane 영역인지 확인
      const target = event.target;
      const isReactFlowPane = target.classList?.contains('react-flow__pane') ||
        target.closest('.react-flow__pane');

      if (!isReactFlowPane) return;

      // 이미 드래그 중이면 무시
      if (circleStart) return;

      event.preventDefault();
      event.stopPropagation();
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      console.log('원형 드래그 시작 - 전역 마우스 이벤트:', position);
      startCircleDrag(position);
    };

    const handleMouseMove = (event) => {
      if (circleStart && toolMode === 'circle') {
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        updateCircleDrag(position);
      }
    };

    const handleMouseUp = () => {
      if (circleStart && toolMode === 'circle') {
        console.log('원형 드래그 종료');
        finishCircleDrag();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [toolMode, circleStart, screenToFlowPosition, startCircleDrag, updateCircleDrag, finishCircleDrag]);

  // 프레임 드래그를 위한 전역 마우스 이벤트
  useEffect(() => {
    if (toolMode !== 'frame') return;

    const handleMouseDown = (event) => {
      const pane = document.querySelector('.react-flow__pane');
      if (!pane || !pane.contains(event.target)) return;
      if (frameStart) return;
      event.preventDefault();
      event.stopPropagation();
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      startFrameDrag(position);
    };

    const handleMouseMove = (event) => {
      if (frameStart && toolMode === 'frame') {
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        updateFrameDrag(position);
      }
    };

    const handleMouseUp = () => {
      if (frameStart && toolMode === 'frame') {
        finishFrameDrag();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [toolMode, frameStart, screenToFlowPosition, startFrameDrag, updateFrameDrag, finishFrameDrag]);

  // toolbar 드래그 핸들러
  const handleToolbarDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingToolbar(true);

    const toolbarElement = e.currentTarget.closest('[data-toolbar]');
    if (!toolbarElement) return;

    const rect = toolbarElement.getBoundingClientRect();

    // 현재 toolbar의 top, right 값 계산
    const currentTop = rect.top;
    const currentRight = window.innerWidth - rect.right;

    toolbarDragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      initialTop: currentTop,
      initialRight: currentRight
    };
  }, []);

  // toolbar 드래그 중
  useEffect(() => {
    if (!isDraggingToolbar) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - toolbarDragStartRef.current.x;
      const deltaY = e.clientY - toolbarDragStartRef.current.y;

      // 마우스를 위로 드래그하면 deltaY가 음수, 아래로 드래그하면 양수
      const newRight = toolbarDragStartRef.current.initialRight - deltaX;
      const newTop = toolbarDragStartRef.current.initialTop + deltaY;

      // 화면 밖으로 나가지 않도록 제한 (toolbar가 화면에 잘 보이도록)
      // toolbar의 실제 크기를 고려하여 범위 설정
      const toolbarElement = document.querySelector('[data-toolbar]');
      let toolbarWidth = 60; // 기본 너비 (세로 배열이므로 작은 값)
      let toolbarHeight = 400; // 기본 높이 (세로 배열이므로 큰 값)

      if (toolbarElement) {
        const rect = toolbarElement.getBoundingClientRect();
        toolbarWidth = rect.width;
        toolbarHeight = rect.height;
      }

      const maxRight = window.innerWidth - toolbarWidth - 12; // 오른쪽 여백 12px
      const maxTop = window.innerHeight - toolbarHeight - 12; // 아래쪽 여백 12px
      const minRight = 12; // 왼쪽 여백 12px
      const minTop = 12; // 위쪽 여백 12px

      setToolbarPosition({
        top: Math.max(minTop, Math.min(maxTop, newTop)),
        right: Math.max(minRight, Math.min(maxRight, newRight))
      });
    };

    const handleMouseUp = () => {
      setIsDraggingToolbar(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingToolbar]);

  // 앞으로 보내기 (맨 앞으로)
  const handleBringToFront = useCallback(() => {
    const currentNodes = nodesRef.current;
    // selected: true인 노드 찾기
    const selectedNodes = currentNodes.filter(n => n.selected);
    if (selectedNodes.length === 0) {
      return;
    }

    const selectedNodeIds = selectedNodes.map(n => n.id);

    // 레이어 작업 중 플래그 설정
    isLayerOperationRef.current = true;

    // 선택된 노드들을 맨 뒤로 이동 (맨 앞에 표시)
    const otherNodes = currentNodes.filter(n => !n.selected);
    const updatedNodes = [...otherNodes, ...selectedNodes].map((n, index) => {
      const wasSelected = selectedNodeIds.includes(n.id);
      return {
        ...n,
        selected: wasSelected, // 선택 상태 유지
        data: {
          ...n.data,
          displayOrder: index
        }
      };
    });

    // displayOrder가 변경된 노드 ID 수집 (업데이트 전 노드와 비교)
    const displayOrderChangedNodeIds = new Set();
    const oldNodesMap = new Map(currentNodes.map(n => [n.id, n.data?.displayOrder ?? 0]));
    updatedNodes.forEach(n => {
      const oldOrder = oldNodesMap.get(n.id) ?? 0;
      const newOrder = n.data?.displayOrder ?? 0;
      if (oldOrder !== newOrder) {
        displayOrderChangedNodeIds.add(n.id);
      }
    });

    // displayOrder에 따라 정렬하여 즉시 반영
    const sortedNodes = [...updatedNodes].sort((a, b) => {
      const orderA = a.data?.displayOrder ?? 0;
      const orderB = b.data?.displayOrder ?? 0;
      return orderA - orderB;
    });

    nodesRef.current = sortedNodes;

    // 선택 상태 유지하며 displayOrder 업데이트
    setNodes(sortedNodes);

    setTimeout(() => {
      const changedNodeIds = displayOrderChangedNodeIds.size > 0
        ? Array.from(displayOrderChangedNodeIds)
        : null;
      broadcast(sortedNodes, true, changedNodeIds);
      // 레이어 작업 완료 후 플래그 즉시 해제 (선택 변경이 가능하도록)
      setTimeout(() => {
        isLayerOperationRef.current = false;
        console.log('✅ 레이어 작업 플래그 해제 - 선택 변경 가능');
      }, 100);
    }, 0);
  }, [broadcast, setNodes]);

  // 뒤로 보내기 (맨 뒤로)
  const handleSendToBack = useCallback(() => {
    const currentNodes = nodesRef.current;
    // selected: true인 노드 찾기
    const selectedNodes = currentNodes.filter(n => n.selected);
    if (selectedNodes.length === 0) {
      return;
    }

    const selectedNodeIds = selectedNodes.map(n => n.id);

    // 레이어 작업 중 플래그 설정 (setNodes 호출 전에 설정)
    isLayerOperationRef.current = true;

    // 선택된 노드들을 맨 뒤로 이동 (맨 뒤에 표시)
    // displayOrder가 작을수록 뒤에 표시되므로, 선택된 노드들의 displayOrder를 최소값으로 설정
    const otherNodes = currentNodes.filter(n => !n.selected);

    // 현재 노드들의 displayOrder 최소값 찾기
    const minDisplayOrder = Math.min(
      ...currentNodes.map(n => n.data?.displayOrder ?? 0),
      0
    );

    // 선택된 노드들의 displayOrder를 최소값보다 작게 설정하여 맨 뒤로 보냄
    const updatedNodes = currentNodes.map((n) => {
      const wasSelected = selectedNodeIds.includes(n.id);
      const currentOrder = n.data?.displayOrder ?? 0;

      return {
        ...n,
        selected: wasSelected, // 선택 상태 유지
        data: {
          ...n.data,
          displayOrder: wasSelected ? minDisplayOrder - 1 : currentOrder
        }
      };
    });

    // displayOrder가 변경된 노드 ID 수집 (업데이트 전 노드와 비교)
    const displayOrderChangedNodeIds = new Set();
    const oldNodesMap = new Map(currentNodes.map(n => [n.id, n.data?.displayOrder ?? 0]));
    updatedNodes.forEach(n => {
      const oldOrder = oldNodesMap.get(n.id) ?? 0;
      const newOrder = n.data?.displayOrder ?? 0;
      if (oldOrder !== newOrder) {
        displayOrderChangedNodeIds.add(n.id);
      }
    });

    // displayOrder에 따라 정렬하여 즉시 반영
    const sortedNodes = [...updatedNodes].sort((a, b) => {
      const orderA = a.data?.displayOrder ?? 0;
      const orderB = b.data?.displayOrder ?? 0;
      return orderA - orderB;
    });

    nodesRef.current = sortedNodes;

    // 선택 상태 유지하며 displayOrder 업데이트 - 함수형 업데이트로 즉시 반영
    setNodes(() => sortedNodes);

    // 강제 리렌더링을 위해 약간의 딜레이 후 다시 설정하여 화면에 즉시 반영
    setTimeout(() => {
      setNodes((prevNodes) => {
        // displayOrder에 따라 다시 정렬하여 확실히 반영
        const reSorted = [...sortedNodes].sort((a, b) => {
          const orderA = a.data?.displayOrder ?? 0;
          const orderB = b.data?.displayOrder ?? 0;
          return orderA - orderB;
        });
        nodesRef.current = reSorted;
        return reSorted;
      });

      const changedNodeIds = displayOrderChangedNodeIds.size > 0
        ? Array.from(displayOrderChangedNodeIds)
        : null;
      broadcast(sortedNodes, true, changedNodeIds);
      // 레이어 작업 완료 후 플래그 즉시 해제 (선택 변경이 가능하도록)
      setTimeout(() => {
        isLayerOperationRef.current = false;
        console.log('✅ 레이어 작업 플래그 해제 - 선택 변경 가능');
      }, 100);
    }, 0);
  }, [broadcast, setNodes]);

  // toolbar 위치 조정: 버튼이 나타날 때 아래로 내려가도록
  const hasSelectedNodes = nodes.some(n => n.selected);
  const prevHasSelectedNodesRef = useRef(false);
  const toolbarBaseBottomRef = useRef(toolbarPosition.bottom);

  useEffect(() => {
    // 선택 상태가 변경될 때만 조정
    if (prevHasSelectedNodesRef.current === hasSelectedNodes) return;
    prevHasSelectedNodesRef.current = hasSelectedNodes;

    // 버튼 2개 높이 (약 80px)
    const buttonHeight = 80;

    if (hasSelectedNodes) {
      // 버튼이 나타날 때: base 위치에서 buttonHeight만큼 아래로
      const newBottom = toolbarBaseBottomRef.current - buttonHeight;
      const minBottom = 12;
      if (newBottom >= minBottom) {
        setToolbarPosition(prev => ({
          ...prev,
          bottom: newBottom
        }));
      }
    } else {
      // 버튼이 사라질 때: base 위치로 복원
      setToolbarPosition(prev => ({
        ...prev,
        bottom: toolbarBaseBottomRef.current
      }));
    }
  }, [hasSelectedNodes]);

  // toolbar 위치가 변경될 때 base 위치 업데이트 (드래그로 변경된 경우, 선택 해제 시)
  useEffect(() => {
    if (!hasSelectedNodes) {
      toolbarBaseBottomRef.current = toolbarPosition.bottom;
    }
  }, [toolbarPosition.bottom, hasSelectedNodes]);

  // 색상 선택기 드래그 핸들러
  const handleColorPickerDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingColorPicker(true);

    const colorPickerElement = e.currentTarget.closest('[data-color-picker]');
    if (!colorPickerElement) return;

    const rect = colorPickerElement.getBoundingClientRect();

    // 현재 색상 선택기의 top, right 값 계산
    const currentTop = rect.top;
    const currentRight = window.innerWidth - rect.right;

    colorPickerDragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      initialTop: currentTop,
      initialRight: currentRight
    };
  }, []);

  // 색상 선택기 드래그 중
  useEffect(() => {
    if (!isDraggingColorPicker) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - colorPickerDragStartRef.current.x;
      const deltaY = e.clientY - colorPickerDragStartRef.current.y;

      const newRight = colorPickerDragStartRef.current.initialRight - deltaX;
      const newTop = colorPickerDragStartRef.current.initialTop + deltaY;

      // 화면 밖으로 나가지 않도록 제한 (색상 선택기가 화면에 잘 보이도록)
      // 색상 선택기의 실제 크기를 고려하여 범위 설정
      const colorPickerElement = document.querySelector('[data-color-picker]');
      let colorPickerWidth = 250; // 기본 너비
      let colorPickerHeight = 400; // 기본 높이

      if (colorPickerElement) {
        const rect = colorPickerElement.getBoundingClientRect();
        colorPickerWidth = rect.width;
        colorPickerHeight = rect.height;
      }

      const maxRight = window.innerWidth - colorPickerWidth - 12; // 오른쪽 여백 12px
      const maxTop = window.innerHeight - colorPickerHeight - 12; // 아래쪽 여백 12px
      const minRight = 12; // 왼쪽 여백 12px
      const minTop = 12; // 위쪽 여백 12px

      setColorPickerPosition({
        top: Math.max(minTop, Math.min(maxTop, newTop)),
        right: Math.max(minRight, Math.min(maxRight, newRight))
      });
    };

    const handleMouseUp = () => {
      setIsDraggingColorPicker(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingColorPicker]);


  // 텍스트박스 드래그를 위한 전역 마우스 이벤트
  useEffect(() => {
    if (toolMode !== 'textbox') return;

    const handleMouseDown = (event) => {
      // ReactFlow pane 영역인지 확인
      const target = event.target;
      const isReactFlowPane = target.classList?.contains('react-flow__pane') ||
        target.closest('.react-flow__pane');

      if (!isReactFlowPane) return;

      // 이미 드래그 중이면 무시
      if (textBoxStart) return;

      event.preventDefault();
      event.stopPropagation();
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      console.log('텍스트박스 드래그 시작 - 전역 마우스 이벤트:', position);
      startTextBoxDrag(position);
    };

    const handleMouseMove = (event) => {
      if (textBoxStart && toolMode === 'textbox') {
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        updateTextBoxDrag(position);
      }
    };

    const handleMouseUp = () => {
      if (textBoxStart && toolMode === 'textbox') {
        console.log('텍스트박스 드래그 종료');
        finishTextBoxDrag();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [toolMode, textBoxStart, screenToFlowPosition, startTextBoxDrag, updateTextBoxDrag, finishTextBoxDrag]);

  // 선 드래그를 위한 전역 마우스 이벤트
  useEffect(() => {
    if (toolMode !== 'line') return;

    const handleMouseDown = (event) => {
      // ReactFlow pane 영역인지 확인
      const target = event.target;
      const isReactFlowPane = target.classList?.contains('react-flow__pane') ||
        target.closest('.react-flow__pane');

      if (!isReactFlowPane) return;

      // 이미 드래그 중이면 무시
      if (lineStart) return;

      event.preventDefault();
      event.stopPropagation();
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      console.log('선 드래그 시작 - 전역 마우스 이벤트:', position);
      startLineDrag(position);
    };

    const handleMouseMove = (event) => {
      if (lineStart && toolMode === 'line') {
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        updateLineDrag(position);
      }
    };

    const handleMouseUp = () => {
      if (lineStart && toolMode === 'line') {
        console.log('선 드래그 종료');
        finishLineDrag();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [toolMode, lineStart, screenToFlowPosition, startLineDrag, updateLineDrag, finishLineDrag]);



  // 드롭다운 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 사각형 버튼 클릭은 제외 (handleRectangleToolSelect에서 처리)
      const isShapeButton = event.target.closest('[data-shape-button]');
      // 스티커 버튼 클릭은 제외
      const isStickerButton = event.target.closest('[data-sticker-button]');
      // 스타일카드 버튼 클릭은 제외
      const isStyleCardButton = event.target.closest('[data-stylecard-button]');
      // 템플릿 버튼 클릭은 제외
      const isTemplateButton = event.target.closest('[data-template-button]');

      if (toolMenuRef.current && !toolMenuRef.current.contains(event.target)) {
        setShowToolMenu(false);
      }
      if (shapeMenuRef.current && !shapeMenuRef.current.contains(event.target) && !isShapeButton) {
        setShowShapeMenu(false);
      }
      if (stickerMenuRef.current && !stickerMenuRef.current.contains(event.target) && !isStickerButton) {
        setShowStickerModal(false);
        // 스티커 모달이 닫힐 때 toolMode도 초기화
        if (toolMode === 'sticker') {
          setToolMode(null);
          setPendingToolMode(null);
        }
      }
      if (styleCardMenuRef.current && !styleCardMenuRef.current.contains(event.target) && !isStyleCardButton) {
        setShowStyleCardMenu(false);
      }
      if (templateMenuRef.current && !templateMenuRef.current.contains(event.target) && !isTemplateButton) {
        setShowTemplateMenu(false);
      }
    };

    if (showToolMenu || showShapeMenu || showStickerModal || showStyleCardMenu || showTemplateMenu) {
      // 약간의 지연을 두어 버튼 클릭 이벤트가 먼저 처리되도록 함
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [showToolMenu, showShapeMenu, showStickerModal, showStyleCardMenu, showTemplateMenu, toolMode]);

  // 색상 팔레트 확인 핸들러 (확인 버튼과 외부 클릭 모두에서 사용)
  const handleColorPaletteConfirm = useCallback(() => {
    const targetTool = pendingToolMode || 'rectangle';

    // 같은 도구를 다시 선택하면 해제
    if (toolMode === targetTool) {
      console.log('🖊️ 색상 팔레트에서 같은 도구 선택 - 해제');
      setToolMode(null);
      setPendingToolMode(null);
      setShowColorPalette(false);
      setAddCardMode(false);
      setAddTemplateMode(false);
      setBoxSelectionMode(false);
      return;
    }

    setShowColorPalette(false);
    // pendingToolMode가 있으면 그것을 사용, 없으면 'rectangle' (기본값)
    setToolMode(targetTool);
    setPendingToolMode(null);
    setAddCardMode(false);
    setAddTemplateMode(false);
    setBoxSelectionMode(false);
  }, [pendingToolMode, toolMode]);

  // 색상 팔레트 외부 클릭 감지
  const colorPaletteRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      // colorPaletteRef 내부인지 확인
      const isInsidePalette = colorPaletteRef.current?.contains(target);

      // SketchPicker 관련 요소인지 확인 (react-color는 다양한 클래스명 사용)
      const isSketchPickerElement =
        target.closest('.sketch-picker') ||
        target.closest('[class*="sketch"]') ||
        target.closest('[class*="flexbox-fix"]') ||
        target.closest('[class*="react-color"]');

      // 팔레트 컨테이너나 SketchPicker 내부가 아닌 경우에만 확인 처리
      if (!isInsidePalette && !isSketchPickerElement) {
        handleColorPaletteConfirm();
      }
    };

    if (showColorPalette) {
      // 약간의 지연을 두어 버튼 클릭 이벤트가 먼저 처리되도록 함
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true); // capture phase 사용
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [showColorPalette, handleColorPaletteConfirm]);



  // 복사/붙여넣기 기능 (다른 시트/보드 간 복사 가능)
  const handleCopy = useCallback(() => {
    // 최신 nodes/edges 상태 사용
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;
    const selectedNodes = currentNodes.filter(node => node.selected);
    const selectedEdges = currentEdges.filter(edge => edge.selected);
    console.log('복사 시도 - 선택된 노드 수:', selectedNodes.length, '선택된 Edge 수:', selectedEdges.length);

    let copiedNodes = [];
    let copiedEdges = [];

    // 노드 복사
    if (selectedNodes.length > 0) {
      copiedNodes = selectedNodes.map(node => {
        // 모든 노드 타입(사각형, 텍스트박스 등)의 속성 포함
        const copiedNode = {
          ...node,
          data: node.data ? JSON.parse(JSON.stringify(node.data)) : {} // 깊은 복사
        };
        // 노드 레벨의 width, height도 복사 (사각형, 텍스트박스 등)
        if (node.width !== undefined) copiedNode.width = node.width;
        if (node.height !== undefined) copiedNode.height = node.height;
        return copiedNode;
      });
      console.log('복사 성공 - 복사된 노드:', copiedNodes.map(n => ({ id: n.id, type: n.type, selected: n.selected, data: n.data })));
    }

    // Edge 복사 (선택된 노드들 간의 연결만 복사)
    if (selectedEdges.length > 0) {
      const selectedNodeIds = new Set(selectedNodes.map(n => n.id));
      // 선택된 노드들 간의 연결만 복사
      copiedEdges = selectedEdges
        .filter(edge => selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target))
        .map(edge => ({
          ...edge,
          data: edge.data ? JSON.parse(JSON.stringify(edge.data)) : {} // 깊은 복사
        }));
      console.log('복사 성공 - 복사된 Edge:', copiedEdges.map(e => ({ id: e.id, source: e.source, target: e.target })));
    }

    if (copiedNodes.length === 0 && copiedEdges.length === 0) {
      console.log('복사 실패 - 선택된 노드나 Edge가 없습니다');
      return;
    }

    // localStorage에 저장하여 다른 시트/보드에서도 사용 가능하게 함
    const clipboardData = {
      nodes: copiedNodes,
      edges: copiedEdges,
      timestamp: Date.now(),
      sourceTabId: tabId,
      sourceFileId: fileId,
      sourceCanvasId: activeCanvasId
    };

    try {
      localStorage.setItem('reactflow_clipboard', JSON.stringify(clipboardData));
      // 기존 ref에도 저장 (하위 호환성)
      copiedNodesRef.current = copiedNodes;
      copiedEdgesRef.current = copiedEdges;

      // 시스템 클립보드에도 복사 (타임스탬프 포함)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        const clipboardText = JSON.stringify({
          type: 'reactflow-nodes',
          data: clipboardData,
          timestamp: clipboardData.timestamp
        });
        navigator.clipboard.writeText(clipboardText).catch(err => {
          console.warn('시스템 클립보드 복사 실패 (권한 필요):', err);
        });
      }

      console.log('클립보드에 저장 완료 - 다른 시트/보드에서도 붙여넣기 가능');
    } catch (error) {
      console.error('클립보드 저장 실패:', error);
      // localStorage 실패 시 ref에만 저장
      copiedNodesRef.current = copiedNodes;
      copiedEdgesRef.current = copiedEdges;
    }
  }, [tabId, fileId, activeCanvasId]);

  const handlePaste = useCallback(() => {
    console.log('🔵 handlePaste 함수 호출됨');
    // localStorage에서 클립보드 데이터 읽기 (다른 시트/보드에서 복사한 데이터 포함)
    let clipboardNodes = [];
    let clipboardEdges = [];

    try {
      const clipboardData = localStorage.getItem('reactflow_clipboard');
      console.log('🔵 localStorage에서 읽은 데이터:', clipboardData ? '있음' : '없음');
      if (clipboardData) {
        const parsed = JSON.parse(clipboardData);
        clipboardNodes = parsed.nodes || [];
        clipboardEdges = parsed.edges || [];
        console.log('🔵 파싱된 데이터 - 노드:', clipboardNodes.length, 'Edge:', clipboardEdges.length);

        // 클립보드 출처 정보 로그
        if (parsed.sourceTabId !== tabId || parsed.sourceFileId !== fileId || parsed.sourceCanvasId !== activeCanvasId) {
          console.log('다른 위치에서 복사된 노드 붙여넣기:', {
            from: { tabId: parsed.sourceTabId, fileId: parsed.sourceFileId, canvasId: parsed.sourceCanvasId },
            to: { tabId, fileId, canvasId: activeCanvasId }
          });
        }
      }
    } catch (error) {
      console.error('클립보드 읽기 실패, ref 사용:', error);
    }

    // localStorage 읽기 실패 시 기존 ref 사용 (하위 호환성)
    if (clipboardNodes.length === 0 && clipboardEdges.length === 0) {
      console.log('🔵 localStorage에 데이터 없음 - ref에서 읽기 시도');
      clipboardNodes = copiedNodesRef.current;
      clipboardEdges = copiedEdgesRef.current;
      console.log('🔵 ref에서 읽은 데이터 - 노드:', clipboardNodes.length, 'Edge:', clipboardEdges.length);
    }

    console.log('붙여넣기 시도 - 복사된 노드 수:', clipboardNodes.length, '복사된 Edge 수:', clipboardEdges.length);

    if (clipboardNodes.length === 0 && clipboardEdges.length === 0) {
      console.log('붙여넣기 실패 - 복사된 노드나 Edge가 없습니다');
      return;
    }

    const viewport = getViewport();
    const offsetX = 50; // 붙여넣기 시 오프셋
    const offsetY = 50;

    // 노드 ID 매핑 (원본 ID -> 새 ID)
    const nodeIdMap = new Map();

    // 노드 붙여넣기
    const newNodes = clipboardNodes.map((node, index) => {
      const newId = uuidv4();
      nodeIdMap.set(node.id, newId);

      // 위치를 격자에 맞춤
      const newPosition = snapToGrid({
        x: node.position.x + offsetX,
        y: node.position.y + offsetY
      });

      // 마지막 노드인지 확인 (마지막 노드에만 이름 표시)
      const isLastNode = index === clipboardNodes.length - 1;

      // 모든 노드 타입(사각형, 텍스트박스 등)의 속성 복사
      const newNode = {
        ...node,
        id: newId,
        selected: true, // 새로 붙여넣은 노드는 선택 상태로
        position: newPosition,
        data: {
          ...(node.data ? JSON.parse(JSON.stringify(node.data)) : {}), // 깊은 복사
          selectedBy: currentUser, // 현재 사용자로 설정
          showUserLabel: isLastNode && !isPersonalRef.current // 마지막 노드에만 이름 표시 (개인 보드에서는 숨김)
        }
      };

      // 노드 레벨의 width, height도 복사 (사각형, 텍스트박스 등)
      if (node.width !== undefined) newNode.width = node.width;
      if (node.height !== undefined) newNode.height = node.height;

      // Line 노드의 경우 markerEndId, markerStartId 재생성
      if (node.type === 'line' && newNode.data) {
        newNode.data.id = newId; // Line 노드의 data.id 업데이트 (화살표 marker용)
      }

      return newNode;
    });

    // Edge 붙여넣기
    const newEdges = clipboardEdges
      .filter(edge => {
        // 원본 노드 ID가 nodeIdMap에 있는지 확인 (복사된 노드들 간의 연결만 붙여넣기)
        return nodeIdMap.has(edge.source) && nodeIdMap.has(edge.target);
      })
      .map(edge => {
        const newId = uuidv4();
        return {
          ...edge,
          id: newId,
          source: nodeIdMap.get(edge.source), // 새 노드 ID로 매핑
          target: nodeIdMap.get(edge.target), // 새 노드 ID로 매핑
          selected: false,
          data: edge.data ? JSON.parse(JSON.stringify(edge.data)) : {} // 깊은 복사
        };
      });

    const newNodesIds = new Set(newNodes.map(n => n.id));
    console.log('붙여넣기 성공 - 새 노드:', newNodes.map(n => ({ id: n.id, type: n.type })), '새 Edge:', newEdges.map(e => ({ id: e.id, source: e.source, target: e.target })));
    console.log('🔵 setNodes 호출 전 - newNodes 개수:', newNodes.length, 'newEdges 개수:', newEdges.length);

    // 붙여넣기 후 clipboard 노드의 위치를 새 위치로 업데이트 (다음 붙여넣기 시 offset이 누적되도록)
    const updatedClipboardNodes = clipboardNodes.map(node => ({
      ...node,
      position: {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY
      }
    }));
    
    // copiedNodesRef와 localStorage 모두 업데이트
    copiedNodesRef.current = updatedClipboardNodes;
    try {
      const existingClipboard = localStorage.getItem('reactflow_clipboard');
      if (existingClipboard) {
        const clipboardData = JSON.parse(existingClipboard);
        clipboardData.nodes = updatedClipboardNodes;
        localStorage.setItem('reactflow_clipboard', JSON.stringify(clipboardData));
        console.log('📋 클립보드 위치 업데이트 완료 - 다음 붙여넣기 위치:', updatedClipboardNodes[0]?.position);
      }
    } catch (e) {
      console.warn('클립보드 위치 업데이트 실패:', e);
    }

    // 기존 선택된 노드/엣지 선택 해제
    setSelectedNodeId(null);
    setSelectedEdgeId(null);

    // 붙여넣기 플래그 설정 (onNodesChangeSync에서 broadcast 호출하지 않도록)
    isPasteOperationRef.current = true;
    const newNodeIds = newNodes.map(n => n.id);

    // 기존에 선택된 노드들의 ID 저장 (다른 브라우저로 selectedBy 해제 전달용)
    const previouslySelectedNodeIds = nodesRef.current
      .filter(n => n.data?.selectedBy || n.data?.showUserLabel)
      .map(n => n.id);

    // 선택 순서 업데이트 (기존 선택 초기화 후 새 노드들 추가)
    selectionOrderRef.current = [...newNodeIds];

    setNodes((nds) => {
      console.log('🔵 setNodes 내부 - 기존 노드 수:', nds.length, '추가할 노드 수:', newNodes.length);
      const updatedNodes = [...nds, ...newNodes].map((node, index) => {
        const isNewNode = newNodesIds.has(node.id);
        return {
          ...node,
          selected: isNewNode, // 새 노드는 선택, 기존 노드는 선택 해제
          data: {
            ...node.data,
            displayOrder: index, // 전체 배열 재정렬
            // 기존 노드는 selectedBy, showUserLabel 제거 (새 노드만 유지)
            selectedBy: isNewNode ? node.data?.selectedBy : null,
            showUserLabel: isNewNode ? node.data?.showUserLabel : false
          }
        };
      });
      nodesRef.current = updatedNodes;
      return updatedNodes;
    });

    // Edge 붙여넣기 및 선택 해제
    setEdges((eds) => {
      const updatedEdges = [...eds.map(edge => ({
        ...edge,
        selected: false // 기존 엣지 모두 선택 해제
      })), ...newEdges]; // 새 엣지 추가
      edgesRef.current = updatedEdges;
      return updatedEdges;
    });

    // broadcast 호출 (setNodes와 setEdges 이후에 호출)
    // 새 노드 ID + 기존 선택 해제된 노드 ID 모두 전달 (다른 브라우저에서 유저 라벨 해제되도록)
    setTimeout(() => {
      const allChangedNodeIds = [...newNodeIds, ...previouslySelectedNodeIds];
      console.log('📋 붙여넣기 broadcast - 새 노드:', newNodeIds.length, '선택 해제된 노드:', previouslySelectedNodeIds.length);
      broadcast(nodesRef.current, true, allChangedNodeIds, true);
      isPasteOperationRef.current = false;
    }, 0);

    // 새로 선택된 노드의 색상 박스 업데이트
    setTimeout(() => {
      if (newNodes.length === 1) {
        const newNode = newNodes[0];
        // Line 노드인 경우 selectedNodeId 업데이트
        if (newNode.type === 'line') {
          setSelectedNodeId(newNode.id);
          setShowNodeColorPicker(true);
          setNodeColorType('stroke');
          setSelectedColor({
            fill: '#000000',
            stroke: newNode.data?.stroke || '#000000',
            fillOpacity: 1,
            strokeOpacity: newNode.data?.strokeOpacity || 1
          });
          setNodeStrokeWidth(newNode.data?.strokeWidth || 2);
        }
        // Rectangle, Circle, Textbox, Heart 등 다른 노드 타입
        else if (newNode.type === 'rectangle' || newNode.type === 'circle' || newNode.type === 'textbox' || newNode.type === 'heart') {
          setSelectedNodeId(newNode.id);
          setShowNodeColorPicker(true);
          setNodeColorType('fill');
          setSelectedColor({
            fill: newNode.data?.fill || '#ffffff',
            stroke: newNode.data?.stroke || '#000000',
            fillOpacity: newNode.data?.fillOpacity || 1,
            strokeOpacity: newNode.data?.strokeOpacity || 1
          });
          setNodeStrokeWidth(newNode.data?.strokeWidth || 1);
        }
      }
    }, 0);
  }, [setNodes, setEdges, broadcast, getViewport, snapToGrid, setSelectedNodeId, setSelectedEdgeId, setShowNodeColorPicker, setNodeColorType, setSelectedColor, setNodeStrokeWidth, tabId, fileId, activeCanvasId, currentUser]);


  // 선택된 노드/Edge 삭제 기능
  const handleDelete = useCallback(() => {
    const selectedNodeIds = nodesRef.current
      .filter(node => node.selected)
      .map(node => node.id);

    const selectedEdgeIds = edgesRef.current
      .filter(edge => edge.selected)
      .map(edge => edge.id);

    if (selectedNodeIds.length === 0 && selectedEdgeIds.length === 0) return;

    // 삭제된 노드가 현재 선택된 노드라면 색상 선택 박스 닫기
    if (selectedNodeId && selectedNodeIds.includes(selectedNodeId)) {
      setShowNodeColorPicker(false);
      setSelectedNodeId(null);
    }

    // 삭제된 Edge가 현재 선택된 Edge라면 색상 선택 박스 닫기
    if (selectedEdgeId && selectedEdgeIds.includes(selectedEdgeId)) {
      setShowNodeColorPicker(false);
      setSelectedEdgeId(null);
    }

    // 노드와 엣지 동시에 업데이트
    const updatedNodes = nodesRef.current
      .filter(node => !selectedNodeIds.includes(node.id))
      .map((node, index) => ({
        ...node,
        data: {
          ...node.data,
          displayOrder: index // 삭제 후 순서 재정렬
        }
      }));

    const filteredEdges = edgesRef.current.filter(
      edge => !selectedEdgeIds.includes(edge.id) &&
        !selectedNodeIds.includes(edge.source) &&
        !selectedNodeIds.includes(edge.target)
    );

    setNodes(updatedNodes);
    setEdges(filteredEdges);

    // ref 업데이트
    nodesRef.current = updatedNodes;
    edgesRef.current = filteredEdges;

    // 한 번만 브로드캐스트 (노드 삭제는 전체 노드 동기화 필요, 삭제된 노드 ID 포함)
    console.log('🗑️ handleDelete - broadcast 호출:', {
      updatedNodesCount: updatedNodes.length,
      selectedNodeIds,
      selectedEdgeIds
    });
    broadcast(updatedNodes, true, null, true, selectedNodeIds);
  }, [setNodes, setEdges, broadcast, selectedNodeId, selectedEdgeId]);



  // 키보드 이벤트 리스너
  useEffect(() => {
    const handleKeyDown = (event) => {
      // input, textarea 등 입력 필드가 포커스되어 있으면 이벤트를 막지 않음
      const target = event.target;
      const isInputField = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('.ql-editor') || // ReactQuill 에디터
        target.closest('.ql-toolbar'); // ReactQuill 툴바

      if (isInputField) {
        return; // 입력 필드에서는 기본 동작 허용
      }

      // Ctrl+C 또는 Cmd+C (Mac)
      if ((event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'C')) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Ctrl+C 감지');
        handleCopy();
        return false;
      }

      // Ctrl+Z 또는 Cmd+Z (Undo)
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && (event.key === 'z' || event.key === 'Z')) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Ctrl+Z 감지 - Undo');
        handleUndo();
        return false;
      }

      // Ctrl+Y 또는 Ctrl+Shift+Z 또는 Cmd+Shift+Z (Redo)
      if (
        ((event.ctrlKey || event.metaKey) && (event.key === 'y' || event.key === 'Y')) ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'z' || event.key === 'Z'))
      ) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Ctrl+Y 감지 - Redo');
        handleRedo();
        return false;
      }

      // Ctrl+V 또는 Cmd+V (Mac) - paste 이벤트를 허용하여 handleClipboardPaste에서 이미지/노드 모두 처리
      // handleClipboardPaste에서 이미지와 노드 중 더 최신 것을 선택하므로 여기서는 이벤트를 막지 않음
      if ((event.ctrlKey || event.metaKey) && (event.key === 'v' || event.key === 'V')) {
        console.log('Ctrl+V 키보드 이벤트 감지 - paste 이벤트 허용 (handleClipboardPaste에서 이미지/노드 처리)');
        // paste 이벤트가 발생하도록 허용 (handleClipboardPaste에서 이미지와 노드 중 더 최신 것을 선택)
        // event.preventDefault()를 호출하지 않음
      }

      // Delete 또는 Backspace 키로 선택된 노드 삭제
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        handleDelete();
      }

      // 방향키 처리
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();

        const selectedNodes = nodesRef.current.filter(n => n.selected);

        if (selectedNodes.length > 0) {
          // 노드가 선택된 경우: 선택된 노드들을 이동
          const moveDistance = gridSize; // 그리드 크기만큼 이동

          setNodes((nds) => {
            const updatedNodes = nds.map(node => {
              if (node.selected) {
                let newX = node.position.x;
                let newY = node.position.y;

                if (event.key === 'ArrowUp') newY -= moveDistance;
                if (event.key === 'ArrowDown') newY += moveDistance;
                if (event.key === 'ArrowLeft') newX -= moveDistance;
                if (event.key === 'ArrowRight') newX += moveDistance;

                return {
                  ...node,
                  position: { x: newX, y: newY }
                };
              }
              return node;
            });
            nodesRef.current = updatedNodes;
            return updatedNodes;
          });

          // 노드 이동 후 서버로 전송
          setTimeout(() => {
            const selectedNodeIds = selectedNodes.map(n => n.id);
            broadcast(nodesRef.current, true, selectedNodeIds);
          }, 50);
        } else {
          // 노드가 선택되지 않은 경우: 화면 이동
          const viewport = getViewport();
          const panSpeed = 50; // 이동 속도 (픽셀)

          let newX = viewport.x;
          let newY = viewport.y;

          if (event.key === 'ArrowUp') newY += panSpeed;
          if (event.key === 'ArrowDown') newY -= panSpeed;
          if (event.key === 'ArrowLeft') newX += panSpeed;
          if (event.key === 'ArrowRight') newX -= panSpeed;

          setViewportRef.current({ x: newX, y: newY, zoom: viewport.zoom });
        }
      }
    };

    // capture phase에서 이벤트 리스너 등록 (ReactFlow보다 먼저 처리)
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleCopy, handleDelete, handleUndo, handleRedo, handlePaste, addCardMode, addTemplateMode, boxSelectionMode, showColorPalette, showLineSettingsModal, showStickerUploadModal, uploadingSticker, toolMode]);

  // 클립보드 붙여넣기 이벤트 리스너 (이미지 붙여넣기 및 노드 붙여넣기)
  useEffect(() => {
    // localStorage에 복사된 노드가 있는지 확인하는 헬퍼 함수
    const hasClipboardData = () => {
      if (copiedNodesRef.current.length > 0) return true;
      try {
        const clipboardData = localStorage.getItem('reactflow_clipboard');
        if (clipboardData) {
          const parsed = JSON.parse(clipboardData);
          return (parsed.nodes && parsed.nodes.length > 0) || (parsed.edges && parsed.edges.length > 0);
        }
      } catch (error) {
        console.error('클립보드 확인 실패:', error);
      }
      return false;
    };

    const handleClipboardPaste = (event) => {
      console.log('🟢 paste 이벤트 감지됨', event.target);
      // input, textarea 등 입력 필드가 포커스되어 있으면 이벤트를 막지 않음
      const target = event.target;
      const isInputField = target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('.ql-editor') || // ReactQuill 에디터
        target.closest('.ql-toolbar'); // ReactQuill 툴바

      if (isInputField) {
        console.log('🟢 입력 필드에서 paste 이벤트 - 기본 동작 허용');
        return; // 입력 필드에서는 기본 동작 허용
      }

      // 스티커 업로드 모달이 열려있으면 여기서 처리하지 않음 (스티커 붙여넣기 핸들러가 처리)
      if (showStickerUploadModal) {
        return;
      }

      const items = event.clipboardData?.items;
      console.log('🟢 클립보드 데이터 확인 시작');
      console.log('🟢 clipboardData.items:', items ? `있음 (${items.length}개)` : '없음');

      // localStorage에서 노드 데이터 확인
      console.log('📋 노드 데이터 확인 시작');
      let nodeDataTimestamp = 0;
      const hasNodeData = hasClipboardData();
      console.log('📋 hasClipboardData() 결과:', hasNodeData);
      console.log('📋 copiedNodesRef.current.length:', copiedNodesRef.current.length);

      if (hasNodeData) {
        try {
          const clipboardData = localStorage.getItem('reactflow_clipboard');
          console.log('📋 localStorage에서 읽은 데이터:', clipboardData ? '있음' : '없음');
          if (clipboardData) {
            const parsed = JSON.parse(clipboardData);
            console.log('📋 파싱된 노드 데이터:', {
              nodes: parsed.nodes ? parsed.nodes.length : 0,
              edges: parsed.edges ? parsed.edges.length : 0,
              timestamp: parsed.timestamp,
              sourceTabId: parsed.sourceTabId,
              sourceFileId: parsed.sourceFileId,
              sourceCanvasId: parsed.sourceCanvasId
            });
            nodeDataTimestamp = parsed.timestamp || 0;
            console.log('📋 노드 데이터 타임스탬프:', nodeDataTimestamp, new Date(nodeDataTimestamp).toISOString());
          }
        } catch (error) {
          console.error('📋 노드 데이터 타임스탬프 확인 실패:', error);
        }
      } else {
        console.log('📋 노드 데이터 없음');
      }

      if (!items) {
        // 클립보드 아이템이 없으면 노드 붙여넣기 시도
        if (hasNodeData) {
          console.log('📋 클립보드 아이템 없음 - 노드 붙여넣기 시도');
          event.preventDefault();
          event.stopPropagation();
          handlePaste();
        } else {
          console.log('📋 클립보드 아이템 없음 - 노드 데이터도 없음');
        }
        return;
      }

      // 클립보드에 이미지가 있는지 확인
      console.log('🖼️ 이미지 데이터 확인 시작');
      let hasImage = false;
      let imageTimestamp = Date.now(); // 이미지는 현재 시간 (복사한 시점)
      console.log('🖼️ 이미지 타임스탬프 (현재 시간):', imageTimestamp, new Date(imageTimestamp).toISOString());

      if (items && items.length > 0) {
        console.log('🖼️ 클립보드 아이템 확인 시작, items.length:', items.length);
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          console.log('🖼️ 클립보드 아이템:', i, 'type:', item.type, 'kind:', item.kind);

          // 이미지 타입 확인 (더 포괄적으로)
          const isFile = item.kind === 'file';
          const isImageType = item.type.indexOf('image') !== -1 ||
            item.type === '' || // Windows 스크린샷의 경우 type이 빈 문자열일 수 있음
            item.type.startsWith('image/');

          console.log('🖼️ 아이템 분석:', {
            index: i,
            kind: item.kind,
            type: item.type,
            isFile: isFile,
            isImageType: isImageType,
            조건만족: isFile && isImageType
          });

          if (isFile && isImageType) {
            hasImage = true;
            console.log('🖼️ ✅ 이미지 발견! type:', item.type, 'kind:', item.kind);
            break;
          }
        }
      } else {
        console.log('🖼️ 클립보드 아이템 없음');
      }

      console.log('🖼️ 이미지 감지 최종 결과:', hasImage);
      console.log('📊 전체 확인 결과:', {
        hasNodeData: hasNodeData,
        nodeDataTimestamp: nodeDataTimestamp,
        nodeDataTime: nodeDataTimestamp ? new Date(nodeDataTimestamp).toISOString() : '없음',
        hasImage: hasImage,
        imageTimestamp: imageTimestamp,
        imageTime: new Date(imageTimestamp).toISOString()
      });

      // 최신 복사본 확인: 노드와 이미지 중 더 최신 것을 붙여넣기
      console.log('📊 붙여넣기 결정 로직 시작');
      console.log('📊 조건 확인:', {
        'hasNodeData && hasImage': hasNodeData && hasImage,
        'hasNodeData만': hasNodeData && !hasImage,
        'hasImage만': !hasNodeData && hasImage,
        '둘 다 없음': !hasNodeData && !hasImage
      });

      if (hasNodeData && hasImage) {
        // 둘 다 있으면 타임스탬프 비교
        console.log('📊 노드와 이미지 둘 다 있음 - 타임스탬프 비교');
        console.log('📊 타임스탬프 비교:', {
          nodeDataTimestamp: nodeDataTimestamp,
          nodeDataTime: new Date(nodeDataTimestamp).toISOString(),
          imageTimestamp: imageTimestamp,
          imageTime: new Date(imageTimestamp).toISOString(),
          차이: nodeDataTimestamp - imageTimestamp,
          '노드가 더 최신?': nodeDataTimestamp > imageTimestamp
        });

        if (nodeDataTimestamp > imageTimestamp) {
          console.log('✅ 결정: 노드가 더 최신 - 노드 붙여넣기');
          console.log('📋 노드 타임스탬프:', new Date(nodeDataTimestamp).toISOString());
          console.log('🖼️ 이미지 타임스탬프:', new Date(imageTimestamp).toISOString());
          event.preventDefault();
          event.stopPropagation();
          handlePaste();
          return;
        } else {
          console.log('✅ 결정: 이미지가 더 최신 - 이미지 붙여넣기');
          console.log('📋 노드 타임스탬프:', new Date(nodeDataTimestamp).toISOString());
          console.log('🖼️ 이미지 타임스탬프:', new Date(imageTimestamp).toISOString());
          // 이미지 붙여넣기 계속 진행
        }
      } else if (hasNodeData) {
        // 노드만 있으면 노드 붙여넣기
        console.log('✅ 결정: 노드만 있음 - 노드 붙여넣기');
        console.log('📋 노드 데이터:', {
          nodes: copiedNodesRef.current.length,
          timestamp: nodeDataTimestamp
        });
        event.preventDefault();
        event.stopPropagation();
        handlePaste();
        return;
      } else if (!hasImage) {
        // 둘 다 없으면 아무것도 안 함
        console.log('✅ 결정: 노드 데이터도 없고 이미지도 없음 - 종료');
        return;
      }

      // 이미지만 있거나 이미지가 더 최신인 경우
      if (hasImage) {
        console.log('✅ 결정: 이미지 붙여넣기 진행');
      }

      // 이미지 붙여넣기 처리
      if (hasImage) {
        console.log('🖼️ 이미지 붙여넣기 시작');
        event.preventDefault();
        event.stopPropagation();

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          console.log('🖼️ 아이템 확인:', i, 'type:', item.type, 'kind:', item.kind);

          // 파일 타입 아이템 확인 (이미지일 가능성)
          if (item.kind === 'file') {
            const blob = item.getAsFile();
            if (!blob) {
              console.log('🖼️ blob이 null입니다');
              continue;
            }

            // blob의 실제 타입 확인 (Windows 스크린샷의 경우 type이 빈 문자열일 수 있음)
            const blobType = blob.type || '';
            const isImageType = blobType.indexOf('image') !== -1 ||
              blobType.startsWith('image/') ||
              blobType === ''; // 타입이 없어도 파일이면 시도

            if (isImageType || blobType === '') {
              console.log('🖼️ 이미지 감지:', blobType || 'unknown (스크린샷 가능성)', blob.size, 'bytes');

              // 이미지를 Base64로 변환
              const reader = new FileReader();
              reader.onerror = (error) => {
                console.error('🖼️ FileReader 오류:', error);
              };
              reader.onload = (e) => {
                const imageUrl = e.target.result;
                console.log('🖼️ FileReader 완료, 이미지 URL 길이:', imageUrl.length);

                // 이미지 객체를 생성하여 원본 크기 가져오기
                const img = new window.Image();
                img.onerror = (error) => {
                  console.error('🖼️ 이미지 로드 실패:', error);
                };
                img.onload = () => {
                  console.log('🖼️ 이미지 로드 성공, 크기:', img.width, 'x', img.height);
                  const originalWidth = img.width;
                  const originalHeight = img.height;

                  // 원본 크기 그대로 사용
                  let nodeWidth = originalWidth;
                  let nodeHeight = originalHeight;

                  // 현재 뷰포트 중앙에 이미지 노드 생성
                  const viewport = getViewport();
                  const centerX = (-viewport.x + window.innerWidth / 2) / viewport.zoom;
                  const centerY = (-viewport.y + window.innerHeight / 2) / viewport.zoom;

                  const position = snapToGrid({
                    x: centerX - nodeWidth / 2,
                    y: centerY - nodeHeight / 2
                  });

                  // 새 이미지 노드 생성
                  const newImageNode = {
                    id: uuidv4(),
                    type: 'image',
                    position: position,
                    data: {
                      imageUrl: imageUrl,
                      imageName: `이미지_${new Date().toLocaleTimeString()}`,
                      displayOrder: nodesRef.current.length,
                      width: nodeWidth,  // data에도 크기 저장
                      height: nodeHeight
                    },
                    width: nodeWidth,
                    height: nodeHeight,
                    selected: true
                  };

                  console.log('🖼️ 이미지 붙여넣기 완료:', {
                    id: newImageNode.id,
                    원본크기: `${originalWidth}x${originalHeight}`,
                    노드크기: `${Math.round(nodeWidth)}x${Math.round(nodeHeight)}`
                  });

                  setNodes((nds) => {
                    const updatedNodes = [...nds.map(n => ({ ...n, selected: false })), newImageNode];
                    nodesRef.current = updatedNodes;
                    broadcast(updatedNodes, true, [newImageNode.id], true);
                    return updatedNodes;
                  });
                };

                img.src = imageUrl;
              };

              reader.readAsDataURL(blob);
              break; // 첫 번째 이미지만 처리
            } // if (isImageType || blobType === '') 닫기
          } // if (item.kind === 'file') 닫기
        } // for 루프 닫기
      } // if (hasImage) 닫기

      // 이미지 처리 완료 (이미지가 없으면 위에서 노드 붙여넣기 처리됨)
    };

    // capture phase에서 이벤트 리스너 등록 (ReactFlow보다 먼저 처리)
    document.addEventListener('paste', handleClipboardPaste, true);
    return () => {
      document.removeEventListener('paste', handleClipboardPaste, true);
    };
  }, [setNodes, broadcast, getViewport, snapToGrid, handlePaste, showStickerUploadModal]);


  // nodes/edges 변동 → 서버로 emit (debounce + 최신 상태 사용)
  const onNodesChangeSync = useCallback(
    (changes) => {
      // 외부 업데이트 중이면 처리하지 않음 (초기 로드 시 불필요한 히스토리 저장 방지)
      if (isExternalUpdateRef.current) {
        const updatedNodes = applyNodeChanges(changes, nodesRef.current);
        nodesRef.current = updatedNodes;
        setNodes(updatedNodes);
        return;
      }

      // 초기 로드 중: select 무시, dimension은 배치 처리
      if (isInitialLoadRef.current) {
        const types = changes.map(c => c.type);
        console.log(`⏱️ [초기로드] onNodesChange 호출: [${[...new Set(types)].join(',')}] x${changes.length}`);
        const dimChanges = changes.filter(c => c.type === 'dimensions');
        if (dimChanges.length > 0) {
          const updatedNodes = applyNodeChanges(dimChanges, nodesRef.current);
          nodesRef.current = updatedNodes;
          pendingDimChangesRef.current.push(...dimChanges);
          if (dimBatchTimerRef.current) clearTimeout(dimBatchTimerRef.current);
          dimBatchTimerRef.current = setTimeout(() => {
            if (pendingDimChangesRef.current.length > 0) {
              onNodesChange(pendingDimChangesRef.current);
              pendingDimChangesRef.current = [];
            }
            isInitialLoadRef.current = false;
            dimBatchTimerRef.current = null;
          }, 300);
        }
        return;
      }

      // 노드 삭제 감지 - 삭제된 노드가 현재 선택된 노드라면 색상 선택 박스 닫기
      const removedNodeIds = changes
        .filter(change => change.type === 'remove')
        .map(change => change.id);

      if (removedNodeIds.length > 0 && selectedNodeId && removedNodeIds.includes(selectedNodeId)) {
        setShowNodeColorPicker(false);
        setSelectedNodeId(null);
      }

      // 위치 변경 시 격자에 맞추기
      const snappedChanges = changes.map(change => {
        if (change.type === 'position' && change.position) {
          return {
            ...change,
            position: snapToGrid(change.position)
          };
        }
        return change;
      });

      // changes를 적용한 최신 상태를 계산
      let updatedNodes = applyNodeChanges(snappedChanges, nodesRef.current);

      // 리사이즈 이벤트 확인 (dimensions 변경)
      const resizeChanges = snappedChanges.filter(change => change.type === 'dimensions');
      const hasPositionChange = snappedChanges.some(change => change.type === 'position' && change.position);
      const hasResize = resizeChanges.length > 0;

      // 드래그 시작/종료 감지 (position change와 select 이벤트 모두 확인)
      const positionChanges = snappedChanges.filter(change => change.type === 'position' && change.position);
      const selectChanges = snappedChanges.filter(change => change.type === 'select');
      let isDraggingEnd = false;

      // ⚡ 노드 선택 변경 시 커서를 선택된 노드의 중심에 고정
      if (selectChanges.length > 0 && yjsUpdateCursor && !isPersonalRef.current) {
        // 선택된 노드 찾기 (updatedNodes 기준)
        const nowSelected = updatedNodes.filter(n => n.selected);
        if (nowSelected.length > 0) {
          // 선택된 노드들의 바운딩박스 중심점 계산
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          nowSelected.forEach(node => {
            const w = node.width || node.style?.width || 200;
            const h = node.height || node.style?.height || 100;
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + w);
            maxY = Math.max(maxY, node.position.y + h);
          });
          yjsUpdateCursor({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 });
        }
        // 모든 노드 선택 해제 시: 커서는 다시 마우스를 따라감 (handleMouseMove에서 처리)
      }

      // position change가 있고 노드가 선택되어 있으면 드래그 중
      // 박스 선택으로 선택된 노드들을 드래그할 때는 onNodeDragStart가 호출되지 않으므로 여기서 처리
      if (positionChanges.length > 0 && draggingNodeIdsRef.current.size === 0) {
        // 드래그가 시작되는 시점 (draggingNodeIdsRef가 비어있을 때)
        // 선택된 모든 노드를 draggedNodeIdsAtStartRef에 저장
        const selectedNodes = nodesRef.current.filter(n => n.selected);
        if (selectedNodes.length > 0) {
          draggedNodeIdsAtStartRef.current.clear();
          dragStartPositionsRef.current.clear();
          isDragHandledByDragStopRef.current = false;
          selectedNodes.forEach(n => {
            draggedNodeIdsAtStartRef.current.add(n.id);
            draggingNodeIdsRef.current.add(n.id);
            // 드래그 시작 위치 저장 (실제 이동 확인용)
            dragStartPositionsRef.current.set(n.id, { x: n.position.x, y: n.position.y });
          });
          isDraggingRef.current = true;
        }
      }

      // position change가 있고 노드가 선택되어 있으면 드래그 중
      // 단, onNodeDragStart에서 이미 처리한 경우는 중복 추가하지 않음
      positionChanges.forEach(change => {
        const node = nodesRef.current.find(n => n.id === change.id);
        if (node && node.selected && !draggingNodeIdsRef.current.has(change.id)) {
          // onNodeDragStart에서 이미 추가되지 않은 경우만 추가
          // (여러 노드 동시 이동 시 onNodeDragStart에서 모든 노드를 한 번에 추가하므로)
          draggingNodeIdsRef.current.add(change.id);
          isDraggingRef.current = true;
        }
      });

      // 레이어 작업 중이면 선택 변경만 건너뛰고, 다른 변경사항(displayOrder 등)은 처리
      const selectTrueChanges = selectChanges.filter(change => change.selected === true);
      const selectFalseChanges = selectChanges.filter(change => change.selected === false);

      // 레이어 작업 중이면 선택 변경 처리만 건너뛰기 (다른 변경사항은 처리)
      if (isLayerOperationRef.current) {
        console.log('🚫 레이어 작업 중: 선택 변경 처리 건너뜀', { isLayerOperation: isLayerOperationRef.current });
        // 선택 변경을 제외한 다른 변경사항만 처리
        const nonSelectChanges = snappedChanges.filter(change => change.type !== 'select');
        if (nonSelectChanges.length > 0) {
          const updatedNodesWithoutSelect = applyNodeChanges(nonSelectChanges, nodesRef.current);
          nodesRef.current = updatedNodesWithoutSelect;
          onNodesChange(nonSelectChanges);
        }
        return;
      }

      // select 해제 시 드래그 종료 및 노드 선택 처리
      // 먼저 selected: true를 처리하고, 그 다음 selected: false를 처리하여 색상 박스가 올바르게 업데이트되도록 함

      // selected: true를 먼저 처리
      selectTrueChanges.forEach(change => {
        const node = updatedNodes.find(n => n.id === change.id);

        if (node) {
          // StyleCard, sticker 등 색상 박스가 필요 없는 노드 선택 시 색상 박스 닫기
          if (node.type === 'StyleCard' || node.type === 'sticker') {
            setShowNodeColorPicker(false);
            setSelectedNodeId(null);
            return;
          }

          if (node.type === 'rectangle' || node.type === 'heart' || node.type === 'circle' || node.type === 'image' || node.type === 'textbox') {
            setSelectedNodeId(node.id);
            // 노드 선택 시 Edge 선택 해제 (실제 Edge의 selected 상태도 변경)
            const selectedEdges = edgesRef.current.filter(edge => edge.selected);
            if (selectedEdges.length > 0) {
              console.log('🔴 이미지/도형 선택 시 엣지 선택 해제:', selectedEdges.map(e => e.id));

              // Edge 선택 해제
              setEdges((eds) => {
                const updatedEdges = eds.map(edge => ({
                  ...edge,
                  selected: false
                }));
                edgesRef.current = updatedEdges;
                return updatedEdges;
              });
            }
            setSelectedEdgeId(null);

            // image 타입은 색상 선택기를 표시하지 않음
            if (node.type === 'image') {
              setShowNodeColorPicker(false);
              console.log('🖼️ 이미지 선택 - 색상 박스 닫기');
              return; // image는 색상 설정이 없으므로 여기서 종료
            }

            setShowNodeColorPicker(true);
            // 노드의 현재 색상으로 초기화 (이미 색상 박스가 열려있어도 새 노드의 색상으로 업데이트)
            setSelectedColor({
              fill: node.data?.fill || '#000000',
              stroke: node.data?.stroke || '#000000',
              fillOpacity: node.data?.fillOpacity !== undefined ? node.data.fillOpacity : 1,
              strokeOpacity: node.data?.strokeOpacity !== undefined ? node.data.strokeOpacity : 1
            });
            setNodeStrokeWidth(node.data?.strokeWidth !== undefined ? node.data.strokeWidth : 5);
            setNodeColorType('fill'); // 기본적으로 배경색부터
          } else if (node.type === 'line') {
            setSelectedNodeId(node.id);
            // 노드 선택 시 Edge 선택 해제 (실제 Edge의 selected 상태도 변경)
            const selectedEdges = edgesRef.current.filter(edge => edge.selected);
            if (selectedEdges.length > 0) {
              // Edge 선택 해제를 위한 changes 생성
              const edgeSelectChanges = selectedEdges.map(edge => ({
                id: edge.id,
                type: 'select',
                selected: false
              }));

              // Edge 선택 해제
              setEdges((eds) => {
                const updatedEdges = eds.map(edge => ({
                  ...edge,
                  selected: false
                }));
                edgesRef.current = updatedEdges;
                return updatedEdges;
              });
            }
            setSelectedEdgeId(null);
            setShowNodeColorPicker(true);
            // Line 노드는 stroke만 있음
            setSelectedColor({
              fill: '#000000',
              stroke: node.data?.stroke || '#000000',
              fillOpacity: 1,
              strokeOpacity: node.data?.strokeOpacity !== undefined ? node.data.strokeOpacity : 1
            });
            setNodeStrokeWidth(node.data?.strokeWidth !== undefined ? node.data.strokeWidth : 5);
            // strokeDasharray를 숫자로 변환 (실선: 0, 큰 대시: 1-15, 점: 16-30)
            const dashValue = node.data?.strokeDasharray || 'none';
            if (dashValue === 'none') {
              setNodeLineStyle(0);
            } else if (typeof dashValue === 'string' && dashValue !== 'none') {
              const dashLength = parseInt(dashValue.split(',')[0]) || 0;
              // 큰 대시 범위 (8-20) -> 슬라이더 15-1 (역순)
              if (dashLength >= 8 && dashLength <= 20) {
                const sliderValue = Math.round(15 - ((dashLength - 8) / 12) * 14);
                setNodeLineStyle(Math.max(1, Math.min(15, sliderValue)));
              }
              // 점 범위 (2-6) -> 슬라이더 30-16 (역순)
              else if (dashLength >= 2 && dashLength <= 6) {
                const sliderValue = Math.round(30 - ((dashLength - 2) / 4) * 14);
                setNodeLineStyle(Math.max(16, Math.min(30, sliderValue)));
              }
              // 기타 큰 값은 큰 대시의 시작
              else if (dashLength > 20) {
                setNodeLineStyle(1);
              }
              // 기타 작은 값은 점의 끝
              else {
                setNodeLineStyle(30);
              }
            } else {
              setNodeLineStyle(0);
            }
            setNodeColorType('stroke'); // Line은 선 색상만
          }
        }
      });

      // 여러 노드가 선택된 경우 색상 박스 닫기
      const selectedNodeCount = updatedNodes.filter(n => n.selected).length;
      if (selectedNodeCount >= 2) {
        setShowNodeColorPicker(false);
        setSelectedNodeId(null);
        console.log('🔴 여러 노드 선택됨 - 색상 박스 닫기:', selectedNodeCount);
      }

      // 드래그 종료 처리 (onNodeDragStop에서 처리하지 않은 경우만)
      // position change가 없고 드래그 중이면 드래그 종료 (노드 선택 상태와 무관)
      const hasPositionChangeForDraggingNodes = positionChanges.some(change =>
        draggingNodeIdsRef.current.has(change.id)
      );

      // position change가 없고 드래그 중이고, onNodeDragStop에서 처리하지 않은 경우만 처리
      if (!hasPositionChangeForDraggingNodes && draggingNodeIdsRef.current.size > 0 && isDraggingRef.current && !isDragHandledByDragStopRef.current) {
        isDraggingEnd = true;
        isDraggingRef.current = false;

        // 드래그 종료 시 최종 위치를 broadcast
        setTimeout(() => {
          const currentNodes = nodesRef.current;

          // 여러 노드 동시 이동 시: 드래그 시작 시점에 저장된 모든 노드 ID 사용
          const draggedNodeIds = draggedNodeIdsAtStartRef.current.size > 0
            ? Array.from(draggedNodeIdsAtStartRef.current)
            : Array.from(draggingNodeIdsRef.current); // fallback

          // 실제로 위치가 변경된 노드만 필터링 (클릭만 했을 때 전송 방지)
          const actuallyMovedNodeIds = draggedNodeIds.filter(nodeId => {
            const startPos = dragStartPositionsRef.current.get(nodeId);
            const currentNode = currentNodes.find(n => n.id === nodeId);
            if (!startPos || !currentNode) return false;
            return startPos.x !== currentNode.position.x || startPos.y !== currentNode.position.y;
          });

          // 실제로 이동한 노드가 없으면 전송하지 않음
          if (actuallyMovedNodeIds.length === 0) {
            draggedNodeIdsAtStartRef.current.clear();
            draggingNodeIdsRef.current.clear();
            dragStartPositionsRef.current.clear();
            return;
          }

          // 실제로 이동한 노드들만 처리
          const finalNodes = currentNodes.map(n => {
            if (actuallyMovedNodeIds.includes(n.id)) {
              return n; // 이미 최신 위치
            }
            return n;
          });

          nodesRef.current = finalNodes;

          // 실제로 이동한 노드들만 전송 (displayOrder는 변경하지 않음)
          broadcast(finalNodes, true, actuallyMovedNodeIds);

          // 드래그 시작 시점 노드 ID 초기화
          draggedNodeIdsAtStartRef.current.clear();
          draggingNodeIdsRef.current.clear();
          dragStartPositionsRef.current.clear();
        }, 50);
      }

      // 기존 selectFalseChanges 처리 (색상 박스 닫기 등)
      selectFalseChanges.forEach(change => {
        if (draggingNodeIdsRef.current.has(change.id)) {
          // 드래그 종료는 위에서 처리하므로 여기서는 제거하지 않음
          isDraggingEnd = true;
        }
      });

      // selected: false를 나중에 처리 (다른 노드가 선택되어 있지 않을 때만 색상 박스 닫기)
      selectFalseChanges.forEach(change => {
        const node = updatedNodes.find(n => n.id === change.id);

        // rectangle, circle, line, textbox, heart 노드가 선택 해제되거나, 현재 선택된 노드가 해제되는 경우 색상 박스 닫기
        const isColorEditableNode = node && (node.type === 'rectangle' || node.type === 'circle' || node.type === 'line' || node.type === 'textbox' || node.type === 'heart');
        const shouldCheckColorBox = isColorEditableNode || selectedNodeId === change.id;

        if (shouldCheckColorBox) {
          // 다른 노드가 선택되어 있는지 확인
          const otherSelectedNode = updatedNodes.find(n => n.selected && n.id !== change.id);
          const otherSelectedEdge = edgesRef.current.find(e => e.selected);
          if (!otherSelectedNode && !otherSelectedEdge) {
            // 다른 노드나 Edge가 선택되어 있지 않으면 색상 박스 닫기
            setShowNodeColorPicker(false);
            setSelectedNodeId(null);
            setSelectedEdgeId(null); // Edge ID도 초기화
          }
        }
      });

      // Line 노드는 화살표 여부와 관계없이 색상 박스를 유지
      // (이전 로직 제거: 화살표가 없는 일반 선도 색상 변경이 가능해야 함)

      // 리사이즈 이벤트가 있으면 width/height를 명시적으로 업데이트
      if (resizeChanges.length > 0) {

        // 초기 로드 중: ReactFlow의 초기 dimensions 측정은 유저 리사이즈가 아님
        // undo 기록, Yjs sync, 그리드 스냅 모두 스킵하고 dimensions만 반영
        if (isInitialLoadRef.current) {
          // 초기 로드 중 dimension 변경은 누적했다가 한 번에 적용 (노드 수만큼 re-render 방지)
          pendingDimChangesRef.current.push(...snappedChanges);
          nodesRef.current = updatedNodes;
          if (dimBatchTimerRef.current) clearTimeout(dimBatchTimerRef.current);
          dimBatchTimerRef.current = setTimeout(() => {
            const t = performance.now();
            if (pendingDimChangesRef.current.length > 0) {
              // console.log(`⏱️ [로드] dimension 배치 적용: ${pendingDimChangesRef.current.length}개`);
              onNodesChange(pendingDimChangesRef.current);
              pendingDimChangesRef.current = [];
              // console.log(`⏱️ [로드] dimension 배치 완료: ${(performance.now() - t).toFixed(0)}ms`);
            }
            isInitialLoadRef.current = false;
            dimBatchTimerRef.current = null;
          }, 300);
          return;
        }

        // 유저 리사이즈가 아닌 경우 (새로고침 후 ReactFlow 내부 dimensions 재측정 등)
        // resizing 속성이 true인 change만 유저가 직접 핸들을 드래그한 것
        const isUserResize = resizeChanges.some(change => change.resizing === true);
        if (!isUserResize) {
          // dimensions 포함 전체 changes를 ReactFlow에 전달 (내부 measured 업데이트)
          onNodesChange(snappedChanges);
          nodesRef.current = updatedNodes;
          return;
        }

        // 리사이즈 시작 시 undo 캡처 경계 설정
        if (!isResizingRef.current) {
          const resizeNodeIds = resizeChanges.map(c => c.id);
          beginUndoCapture(resizeNodeIds);
        }

        // 리사이즈 중 플래그 설정
        isResizingRef.current = true;

        // 리사이즈 중인 노드 ID 추가
        resizeChanges.forEach(change => {
          resizingNodeIdsRef.current.add(change.id);
        });

        // 먼저 dimensions change를 제외한 나머지 changes를 처리
        const nonResizeChanges = snappedChanges.filter(change => change.type !== 'dimensions');
        if (nonResizeChanges.length > 0) {
          onNodesChange(nonResizeChanges);
        }

        // 리사이즈된 노드의 width/height를 업데이트
        updatedNodes = updatedNodes.map(node => {
          const resizeChange = resizeChanges.find(change => change.id === node.id);
          if (resizeChange && resizeChange.dimensions) {
            // 격자에 맞춘 크기 계산
            const snappedWidth = snapSizeToGrid(resizeChange.dimensions.width);
            const snappedHeight = snapSizeToGrid(resizeChange.dimensions.height);

            // 최소 크기 보장 (격자 크기 이상)
            const newWidth = Math.max(snappedWidth, gridSize);
            const newHeight = Math.max(snappedHeight, gridSize);

            // data 안전하게 업데이트
            const updatedData = node.data ? {
              ...node.data,
              width: newWidth,
              height: newHeight
            } : { width: newWidth, height: newHeight };

            return {
              ...node,
              width: newWidth,
              height: newHeight,
              data: updatedData
            };
          }
          return node;
        });

        // ref도 즉시 업데이트
        nodesRef.current = updatedNodes;

        // 리사이즈된 노드를 상태에 직접 반영
        setNodes(updatedNodes);

        // ⚡ 리사이즈 중 실시간 동기화 (throttle - 드래그와 동일한 패턴)
        const resizingIds = Array.from(resizingNodeIdsRef.current)
          .filter(id => !recentlyCreatedNodeIdsRef.current.has(id));
        if (resizingIds.length > 0) {
          const now = Date.now();
          if (now - lastResizeSyncTimeRef.current >= DRAG_SYNC_THROTTLE_MS) {
            lastResizeSyncTimeRef.current = now;
            syncNodes(nodesRef.current, {
              updatedNodeIds: resizingIds,
              triggerThumbnail: false,
              trackHistory: true,
            });
          }
        }

        // 리사이즈 종료 감지 (debounce) - 최종 sync + undo 커밋
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(() => {
          isResizingRef.current = false;

          // 리사이즈된 노드 ID 수집 (최근 생성된 노드 제외)
          const resizedNodeIds = Array.from(resizingNodeIdsRef.current)
            .filter(id => !recentlyCreatedNodeIdsRef.current.has(id));
          resizingNodeIdsRef.current.clear();

          // 최종 상태 sync + undo 캡처 종료
          if (resizedNodeIds.length > 0) {
            console.log('📏 리사이즈 완료 - 최종 sync 전송');
            syncNodes(nodesRef.current, {
              updatedNodeIds: resizedNodeIds,
              triggerThumbnail: true,
              trackHistory: true,
            });
            commitUndoCapture();
          }
        }, 300); // 300ms 후에 최종 sync (리사이즈가 멈춘 후)
      } else {
        // 리사이즈가 아닌 경우 일반적인 상태 업데이트
        onNodesChange(snappedChanges);
        // ref도 즉시 업데이트
        nodesRef.current = updatedNodes;
      }

      // 노드 선택(select) 이벤트만 즉시 broadcast (다른 브라우저에서 선택 차단을 위해)
      if (selectChanges.length > 0) {
        // 선택 상태 변경 시 broadcast (selectedBy 정보 포함)
        const currentUser = getUserNameFromMsal();

        // 선택 순서 업데이트
        selectChanges.forEach(change => {
          if (change.selected === true) {
            // 새로 선택된 노드를 순서 목록 끝에 추가 (이미 있으면 제거 후 추가)
            selectionOrderRef.current = selectionOrderRef.current.filter(id => id !== change.id);
            selectionOrderRef.current.push(change.id);
          } else {
            // 선택 해제된 노드는 순서 목록에서 제거
            selectionOrderRef.current = selectionOrderRef.current.filter(id => id !== change.id);
          }
        });

        // 현재 선택된 모든 노드 찾기
        const allSelectedNodes = updatedNodes.filter(n => n.selected);
        const allSelectedNodeIds = new Set(allSelectedNodes.map(n => n.id));

        // 선택 순서에서 실제로 선택된 노드만 유지 (동기화)
        selectionOrderRef.current = selectionOrderRef.current.filter(id => allSelectedNodeIds.has(id));

        // 여러 개 선택 시 사용자 표시할 노드 결정 - 선택 순서 기반
        let primarySelectedNodeId = null;
        if (allSelectedNodes.length > 1) {
          // 선택 순서에서 마지막 노드 (가장 최근에 선택된 노드)
          if (selectionOrderRef.current.length > 0) {
            primarySelectedNodeId = selectionOrderRef.current[selectionOrderRef.current.length - 1];
          } else {
            // fallback: 배열 마지막 노드
            primarySelectedNodeId = allSelectedNodes[allSelectedNodes.length - 1].id;
          }
        } else if (allSelectedNodes.length === 1) {
          // 단일 선택인 경우
          primarySelectedNodeId = allSelectedNodes[0].id;
        }

        // 변경된 노드 ID 추적
        const changedNodeIds = new Set(selectChanges.map(c => c.id));
        
        const updatedNodesWithSelection = updatedNodes.map(node => {
          const selectChange = selectChanges.find(c => c.id === node.id);

          // 마지막 선택된 노드인지 확인
          const isPrimaryNode = node.id === primarySelectedNodeId;

          if (selectChange) {
            const newSelectedBy = selectChange.selected ? currentUser : null;
            // 개인 보드에서는 showUserLabel을 항상 false로
            const showUserLabel = selectChange.selected && isPrimaryNode && !isPersonalRef.current;

            return {
              ...node,
              data: {
                ...node.data,
                selectedBy: newSelectedBy,
                showUserLabel
              }
            };
          }

          // 선택된 노드 중 primary인 경우 showUserLabel 설정 (개인 보드에서는 항상 false)
          if (node.selected && isPrimaryNode && !isPersonalRef.current && node.data?.showUserLabel !== true) {
            changedNodeIds.add(node.id); // 변경된 노드 추적
            return {
              ...node,
              data: {
                ...node.data,
                selectedBy: node.data?.selectedBy || currentUser,
                showUserLabel: !isPersonalRef.current
              }
            };
          }

          // 선택된 노드 중 primary가 아니면 showUserLabel을 false로
          if (node.selected && !isPrimaryNode && node.data?.showUserLabel) {
            changedNodeIds.add(node.id); // 변경된 노드 추적
            return {
              ...node,
              data: {
                ...node.data,
                showUserLabel: false
              }
            };
          }

          // 선택되지 않은 노드는 showUserLabel을 false로
          if (!node.selected && node.data?.showUserLabel) {
            changedNodeIds.add(node.id); // 변경된 노드 추적
            return {
              ...node,
              data: {
                ...node.data,
                showUserLabel: false
              }
            };
          }

          return node;
        });

        nodesRef.current = updatedNodesWithSelection;
        setNodes(updatedNodesWithSelection);

        // 변경된 모든 노드 ID 전송 (선택 변경은 히스토리에 저장하지 않음)
        broadcast(updatedNodesWithSelection, true, Array.from(changedNodeIds), false);
      } else if (removedNodeIds.length > 0) {
        // 이미지 노드 삭제 시 서버에 이미지 파일 삭제 요청
        removedNodeIds.forEach(nodeId => {
          const removedNode = nodesRef.current.find(n => n.id === nodeId);
          if (removedNode?.type === 'image' && removedNode?.data?.imageUrl) {
            // 서버에 이미지 삭제 요청
            const imageUrl = removedNode.data.imageUrl;
            const filename = imageUrl.split('/').pop();

            // 상대 경로 — ALB Ingress 가 /api 를 api 서비스로 라우팅한다 (동일 origin).
            // 빌드타임 상수(REACT_APP_API_URL)를 쓰면 이미지가 환경 의존이 되어 promote 가 깨진다.
            fetch(`/api/delete-image`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ filename })
            }).catch(err => {
              console.error('이미지 삭제 실패:', err);
            });
          }
        });

        // 노드나 Edge 삭제 시 전체 동기화 (즉시 전송, 삭제된 노드 ID 포함)
        broadcast(updatedNodes, true, null, true, removedNodeIds);
      }
    },
    [onNodesChange, broadcast, snapToGrid, snapSizeToGrid, gridSize, selectedNodeId, setNodes, setEdges, currentUser, boxSelectionMode]
  );
  const onEdgesChangeSync = useCallback(
    (changes) => {
      // Edge 삭제 감지
      const removedEdgeIds = changes
        .filter(change => change.type === 'remove')
        .map(change => change.id);

      // changes를 적용한 최신 상태를 계산
      const updatedEdges = applyEdgeChanges(changes, edgesRef.current);
      // 상태 업데이트
      onEdgesChange(changes);
      // ref도 즉시 업데이트
      edgesRef.current = updatedEdges;

      // Edge 삭제 시 color picker 닫기
      if (removedEdgeIds.length > 0 && selectedEdgeId && removedEdgeIds.includes(selectedEdgeId)) {
        setShowNodeColorPicker(false);
        setSelectedEdgeId(null);
      }

      // 브로드캐스트 (50ms debounce) - Edge 변경 사항 전송
      broadcast(nodesRef.current, updatedEdges, false, []);
    },
    [onEdgesChange, broadcast]
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        // 이미 존재하는 Edge인지 확인
        const existingEdge = eds.find(
          e => e.source === params.source && e.target === params.target
        );
        if (existingEdge) {
          console.log('이미 존재하는 Edge 무시:', params);
          return eds;
        }

        const newEdges = addEdge(params, eds);
        // 엣지 연결은 즉시 전송 (실시간 동기화) - 엣지만 변경, 노드 변경 없음
        broadcast(nodesRef.current, newEdges, true, []);
        return newEdges;
      });
    },
    [setEdges, broadcast]
  );



  // 마우스 커서 위치를 Yjs Awareness로 브로드캐스트 (throttle)
  const cursorThrottleRef = useRef(null);
  const handleMouseMove = useCallback((event) => {
    if (!yjsUpdateCursor || isPersonalRef.current) return;
    if (cursorThrottleRef.current) return;
    cursorThrottleRef.current = setTimeout(() => {
      cursorThrottleRef.current = null;
    }, 50);
    try {
      // ⚡ 노드가 선택되어 있으면 커서 업데이트 건너뜀
      // 선택된 노드의 중심에 커서가 고정된 상태를 유지
      const hasSelectedNodes = nodesRef.current.some(n => n.selected);
      if (hasSelectedNodes) return;

      const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      yjsUpdateCursor({ x: flowPos.x, y: flowPos.y });
    } catch (e) { /* ignore */ }
  }, [yjsUpdateCursor, screenToFlowPosition]);

  // 마우스가 캔버스 밖으로 나갈 때 커서 숨기기
  const handleMouseLeave = useCallback(() => {
    if (yjsUpdateCursor && !isPersonalRef.current) {
      yjsUpdateCursor(null);
    }
  }, [yjsUpdateCursor]);

  // 마우스가 캔버스로 돌아올 때 커서 복원
  const handleMouseEnterCanvas = useCallback((event) => {
    if (!yjsUpdateCursor || isPersonalRef.current) return;
    try {
      const selectedNode = nodesRef.current.find(n => n.selected);
      if (selectedNode) {
        const cx = (selectedNode.position?.x || 0) + ((selectedNode.width || 0) / 2);
        const cy = (selectedNode.position?.y || 0) + ((selectedNode.height || 0) / 2);
        yjsUpdateCursor({ x: cx, y: cy });
      } else {
        const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        yjsUpdateCursor({ x: flowPos.x, y: flowPos.y });
      }
    } catch (e) { /* ignore */ }
  }, [yjsUpdateCursor, screenToFlowPosition]);

  // 다른 유저 커서 렌더링용 데이터 (자신 제외)
  const remoteCursors = useMemo(() => {
    if (!awarenessStates || isPersonal) return [];
    const localClientId = ydocRef?.current?.clientID;
    const localName = yjsUser?.name;
    const cursorMap = new Map();
    awarenessStates.forEach((state, clientId) => {
      if (clientId === localClientId) return;
      if (state?.user?.name === localName) return;
      if (state?.user?.cursor && state?.user?.name) {     
        cursorMap.set(state.user.name, {
          clientId,
          name: state.user.name,
          color: state.user.color || '#A89F91',
          cursor: state.user.cursor,
        });
      }
    });
    return [...cursorMap.values()];
  }, [awarenessStates, isPersonal, ydocRef, yjsUser]);

  // 뷰포트 상태 추적 (커서 렌더링용)
  const [cursorViewport, setCursorViewport] = useState({ x: 0, y: 0, zoom: fixedZoom });
  const handleViewportChange = useCallback((event, vp) => {
    setCursorViewport(vp);
  }, []);

  const [aiChatOpen, setAiChatOpen] = useState(false);

  return (
    <div style={{ height: "100%", width: "100%", display: "flex" }}>
    <div
      style={{ flex: 1, position: "relative", minWidth: 0 }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnterCanvas}
      onMouseLeave={handleMouseLeave}
    >
      {/* 다른 유저 커서 표시 - 뷰포트 transform 적용 */}
      {remoteCursors.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 9999998,
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `translate(${cursorViewport.x}px, ${cursorViewport.y}px) scale(${cursorViewport.zoom})`,
            transformOrigin: '0 0',
          }}>
            {remoteCursors.map(({ clientId, name, color, cursor }) => (
              <div
                key={clientId}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: `translate(${cursor.x}px, ${cursor.y}px)`,
                  transition: 'transform 0.3s ease-out',
                  willChange: 'transform',
                }}
              >
                {/* 커서 SVG (zoom 보정으로 일정 크기 유지) */}
                <div style={{ transform: `scale(${1 / cursorViewport.zoom})`, transformOrigin: '0 0' }}>
                  <svg width="16" height="20" viewBox="0 0 16 20" fill="none" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}>
                    <path d="M0 0L16 12L8 12L4 20L0 0Z" fill={color} stroke="white" strokeWidth="1" />
                  </svg>
                  {/* 유저명 라벨 */}
                  <div style={{
                    position: 'absolute',
                    left: '14px',
                    top: '14px',
                    backgroundColor: color,
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    lineHeight: '16px',
                  }}>
                    {name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 접속 인원 표시 (개인 보드에서는 숨김, API 응답 전에도 숨김) */}
      {isPersonalLoaded && !isPersonal && <div style={{
        position: "fixed",
        top: "60px",
        left: "20px",
        zIndex: 1000,
        backgroundColor: "#F5F1ED",
        padding: "5px 8px",
        // borderRadius: "24px",
        boxShadow: "0 2px 12px rgba(168, 159, 145, 0.2)",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        fontSize: "14px",
        fontWeight: "500",
        color: "#6B5D52",
        maxWidth: "400px"
      }}>
        {/* 프로필 아바타 표시 */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {userList.slice(0, 5).map((userItem, index) => {
            // userItem은 {name, clientId, color} 객체 또는 string(fallback)
            const userName = typeof userItem === 'string' ? userItem : userItem.name;
            // 이름에서 첫 글자 추출
            const initial = userName ? userName.charAt(0).toUpperCase() : '?';
            let bgColor = '#A89F91';
            let hasCursor = false;
            if (awarenessStates) {
              awarenessStates.forEach((s) => {
                if (s?.user?.name === userName) {
                  if (!hasCursor && s.user.color) bgColor = s.user.color;
                  if (s.user.cursor) hasCursor = true;
                }
              });
            }

            return (
              <div
                key={index}
                title={userName}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: bgColor,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "600",
                  border: `2px solid ${hasCursor ? bgColor : '#F5F1ED'}`,
                  boxShadow: hasCursor ? `0 0 0 2px #F5F1ED, 0 0 0 4px ${bgColor}` : "0 2px 4px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.3s",
                  marginLeft: index > 0 ? "-8px" : "0",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1)";
                  e.currentTarget.style.zIndex = "10";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.zIndex = "1";
                }}
              >
                {initial}
              </div>
            );
          })}

          {/* 5명 초과 시 +N 표시 */}
          {userList.length > 5 && (
            <div
              title={`+${userList.length - 5}명: ${userList.slice(5).map(u => typeof u === 'string' ? u : u.name).join(', ')}`}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#9E9589",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: "600",
                border: "2px solid #F5F1ED",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                cursor: "pointer",
                marginLeft: "-8px"
              }}
            >
              +{userList.length - 5}
            </div>
          )}
        </div>

        <span style={{ color: "#8E8275", fontSize: "13px", fontWeight: "600" }}>
          {connectedUsers}명
        </span>
      </div>}

      {/* 노드/Edge 색상 편집 팔레트 */}
      {showNodeColorPicker && (selectedNodeId || selectedEdgeId) && (() => {
        const selectedNode = selectedNodeId ? nodesRef.current.find(node => node.id === selectedNodeId) : null;
        const selectedEdge = selectedEdgeId ? edgesRef.current.find(edge => edge.id === selectedEdgeId) : null;
        const isLineNode = selectedNode?.type === 'line';
        const isEdge = !!selectedEdge;

        return (
          <div
            data-color-picker
            style={{
              position: "fixed",
              top: `${colorPickerPosition.top}px`,
              right: `${colorPickerPosition.right}px`,
              zIndex: 1001,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              width: "250px",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              borderRadius: 8,
              padding: "4px",
              gap: 4,
              cursor: isDraggingColorPicker ? 'grabbing' : 'default',
            }}
          >
            {/* 드래그 핸들 */}
            <div
              onMouseDown={handleColorPickerDragStart}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: isDraggingColorPicker ? 'grabbing' : 'grab',
                padding: '4px',
                color: '#9ca3af',
                fontSize: '16px',
                fontWeight: 'bold',
                userSelect: 'none',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <TfiLineDouble />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "8px" }}>
              {/* 배경/테두리 선택 버튼 - Line 노드와 Edge는 선 색상만 표시 */}
              {!isLineNode && !isEdge && (
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setNodeColorType('fill')}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      backgroundColor: nodeColorType === 'fill' ? "#ccc5b9" : "#f3f4f6",
                      color: nodeColorType === 'fill' ? "#2c3e50" : "#374151",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    배경
                  </button>
                  <button
                    onClick={() => setNodeColorType('stroke')}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      backgroundColor: nodeColorType === 'stroke' ? "#ccc5b9" : "#f3f4f6",
                      color: nodeColorType === 'stroke' ? "#2c3e50" : "#374151",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    테두리
                  </button>
                </div>
              )}
              {(isLineNode || isEdge) && (
                <div style={{
                  padding: "8px 12px",
                  backgroundColor: "#ccc5b9",
                  color: "#2c3e50",
                  border: "none",
                  borderRadius: 6,
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                }}>
                  선 색상
                </div>
              )}

              {/* 색상 선택기 */}
              <SketchPicker
                color={{
                  r: parseInt((nodeColorType === 'fill' ? selectedColor.fill : selectedColor.stroke).slice(1, 3), 16),
                  g: parseInt((nodeColorType === 'fill' ? selectedColor.fill : selectedColor.stroke).slice(3, 5), 16),
                  b: parseInt((nodeColorType === 'fill' ? selectedColor.fill : selectedColor.stroke).slice(5, 7), 16),
                  a: nodeColorType === 'fill' ? selectedColor.fillOpacity : selectedColor.strokeOpacity
                }}
                onChange={handleNodeColorChange}
                disableAlpha={false}
                width="100%"
                styles={{
                  default: {
                    picker: {
                      boxShadow: 'none',
                      border: 'none',
                      padding: 0,
                      borderRadius: 0,
                    }
                  }
                }}
              />

              {/* 테두리 선택 시 굵기 조절 또는 Line 노드/Edge일 때 선 굵기 조절 */}
              {(nodeColorType === 'stroke' || isLineNode || isEdge) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    {(isLineNode || isEdge) ? '선 굵기' : '테두리 굵기'}: {nodeStrokeWidth}px
                  </label>
                  <input
                    type="range"
                    min={isLineNode || isEdge ? "1" : "0"}
                    max="10"
                    value={nodeStrokeWidth}
                    onChange={(e) => handleNodeStrokeWidthChange(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: '#e5e7eb',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                    <span>{isLineNode || isEdge ? '1px' : '0px'}</span>
                    <span>10px</span>
                  </div>

                  {/* 선 스타일 슬라이더 (Line 노드만) */}
                  {isLineNode && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                      <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        선 스타일
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        value={nodeLineStyle}
                        onChange={(e) => handleLineStyleChange(parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          height: '6px',
                          borderRadius: '3px',
                          background: '#e5e7eb',
                          outline: 'none',
                          cursor: 'pointer',
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                        <span>실선</span>
                        <span>큰 대시</span>
                        <span>점</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 선 설정 모달 */}
      {showLineSettingsModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLineSettingsModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 12,
              padding: "24px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: 600 }}>
              선 설정
            </h3>

            {/* 색상 선택 */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 500 }}>
                선 색상
              </label>
              <input
                type="color"
                value={lineSettings.color}
                onChange={(e) => setLineSettings({ ...lineSettings, color: e.target.value })}
                style={{
                  width: "100%",
                  height: "40px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              />
            </div>

            {/* 선 굵기 */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 500 }}>
                선 굵기: {lineSettings.strokeWidth}px
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={lineSettings.strokeWidth}
                onChange={(e) => setLineSettings({ ...lineSettings, strokeWidth: parseInt(e.target.value) })}
                style={{
                  width: "100%",
                  height: "6px",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#6b7280" }}>
                <span>1px</span>
                <span>5px</span>
              </div>
            </div>




            {/* 버튼 */}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowLineSettingsModal(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                취소
              </button>
              <button
                onClick={() => {
                  setShowLineSettingsModal(false);
                  handleToolSelect('line');
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* toolbar 표시 - 권한이 있을 때만 표시 */}
      {canEdit && (
        <div
          data-toolbar
          style={{
            position: "fixed",
            top: `${toolbarPosition.top}px`,
            right: `${toolbarPosition.right}px`,
            zIndex: 1001,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            border: "1px solid #ececec",
            padding: "5px 10px 12px 10px",
            borderRadius: 6,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            cursor: isDraggingToolbar ? 'grabbing' : 'default',
          }}>
          {/* 드래그 핸들 - 상단 */}
          <div
            onMouseDown={handleToolbarDragStart}
            title="드래그하여 이동"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDraggingToolbar ? 'grabbing' : 'grab',
              padding: '4px 0',
              color: '#9ca3af',
              fontSize: '16px',
              userSelect: 'none',
              width: '100%',
              borderBottom: '1px solid #ececec'
            }}
          >
            <TfiLineDouble />
          </div>

          {/* 스타일카드 버튼 */}
          <div style={{ position: 'relative' }}>
            <button
              data-stylecard-button
              onClick={handleAddCardClick}
              title="스타일카드"
              style={{
                padding: "8px 12px",
                background: (addCardMode || showStyleCardMenu) ? "rgb(243 243 243)" : "white",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                if (!addCardMode && !showStyleCardMenu) {
                  e.currentTarget.style.background = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                if (!addCardMode && !showStyleCardMenu) {
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <FiPlusSquare size={17} />
            </button>

            {/* 스타일카드 맵 타입 선택 메뉴 */}
            {showStyleCardMenu && (() => {
              // toolbar의 위치를 확인하여 좌우 결정
              const toolbarElement = document.querySelector('[data-toolbar]');
              let showLeft = true;

              if (toolbarElement) {
                const rect = toolbarElement.getBoundingClientRect();
                const spaceLeft = rect.left;
                const spaceRight = window.innerWidth - rect.right;

                // 오른쪽에 공간이 더 많으면 오른쪽으로 표시
                if (spaceRight > spaceLeft) {
                  showLeft = false;
                }
              }

              return (
                <div
                  ref={styleCardMenuRef}
                  style={{
                    position: "absolute",
                    ...(showLeft ? {
                      right: "120%",
                      marginRight: "8px",
                    } : {
                      left: "120%",
                      marginLeft: "8px",
                    }),
                    transform: "translateY(-23%)",
                    backgroundColor: "white",
                    border: "1px solid #ececec",
                    borderRadius: 6,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    padding: "4px",
                    zIndex: 1001,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: "120px",
                  }}
                >
                  <button
                    onClick={() => handleMapTypeSelect('레인지맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === '레인지맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== '레인지맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== '레인지맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>레인지맵</span>
                  </button>
                  {boardMapType !== 'S' && (
                  <button
                    onClick={() => handleMapTypeSelect('CAD맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === 'CAD맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== 'CAD맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== 'CAD맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>CAD맵</span>
                  </button>
                  )}
                  {boardMapType !== 'C' && (
                  <button
                    onClick={() => handleMapTypeSelect('판매맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === '판매맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== '판매맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== '판매맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>판매맵</span>
                  </button>
                  )}
                  {/* <button
                    onClick={() => handleMapTypeSelect('런칭맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === '런칭맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== '런칭맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== '런칭맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>런칭맵</span>
                  </button> */}
                </div>
              );
            })()}
          </div>

          {/* 템플릿 버튼 */}
          <div style={{ position: 'relative' }}>
            <button
              data-template-button
              onClick={handleAddTemplate}
              title="스타일카드 템플릿"
              style={{
                padding: "8px 12px",
                background: (addTemplateMode || showTemplateMenu) ? "rgb(243 243 243)" : "white",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                if (!addTemplateMode && !showTemplateMenu) {
                  e.currentTarget.style.background = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                if (!addTemplateMode && !showTemplateMenu) {
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <BsGrid3X3GapFill size={17} />
            </button>

            {/* 템플릿 맵 타입 선택 메뉴 */}
            {showTemplateMenu && (() => {
              // toolbar의 위치를 확인하여 좌우 결정
              const toolbarElement = document.querySelector('[data-toolbar]');
              let showLeft = true;

              if (toolbarElement) {
                const rect = toolbarElement.getBoundingClientRect();
                const spaceLeft = rect.left;
                const spaceRight = window.innerWidth - rect.right;

                // 오른쪽에 공간이 더 많으면 오른쪽으로 표시
                if (spaceRight > spaceLeft) {
                  showLeft = false;
                }
              }

              return (
                <div
                  ref={templateMenuRef}
                  style={{
                    position: "absolute",
                    ...(showLeft ? {
                      right: "120%",
                      marginRight: "8px",
                    } : {
                      left: "120%",
                      marginLeft: "8px",
                    }),
                    transform: "translateY(-23%)",
                    backgroundColor: "white",
                    border: "1px solid #ececec",
                    borderRadius: 6,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    padding: "4px",
                    zIndex: 1001,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: "120px",
                  }}
                >
                  <button
                    onClick={() => handleTemplateMapTypeSelect('레인지맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === '레인지맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== '레인지맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== '레인지맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>레인지맵</span>
                  </button>
                  {boardMapType !== 'S' && (
                  <button
                    onClick={() => handleTemplateMapTypeSelect('CAD맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === 'CAD맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== 'CAD맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== 'CAD맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>CAD맵</span>
                  </button>
                  )}
                  {boardMapType !== 'C' && (
                  <button
                    onClick={() => handleTemplateMapTypeSelect('판매맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === '판매맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== '판매맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== '판매맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>판매맵</span>
                  </button>
                  )}
                  {/* <button
                    onClick={() => handleTemplateMapTypeSelect('런칭맵')}
                    style={{
                      padding: "8px 12px",
                      background: selectedMapType === '런칭맵' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedMapType !== '런칭맵') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedMapType !== '런칭맵') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <span>런칭맵</span>
                  </button> */}
                </div>
              );
            })()}
          </div>

          <div style={{ position: 'relative' }}>
            <button
              data-shape-button
              onClick={handleRectangleToolSelect}
              title="도형"
              style={{
                padding: "8px 12px",
                background: (toolMode === 'rectangle' || toolMode === 'circle' || toolMode === 'line' || showShapeMenu) ? 'rgb(243 243 243)' : 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (toolMode !== 'rectangle' && toolMode !== 'circle' && toolMode !== 'line' && !showShapeMenu) {
                  e.currentTarget.style.background = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                if (toolMode !== 'rectangle' && toolMode !== 'circle' && toolMode !== 'line' && !showShapeMenu) {
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <RiShapesFill size={17} />
            </button>
            {/* 도형 옵션 메뉴 */}
            {showShapeMenu && (() => {
              // toolbar의 위치를 확인하여 좌우 결정
              const toolbarElement = document.querySelector('[data-toolbar]');
              let showLeft = true;

              if (toolbarElement) {
                const rect = toolbarElement.getBoundingClientRect();
                const spaceLeft = rect.left;
                const spaceRight = window.innerWidth - rect.right;

                // 오른쪽에 공간이 더 많으면 오른쪽으로 표시
                if (spaceRight > spaceLeft) {
                  showLeft = false;
                }
              }

              return (
                <div
                  ref={shapeMenuRef}
                  style={{
                    position: "absolute",
                    ...(showLeft ? {
                      right: "120%",
                      marginRight: "8px",
                    } : {
                      left: "120%",
                      marginLeft: "8px",
                    }),
                    transform: "translateY(-23%)",
                    backgroundColor: "white",
                    border: "1px solid #ececec",
                    borderRadius: 6,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    padding: "4px",
                    zIndex: 1001,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: "120px",
                  }}
                >
                  <button
                    onClick={() => handleShapeSelect('rectangle')}
                    style={{
                      padding: "8px 12px",
                      background: toolMode === 'rectangle' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (toolMode !== 'rectangle') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (toolMode !== 'rectangle') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <FaSquareFull size={16} />
                    <span>사각형</span>
                  </button>
                  <button
                    onClick={() => handleShapeSelect('circle')}
                    style={{
                      padding: "8px 12px",
                      background: toolMode === 'circle' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (toolMode !== 'circle') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (toolMode !== 'circle') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <FaCircle size={16} />
                    <span>원</span>
                  </button>
                  <button
                    onClick={() => handleShapeSelect('line')}
                    style={{
                      padding: "8px 12px",
                      background: toolMode === 'line' ? 'rgb(243 243 243)' : 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (toolMode !== 'line') {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (toolMode !== 'line') {
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    <HiMinus size={16} style={{ transform: 'rotate(45deg)' }} />
                    <span>선</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowShapeMenu(false);
                      // 화살표는 선을 생성하고 arrowEnd를 true로 설정
                      setLineSettings(prev => ({ ...prev, arrowEnd: true }));
                      handleToolSelect('line');
                    }}
                    style={{
                      padding: "8px 12px",
                      background: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" style={{ transform: 'rotate(45deg)' }}>
                      <line x1="2" y1="8" x2="14" y2="8" stroke="#000" strokeWidth="2" />
                      <path d="M10 4 L14 8 L10 12" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>화살표</span>
                  </button>
                </div>
              );
            })()}
          </div>
          {/* 스티커 버튼 */}
          <div style={{ position: 'relative' }}>
            <button
              data-sticker-button
              onClick={() => {
                if (showStickerModal) {
                  setShowStickerModal(false);
                  setToolMode(null);
                  setPendingToolMode(null);
                } else {
                  setShowStickerModal(true);
                }
              }}
              style={{
                padding: "8px 12px",
                background: (toolMode === 'sticker' || showStickerModal) ? 'rgb(243 243 243)' : 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (toolMode !== 'sticker' && !showStickerModal) {
                  e.currentTarget.style.background = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                if (toolMode !== 'sticker' && !showStickerModal) {
                  e.currentTarget.style.background = 'white';
                }
              }}
              title="스티커"
            >
              <LuSticker size={17} />
            </button>
            {/* 스티커 메뉴 */}
            {showStickerModal && (() => {
              // toolbar의 위치를 확인하여 좌우 결정
              const toolbarElement = document.querySelector('[data-toolbar]');
              let showLeft = true;

              if (toolbarElement) {
                const rect = toolbarElement.getBoundingClientRect();
                const spaceLeft = rect.left;
                const spaceRight = window.innerWidth - rect.right;

                // 오른쪽에 공간이 더 많으면 오른쪽으로 표시
                if (spaceRight > spaceLeft) {
                  showLeft = false;
                }
              }

              return (
                <div
                  ref={stickerMenuRef}
                  style={{
                    position: "absolute",
                    ...(showLeft ? {
                      right: "120%",
                      marginRight: "8px",
                    } : {
                      left: "120%",
                      marginLeft: "8px",
                    }),
                    transform: "translateY(-18%)",
                    backgroundColor: "white",
                    border: "1px solid #ececec",
                    borderRadius: 6,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    padding: "12px",
                    zIndex: 1001,
                    width: "200px",
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '12px' }}>
                    <button
                      onClick={() => {
                        setShowStickerUploadModal(true);
                      }}
                      disabled={uploadingSticker}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#3b82f6',
                        cursor: uploadingSticker ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.2s',
                        padding: 0,
                      }}
                      onMouseEnter={(e) => {
                        if (!uploadingSticker) {
                          e.currentTarget.style.backgroundColor = '#3b82f6';
                          e.currentTarget.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!uploadingSticker) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.color = '#3b82f6';
                        }
                      }}
                      title="스티커 업로드"
                    >
                      <FiPlusSquare size={16} />
                    </button>
                  </div>

                  {uploadingSticker && (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      업로드 중...
                    </div>
                  )}

                  {stickers.length === 0 && !uploadingSticker ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '12px' }}>
                      업로드된 스티커가 없습니다.
                      <div style={{ marginTop: '8px', fontSize: '11px', lineHeight: '1.5' }}>
                        <div>+ 버튼을 클릭하여 스티커를 추가하세요</div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
                        gap: '8px',
                      }}
                    >
                      {stickers.map((sticker) => (
                        <div
                          key={sticker.id}
                          onClick={() => handleStickerSelect(sticker)}
                          style={{
                            borderRadius: 4,
                            padding: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: 'white',
                            aspectRatio: '1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e5e5e5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                          }}
                        >
                          <img
                            src={sticker.url}
                            alt={sticker.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <button
            onClick={() => handleToolSelect('textbox')}
            title="텍스트"
            style={{
              padding: "8px 12px",
              background: toolMode === 'textbox' ? 'rgb(243 243 243)' : 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              if (toolMode !== 'textbox') {
                e.currentTarget.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (toolMode !== 'textbox') {
                e.currentTarget.style.background = 'white';
              }
            }}
          >
            <FaFont size={17} />
          </button>
          {/* 프레임 버튼 */}
          <button
            onClick={() => handleToolSelect('frame')}
            style={{
              padding: "8px 12px",
              background: toolMode === 'frame' ? 'rgb(243 243 243)' : 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              if (toolMode !== 'frame') e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              if (toolMode !== 'frame') e.currentTarget.style.background = 'white';
            }}
            title="프레임"
          >
            <LuFrame size={17} />
          </button>
          <button
            onClick={handleBoxSelectionMode}
            title="영역선택"
            style={{
              padding: "8px 12px",
              background: boxSelectionMode ? "rgb(243 243 243)" : "white",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              if (!boxSelectionMode) {
                e.currentTarget.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (!boxSelectionMode) {
                e.currentTarget.style.background = 'white';
              }
            }}
          >
            <RiFullscreenLine size={17} />
          </button>
          {/* 앞으로 보내기/뒤로 보내기 버튼 - 항상 표시 */}
          <div style={{ borderTop: "1px solid #ececec", paddingTop: "2px" }}></div>
          <button
            onClick={handleBringToFront}
            title="앞으로 보내기"
            disabled={!nodes.some(n => n.selected)}
            style={{
              padding: "8px 12px",
              background: nodes.some(n => n.selected) ? "white" : "transparent",
              borderRadius: 6,
              border: "none",
              cursor: nodes.some(n => n.selected) ? "pointer" : "not-allowed",
              opacity: nodes.some(n => n.selected) ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (nodes.some(n => n.selected)) {
                e.currentTarget.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (nodes.some(n => n.selected)) {
                e.currentTarget.style.background = 'white';
              } else {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <BsBack size={17} />
          </button>
          <button
            onClick={handleSendToBack}
            title="뒤로 보내기"
            disabled={!nodes.some(n => n.selected)}
            style={{
              padding: "8px 12px",
              background: nodes.some(n => n.selected) ? "white" : "transparent",
              borderRadius: 6,
              border: "none",
              cursor: nodes.some(n => n.selected) ? "pointer" : "not-allowed",
              opacity: nodes.some(n => n.selected) ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (nodes.some(n => n.selected)) {
                e.currentTarget.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (nodes.some(n => n.selected)) {
                e.currentTarget.style.background = 'white';
              } else {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <BsFront size={17} />
          </button>
          {/* AI 어시스턴트 버튼 - 임시 주석 */}
          {/* <div style={{ borderTop: "1px solid #ececec", paddingTop: "2px" }}></div>
          <button
            onClick={() => setAiChatOpen(true)}
            title="AI 어시스턴트"
            style={{
              padding: "8px 12px",
              background: aiChatOpen ? "rgb(243 243 243)" : "white",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              fontSize: 17,
            }}
            onMouseEnter={(e) => {
              if (!aiChatOpen) e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              if (!aiChatOpen) e.currentTarget.style.background = 'white';
            }}
          >
            ✨
          </button> */}
         
        </div>
      )}

      {/* 템플릿 추가 다이얼로그 */}
      {showTemplateDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => {
            setShowTemplateDialog(false);
            setTemplateCols('');
            setTemplateRows('');
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: 8,
              minWidth: "300px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: 18, fontWeight: 600 }}>
              템플릿 추가
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: 14, color: "#6B7280" }}>
              그리드 크기를 입력하세요
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "16px" }}>
              <input
                type="number"
                min="1"
                max="10"
                value={templateCols}
                onChange={(e) => setTemplateCols(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (templateCols && templateRows) {
                      handleCreateTemplate();
                    } else if (templateCols && !templateRows) {
                      document.getElementById('template-rows-input')?.focus();
                    }
                  } else if (e.key === 'Escape') {
                    setShowTemplateDialog(false);
                    setTemplateCols('');
                    setTemplateRows('');
                  }
                }}
                placeholder="열"
                autoFocus
                id="template-cols-input"
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none",
                  textAlign: "center",
                }}
              />
              <span style={{ fontSize: 18, fontWeight: 600, color: "#374151" }}>X</span>
              <input
                type="number"
                min="1"
                max="10"
                value={templateRows}
                onChange={(e) => setTemplateRows(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (templateCols && templateRows) {
                      handleCreateTemplate();
                    }
                  } else if (e.key === 'Escape') {
                    setShowTemplateDialog(false);
                    setTemplateCols('');
                    setTemplateRows('');
                  }
                }}
                placeholder="행"
                id="template-rows-input"
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #D1D5DB",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none",
                  textAlign: "center",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setShowTemplateDialog(false);
                  setTemplateCols('');
                  setTemplateRows('');
                }}
                style={{
                  padding: "8px 16px",
                  background: "#F3F4F6",
                  color: "#374151",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                취소
              </button>
              <button
                onClick={handleCreateTemplate}
                style={{
                  padding: "8px 16px",
                  background: "#ccc5b9",
                  color: "#2c3e50",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                생성
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 스티커 업로드 모달 */}
      {showStickerUploadModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => {
            if (!uploadingSticker) {
              setShowStickerUploadModal(false);
              setIsDraggingOverSticker(false);
            }
          }}
        >
          <div
            ref={stickerUploadModalRef}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDraggingOverSticker(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsDraggingOverSticker(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDraggingOverSticker(false);

              const file = e.dataTransfer.files[0];
              if (file && file.type.startsWith('image/')) {
                handleStickerUpload(file);
              }
            }}
            style={{
              background: "#fff",
              padding: "32px",
              borderRadius: 12,
              minWidth: "400px",
              maxWidth: "500px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              border: isDraggingOverSticker ? "2px dashed #3b82f6" : "1px solid #e5e7eb",
              backgroundColor: isDraggingOverSticker ? "#f0f9ff" : "#fff",
              transition: "all 0.2s",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 닫기 버튼 */}
            <button
              onClick={() => {
                if (!uploadingSticker) {
                  setShowStickerUploadModal(false);
                  setIsDraggingOverSticker(false);
                }
              }}
              disabled={uploadingSticker}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                width: "28px",
                height: "28px",
                border: "none",
                borderRadius: "50%",
                background: "#f3f4f6",
                cursor: uploadingSticker ? 'not-allowed' : 'pointer',
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                color: "#6b7280",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!uploadingSticker) {
                  e.currentTarget.style.background = "#e5e7eb";
                  e.currentTarget.style.color = "#374151";
                }
              }}
              onMouseLeave={(e) => {
                if (!uploadingSticker) {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#6b7280";
                }
              }}
            >
              ×
            </button>

            <h3 style={{ margin: "0 0 20px 0", fontSize: 20, fontWeight: 600, color: "#111827" }}>
              스티커 업로드
            </h3>
            {/* 업로드 방법 안내 */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                fontSize: "14px",
                color: "#374151"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px"
                  }}>
                    📁
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: "4px" }}>파일 선택</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>버튼을 클릭하여 이미지 파일을 선택하세요</div>
                  </div>
                  <button
                    onClick={() => {
                      if (stickerFileInputRef.current) {
                        stickerFileInputRef.current.click();
                      }
                    }}
                    disabled={uploadingSticker}
                    style={{
                      padding: "8px 16px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: uploadingSticker ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      fontWeight: 500,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (!uploadingSticker) {
                        e.currentTarget.style.background = "#2563eb";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!uploadingSticker) {
                        e.currentTarget.style.background = "#3b82f6";
                      }
                    }}
                  >
                    파일 선택
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px"
                  }}>
                    📎
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: "4px" }}>드래그앤드랍</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>이미지 파일을 이 영역으로 드래그하세요</div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px"
                  }}>
                    📋
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: "4px" }}>복사 붙여넣기</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>이미지를 복사한 후 Ctrl+V로 붙여넣으세요</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 파일 입력 (숨김) */}
            <input
              ref={stickerFileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleStickerUpload(file);
                }
              }}
            />

            {uploadingSticker && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: 500
              }}>
                업로드 중...
              </div>
            )}
          </div>
        </div>
      )}

      {/* <div 
        style={{ width: '100%', height: '100%', position: 'relative' }}
      >
      Undo/Redo 버튼 - 오른쪽 상단
      <div style={{
        position: 'absolute',
        top: '50px',
        right: '16px',
        zIndex: 1000,
        display: 'flex',
        pointerEvents: 'auto'
      }}>
        Undo 버튼
        <button
          onClick={handleUndo}
          disabled={!yjsCanUndo}
          style={{
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            cursor: (historyIndex <= 0 || !isHistoryInitializedRef.current) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            opacity: (historyIndex <= 0 || !isHistoryInitializedRef.current) ? 0.5 : 1,
            transition: 'all 0.2s'
          }}
          title="되돌리기 (Ctrl+Z)"
        >
          <BiUndo size={25} />
        </button>

        Redo 버튼
        <button
          onClick={handleRedo}
          disabled={!yjsCanRedo}
          style={{
            padding: '8px 12px',
            background: 'transparent',
            border: 'none',
            cursor: (historyIndex >= historyLength - 1 || !isHistoryInitializedRef.current) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            opacity: (historyIndex >= historyLength - 1 || !isHistoryInitializedRef.current) ? 0.5 : 1,
            transition: 'all 0.2s'
          }}
          title="다시 실행 (Ctrl+Y)"
        >
          <BiRedo size={25} />
        </button>
      </div>
      </div> */}

      <ReactFlow
        className={(addCardMode || addTemplateMode || toolMode) ? 'insert-mode' : undefined}
        nodes={[...nodes]
          .sort((a, b) => {
            const orderA = a.data?.displayOrder ?? 0;
            const orderB = b.data?.displayOrder ?? 0;
            return orderA - orderB;
          })
          .map(node => {
            const displayOrder = node.data?.displayOrder ?? 0;
            const nodeWithZIndex = {
              ...node,
              style: {
                ...node.style,
                zIndex: displayOrder
              }
            };
            if (!canEdit) {
              return { ...nodeWithZIndex, draggable: false };
            }
            if (node.type === 'textbox') {
              if (node.data?.isEditing) {
                return { ...nodeWithZIndex, draggable: false };
              }
              return { ...nodeWithZIndex, draggable: true };
            }
            return { ...nodeWithZIndex, draggable: true };
          })}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={canEdit ? onNodesChangeSync : undefined}
        onEdgesChange={canEdit ? onEdgesChangeSync : undefined}
        onConnect={canEdit ? onConnect : undefined}
        connectionMode="loose"
        nodesConnectable={false}
        snapToGrid={true}
        snapGrid={[gridSize, gridSize]}
        elevateNodesOnSelect={false}
        elevateEdgesOnSelect={false}
        panOnDrag={(() => {
          // 편집 중인 텍스트박스가 있으면 캔버스 드래그 비활성화 (텍스트 선택을 위해)
          const hasEditingTextBox = nodes.some(node => node.type === 'textbox' && node.data?.isEditing);
          if (hasEditingTextBox) return false;
          // 박스 선택 모드일 때는 드래그로 선택하므로 패닝 비활성화
          if (boxSelectionMode) return false;
          // 도구 모드가 명시적으로 설정되어 있을 때만 패닝 비활성화
          if (toolMode && (toolMode === 'rectangle' || toolMode === 'circle' || toolMode === 'textbox' || toolMode === 'frame')) {
            return false;
          }
          return true;
        })()}
        panOnScroll={false}
        onMove={handleViewportChange}
        nodesDraggable={canEdit}
        elementsSelectable={canEdit && !addCardMode && !addTemplateMode && !toolMode}
        zoomOnScroll={true}
        zoomOnPinch={true}
        selectionOnDrag={canEdit && boxSelectionMode}
        onSelectionStart={(event) => {
          const pos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
          selectionStartPosRef.current = { flow: pos, screen: { x: event.clientX, y: event.clientY } };
        }}
        onSelectionEnd={() => {
          selectionStartPosRef.current = null;
        }}
        deleteKeyCode={null}
        multiSelectionKeyCode={canEdit ? 'Control' : null}
        onPaneMouseDown={(event) => {
          // 권한이 없으면 모든 상호작용 차단
          if (!canEdit) {
            return;
          }

          console.log('🖊️ onPaneMouseDown 호출:', { toolMode, button: event.button, boxSelectionMode, shiftKey: event.shiftKey });

          // 사각형 드래그 시작
          if (toolMode === 'rectangle') {
            event.preventDefault();
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            console.log('사각형 드래그 시작 - onPaneMouseDown:', position);
            startRectangleDrag(position);
            return;
          }


          // 원형 드래그 시작
          if (toolMode === 'circle') {
            event.preventDefault();
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            console.log('원형 드래그 시작 - onPaneMouseDown:', position);
            startCircleDrag(position);
            return;
          }

          // 텍스트박스 드래그 시작
          if (toolMode === 'textbox') {
            event.preventDefault();
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            console.log('텍스트박스 드래그 시작 - onPaneMouseDown:', position);
            startTextBoxDrag(position);
            return;
          }


          // 프레임 드래그 시작
          if (toolMode === 'frame') {
            event.preventDefault();
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            startFrameDrag(position);
            return;
          }

          // 박스 선택 모드일 때 커스텀 박스 선택 시작
          if (boxSelectionMode) {
            event.preventDefault();
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            console.log('🔍 박스 선택 시작:', position);
            setSelectionBox({ x1: position.x, y1: position.y, x2: position.x, y2: position.y });
            setIsBoxSelecting(true);
            return;
          }

          // 그 외의 경우는 ReactFlow의 기본 동작(패닝)을 허용
        }}
        onPaneClick={(event) => {
          // 노드가 선택되어 있으면 선택 해제 (로컬에서만 처리, broadcast 불필요)
          const selectedNodeIds = nodesRef.current.filter(node => node.selected).map(node => node.id);
          if (selectedNodeIds.length > 0) {
            setNodes((nds) => {
              const updatedNodes = nds.map(node => ({
                ...node,
                selected: false
              }));
              nodesRef.current = updatedNodes;
              return updatedNodes;
            });
            setShowNodeColorPicker(false);
            setSelectedNodeId(null);
          }


          // 사각형, 하트, 원형, 텍스트박스 모드는 드래그로 처리되므로 클릭 이벤트 무시
          if (toolMode === 'rectangle' || toolMode === 'circle' || toolMode === 'textbox') {
            return;
          }

          // 카드 추가 모드일 때
          if (addCardMode) {
            event.preventDefault();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            console.log('카드 추가 모드 - 클릭 위치:', position);
            addCardAtPosition(position);
            return;
          }

          // 템플릿 추가 모드일 때
          if (addTemplateMode) {
            event.preventDefault();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            console.log('템플릿 추가 모드 - 클릭 위치:', position);
            addTemplateAtPosition(position);
            return;
          }

          // 도구 모드일 때 (펜, 하트, 원형, 텍스트박스, 사각형은 드래그로 처리되므로 제외)
          if (toolMode && toolMode !== 'pen' && toolMode !== 'heart' && toolMode !== 'circle' && toolMode !== 'textbox' && toolMode !== 'rectangle') {
            event.preventDefault();
            return;
          }
        }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
        minZoom={0.1}
        maxZoom={4}
        style={{ cursor: (addCardMode || addTemplateMode || toolMode || boxSelectionMode) ? 'crosshair' : 'default' }}
        onNodeClick={(event, node) => {
          // 카드/템플릿 추가 모드에서는 노드 위 클릭도 추가 처리
          if (addCardMode) {
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            addCardAtPosition(position);
            return;
          }
          if (addTemplateMode) {
            event.stopPropagation();
            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            addTemplateAtPosition(position);
            return;
          }
        }}
        onNodeDragStart={(event, node) => {
          // 드래그 시작 시 기본 동작 방지 (흔적 제거)
          if (event && event.preventDefault) {
            event.preventDefault();
          }

          // 편집 중인 텍스트박스 노드는 드래그 방지
          if (node.type === 'textbox' && node.data?.isEditing) {
            return;
          }

          // 드래그 시작 시점에 현재 선택된 모든 노드 ID 저장 (여러 노드 동시 이동용)
          if (draggingNodeIdsRef.current.size === 0) {
            // 첫 번째 노드가 드래그 시작될 때만 초기화
            draggedNodeIdsAtStartRef.current.clear();
            dragStartPositionsRef.current.clear();
            isDragHandledByDragStopRef.current = false;
            const selectedNodes = nodesRef.current.filter(n => n.selected);
            const selectedNodeIds = [];
            selectedNodes.forEach(n => {
              draggedNodeIdsAtStartRef.current.add(n.id);
              draggingNodeIdsRef.current.add(n.id);
              selectedNodeIds.push(n.id);
              // 드래그 시작 위치 저장 (실제 이동 확인용)
              dragStartPositionsRef.current.set(n.id, { x: n.position.x, y: n.position.y });
            });
            // ⚡ 드래그 시작: stopCapturing()으로 이전 작업과 undo 스텝 분리
            // 드래그 중 trackHistory=true로 전송 → captureTimeout으로 자동 병합
            if (selectedNodeIds.length > 0) {
              beginUndoCapture(selectedNodeIds);
            }
          } else {
            // 이미 드래그가 시작된 경우에도 현재 노드 추가
            draggingNodeIdsRef.current.add(node.id);
            dragStartPositionsRef.current.set(node.id, { x: node.position.x, y: node.position.y });
          }

          isDraggingRef.current = true;
        }}
        onNodeDrag={(event, node, draggedNodes) => {
          // 드래그 중 기본 동작 방지 (흔적 제거)
          if (event && event.preventDefault) {
            event.preventDefault();
          }

          // ⚡ 박스 선택 후 드래그 시 onNodeDragStart가 호출되지 않을 수 있음
          // draggingNodeIdsRef가 비어있으면 여기서 초기화
          if (draggingNodeIdsRef.current.size === 0 && draggedNodes && draggedNodes.length > 0) {
            draggedNodeIdsAtStartRef.current.clear();
            dragStartPositionsRef.current.clear();
            isDragHandledByDragStopRef.current = false;
            const dragNodeIds = [];
            draggedNodes.forEach(n => {
              draggedNodeIdsAtStartRef.current.add(n.id);
              draggingNodeIdsRef.current.add(n.id);
              dragNodeIds.push(n.id);
              dragStartPositionsRef.current.set(n.id, { x: n.position.x, y: n.position.y });
            });
            isDraggingRef.current = true;
            // undo 스텝 경계 설정
            if (dragNodeIds.length > 0) {
              beginUndoCapture(dragNodeIds);
            }
          }

          // ⚡ 드래그 중 실시간 동기화 (throttle 없음 - 즉시 반영)
          // ⚡ draggedNodes (3rd arg)에 드래그 중인 모든 노드의 최신 위치가 포함됨
          let currentNodes = nodesRef.current;
          if (draggedNodes && draggedNodes.length > 0) {
            const draggedMap = new Map(draggedNodes.map(n => [n.id, n]));
            currentNodes = currentNodes.map(n => draggedMap.get(n.id) || n);
            nodesRef.current = currentNodes;
          }

          // ⚡ draggedNodes에서 직접 노드 ID를 추출
          const dragNodeIds = draggedNodes && draggedNodes.length > 0
            ? draggedNodes.map(n => n.id)
            : Array.from(draggingNodeIdsRef.current);

          if (dragNodeIds.length > 0) {
            syncNodes(currentNodes, {
              updatedNodeIds: dragNodeIds,
              triggerThumbnail: false,
              trackHistory: true,
            });
          }

          // ⚡ 드래그 중 커서 위치도 함께 동기화
          if (yjsUpdateCursor && !isPersonalRef.current && event) {
            try {
              const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
              yjsUpdateCursor({ x: flowPos.x, y: flowPos.y });
            } catch (e) { /* ignore */ }
          }
        }}
        onNodeDragStop={(event, node, draggedNodes) => {
          draggingNodeIdsRef.current.delete(node.id);

          // ⚡ draggedNodes의 최신 위치를 nodesRef에 반영
          if (draggedNodes && draggedNodes.length > 0) {
            const draggedMap = new Map(draggedNodes.map(n => [n.id, n]));
            nodesRef.current = nodesRef.current.map(n => draggedMap.get(n.id) || n);
          }

          // 첫 번째 노드가 드래그 종료되면 바로 전송 (모든 노드 종료를 기다리지 않음)
          // 여러 노드 동시 이동 시 (일반 선택 또는 박스 선택): 드래그 시작 시점에 저장된 모든 노드의 현재 위치를 전송
          if (draggedNodeIdsAtStartRef.current.size > 0) {
            // 여러 노드 동시 이동인 경우
            isDraggingRef.current = false;
            isDragHandledByDragStopRef.current = true; // onNodeDragStop에서 처리했다는 플래그

            // 드래그 종료 시 최종 위치를 전송 및 undo 커밋
            setTimeout(() => {
              const currentNodes = nodesRef.current;

              // 드래그 시작 시점에 저장된 모든 노드 ID 사용
              const draggedNodeIds = Array.from(draggedNodeIdsAtStartRef.current);

              // 실제로 위치가 변경된 노드만 필터링 (클릭만 했을 때 전송 방지)
              const actuallyMovedNodeIds = draggedNodeIds.filter(nodeId => {
                const startPos = dragStartPositionsRef.current.get(nodeId);
                const currentNode = currentNodes.find(n => n.id === nodeId);
                if (!startPos || !currentNode) return false;
                return startPos.x !== currentNode.position.x || startPos.y !== currentNode.position.y;
              });

              // 실제로 이동한 노드가 없으면 전송하지 않음
              if (actuallyMovedNodeIds.length === 0) {
                draggedNodeIdsAtStartRef.current.clear();
                draggingNodeIdsRef.current.clear();
                dragStartPositionsRef.current.clear();
                return;
              }

              nodesRef.current = currentNodes;

              // ⚡ 최종 위치를 syncNodes로 전송 (UndoManager가 추적, 이전 드래그 위치와 자동 병합)
              syncNodes(currentNodes, {
                updatedNodeIds: actuallyMovedNodeIds,
                triggerThumbnail: true,
                trackHistory: true,
              });

              // ⚡ 드래그 종료: stopCapturing()으로 다음 작업과 undo 스텝 분리
              commitUndoCapture();

              // 드래그 시작 시점 노드 ID 초기화
              draggedNodeIdsAtStartRef.current.clear();
              draggingNodeIdsRef.current.clear();
              dragStartPositionsRef.current.clear();
            }, 50);
          } else {
            // 단일 노드 이동인 경우 (onNodesChange에서 감지된 드래그)
            // beginUndoCapture가 호출되지 않았을 수 있으므로 broadcast(trackHistory=true)를 사용
            if (draggingNodeIdsRef.current.size === 0) {
              isDraggingRef.current = false;
              setTimeout(() => {
                const currentNodes = nodesRef.current;
                const finalNodes = currentNodes.map(n =>
                  n.id === node.id ? { ...n, position: node.position } : n
                );

                nodesRef.current = finalNodes;
                broadcast(finalNodes, true, [node.id]);
              }, 50);
            }
          }

          // 드래그 종료 후 현재 마우스 위치로 커서 업데이트 (흔적 방지)
          if (yjsUpdateCursor && !isPersonalRef.current && event) {
            try {
              const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
              yjsUpdateCursor({ x: flowPos.x, y: flowPos.y });
            } catch (e) { /* ignore */ }
          }
        }}
        onSelectionDragStart={(event, nodes) => {
          // ⚡ 박스 선택(Shift+드래그) 후 드래그 시 호출됨
          // onNodeDragStart 대신 이 핸들러가 호출됨
          if (!nodes || nodes.length === 0) return;

          draggedNodeIdsAtStartRef.current.clear();
          dragStartPositionsRef.current.clear();
          draggingNodeIdsRef.current.clear();
          isDragHandledByDragStopRef.current = false;

          const selectedNodeIds = [];
          nodes.forEach(n => {
            draggedNodeIdsAtStartRef.current.add(n.id);
            draggingNodeIdsRef.current.add(n.id);
            selectedNodeIds.push(n.id);
            dragStartPositionsRef.current.set(n.id, { x: n.position.x, y: n.position.y });
          });

          isDraggingRef.current = true;

          // undo 스텝 경계 설정
          if (selectedNodeIds.length > 0) {
            beginUndoCapture(selectedNodeIds);
          }
        }}
        onSelectionDrag={(event, nodes) => {
          // ⚡ 박스 선택 후 드래그 중 실시간 동기화 (throttle 없음 - 즉시 반영)
          if (!nodes || nodes.length === 0) return;

          // nodes에 드래그 중인 모든 노드의 최신 위치가 포함됨
          const draggedMap = new Map(nodes.map(n => [n.id, n]));
          const currentNodes = nodesRef.current.map(n => draggedMap.get(n.id) || n);
          nodesRef.current = currentNodes;

          const dragNodeIds = nodes.map(n => n.id);
          if (dragNodeIds.length > 0) {
            syncNodes(currentNodes, {
              updatedNodeIds: dragNodeIds,
              triggerThumbnail: false,
              trackHistory: true,
            });
          }

          // 커서 위치도 함께 동기화
          if (yjsUpdateCursor && !isPersonalRef.current && event) {
            try {
              const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
              yjsUpdateCursor({ x: flowPos.x, y: flowPos.y });
            } catch (e) { /* ignore */ }
          }
        }}
        onSelectionDragStop={(event, nodes) => {
          // ⚡ 박스 선택 후 드래그 종료
          if (!nodes || nodes.length === 0) return;

          isDraggingRef.current = false;
          isDragHandledByDragStopRef.current = true;

          // 최신 위치 반영
          const draggedMap = new Map(nodes.map(n => [n.id, n]));
          nodesRef.current = nodesRef.current.map(n => draggedMap.get(n.id) || n);

          const currentNodes = nodesRef.current;
          const draggedNodeIds = Array.from(draggedNodeIdsAtStartRef.current);

          // 실제로 위치가 변경된 노드만 필터링
          const actuallyMovedNodeIds = draggedNodeIds.filter(nodeId => {
            const startPos = dragStartPositionsRef.current.get(nodeId);
            const currentNode = currentNodes.find(n => n.id === nodeId);
            if (!startPos || !currentNode) return false;
            return startPos.x !== currentNode.position.x || startPos.y !== currentNode.position.y;
          });

          if (actuallyMovedNodeIds.length > 0) {
            syncNodes(currentNodes, {
              updatedNodeIds: actuallyMovedNodeIds,
              triggerThumbnail: true,
              trackHistory: true,
            });

            // undo 스텝 경계 종료
            commitUndoCapture();
          }

          // 상태 초기화
          draggedNodeIdsAtStartRef.current.clear();
          draggingNodeIdsRef.current.clear();
          dragStartPositionsRef.current.clear();

          // 드래그 종료 후 현재 마우스 위치로 커서 업데이트 (흔적 방지)
          if (yjsUpdateCursor && !isPersonalRef.current && event) {
            try {
              const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
              yjsUpdateCursor({ x: flowPos.x, y: flowPos.y });
            } catch (e) { /* ignore */ }
          }
        }}
      >
        <MiniMap />
        <Background
          gap={gridSize}
          size={1}
          color="rgba(204, 197, 185, 0.15)"
        />
      </ReactFlow>

    </div>
    <AiChat canvases={canvases} activeCanvasId={activeCanvasId} currentNodes={nodes} cadData={cadData} open={aiChatOpen} onClose={() => setAiChatOpen(false)} />
    </div>
  );
}

export default function ReactFlowWrapper({ tabId = 'default', cadData, fileId = 'default', onTabsRestore = null, isPersonal = false, isPersonalLoaded = false, boardAutoUpdate = false, boardMapType = null, boardBrdCd = null, boardSesn = null }) {
  return (
    <ReactFlowProvider>
      <ReactFlowInner tabId={tabId} cadData={cadData} fileId={fileId} onTabsRestore={onTabsRestore} isPersonal={isPersonal} isPersonalLoaded={isPersonalLoaded} boardAutoUpdate={boardAutoUpdate} boardMapType={boardMapType} boardBrdCd={boardBrdCd} boardSesn={boardSesn} />
    </ReactFlowProvider>
  );
}


